import { useNavigate } from 'react-router-dom';
import CriteriaEntries from './CriteriaEntries';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import React, { useState, useEffect, useMemo, useRef, startTransition } from 'react';
import { useQuery, useMutation, useQueryClient, UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import {
    Box, Grid, TextField, Typography, List, ListItemButton, ListItemText,
    Checkbox, FormControlLabel, Paper, Divider, Button, Drawer, IconButton,
    useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, ListItem, Stack, Alert,
    Accordion, AccordionSummary, AccordionDetails, Tooltip, ToggleButton, ToggleButtonGroup, Chip, Collapse, Select, MenuItem, FormControl, InputLabel,
    InputBase, Autocomplete, ListSubheader,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { useAuth } from "react-oidc-context";
import GroupIcon from '@mui/icons-material/Group';
import MenuIcon from '@mui/icons-material/Menu';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WorkIcon from '@mui/icons-material/Work';
import TuneIcon from '@mui/icons-material/Tune';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';
import { v4 as uuidv4 } from 'uuid';
import LaunchIcon from '@mui/icons-material/Launch';
import config from './config.json';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TimelineComponent from './Timeline';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TenderExportDialog from './TenderExportDialog';
import { SCORE_DIMENSION_LABELS } from './tenderExport';
import type { DailyTenderStats } from './tenderStats';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import MermaidDiagram from './MermaidDiagram';
import { parseTag, rowBadges, detailBadges, facetCounts, matchesTagSelection, emptyTagSelection, effectiveSelection, passesFitFloor, tenderComparator, CPV_DIVISION_LABELS, NO_TOPIC, NDA_TAG, NDA_REQUIRED_TAG, setTopicCatalog, topicTree, scopedTags, GROUP_LABELS } from './tenderTags';
import type { TagSelection, TagInfo, TenderSort, TopicCatalog, FacetOption } from './tenderTags';
import { isSemanticTerm, similarTenders, similarityLabel, isConcluded, rowStatus, startOfToday, passesNdaFilter,
    filtersBypassed, searchCaption, listCountLabel, filterSummary, passesStatusFilter, STATUS_FILTER_LABELS } from './tenderSearch';
import { splitDocuments, skippedLabel, skippedNote } from './tenderDocuments';
import type { SemanticHit, RowStatusKind, NdaFilter, ActiveFilters, StatusFilter } from './tenderSearch';
import { vendorLine, awardMeta } from './tenderAward';
import type { AwardVendor } from './tenderAward';
import ChatModelPicker, { ThoroughWarning, AnswerModelChip } from './ChatModelPicker';
import { loadChatModelTier, saveChatModelTier } from './chatModels';
import type { ChatModelTier } from './chatModels';
import { CompanyTagFilter, CompanyTagBadges, CompanyTagDetail } from './CompanyTagWidgets';
import CompanyTagsDialog from './CompanyTagsDialog';
import {
    parseCompanyTags, hitsByTender, activeTagSelection, passesCompanyTagFilter, companyTagCounts,
    tagMatchCounts, draftPayload, keywordProblem, EMPTY_COMPANY_TAGS,
} from './companyTags';
import type { TagDraft, TagPreview } from './companyTags';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { DRAFT_PROPOSAL_FLOW, REVIEW_PROPOSAL_FLOW, COMPANY_ONBOARDING_FLOW, CHAT_FLOW } from './mermaidDiagrams';

const desktopPanelFontSize = '0.85rem';
const smallScreenPanelFontSize = '0.75rem';

// "Aug 13, 2026" — shared format for every date shown in the tender panels.
const formatDate = (iso?: string | null): string =>
    iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

// Fit-score (0–100) → MUI theme colour token: green ≥70, amber ≥40, red below.
const fitColor = (pct: number): string => (pct >= 70 ? 'success.main' : pct >= 40 ? 'warning.main' : 'error.main');

// Left-list row background: a faint horizontal "bar" filled to the score %,
// hued red (0) → yellow (50) → green (100). Higher alpha for hover/selected.
const scoreRowBar = (score: number | null | undefined, alpha = 0.16): string => {
    if (score == null) return 'transparent';
    const pct = Math.max(0, Math.min(100, score <= 1 ? score * 100 : score));
    const hue = (pct / 100) * 120;
    const tint = `hsla(${hue}, 65%, 45%, ${alpha})`;
    return `linear-gradient(to right, ${tint} ${pct}%, transparent ${pct}%)`;
};

// Tag badge colours: the order type drives the hue (construction amber,
// services blue, supplies purple); cantons get a green tint, everything else
// stays neutral. Tags come from simap_core.tender_tags_v via the API.
const TAG_TYPE_COLORS: Record<string, { bg: string; fg: string }> = {
    construction: { bg: 'rgba(237,108,2,0.14)', fg: '#9a5700' },
    services: { bg: 'rgba(2,136,209,0.14)', fg: '#01579b' },
    supplies: { bg: 'rgba(123,31,162,0.12)', fg: '#6a1b9a' },
};
// 'row' badges are tiny and bold (they sit under a one-line title);
// 'detail' badges match the details panel's body text so they read as part
// of it instead of shouting at it.
const tagBadgeSx = (info: TagInfo, variant: 'row' | 'detail' = 'row') => {
    const c = info.kind === 'type'
        ? (TAG_TYPE_COLORS[info.value] ?? { bg: 'rgba(0,0,0,0.06)', fg: '#555' })
        : info.kind === 'topic'
            ? { bg: 'rgba(103,58,183,0.12)', fg: '#4527a0' }
            : (info.kind === 'subtopic' || info.kind === 'code')
                ? { bg: 'rgba(103,58,183,0.07)', fg: '#5e35b1' }
                : info.kind === 'canton'
                    ? { bg: 'rgba(46,125,50,0.12)', fg: '#1b5e20' }
                    : info.kind === 'docs'
                        ? { bg: 'rgba(237,108,2,0.14)', fg: '#b26a00' }
                        : { bg: 'rgba(0,0,0,0.06)', fg: '#555' };
    return {
        display: 'inline-block',
        px: variant === 'detail' ? 0.75 : 0.5,
        py: variant === 'detail' ? '2px' : '1px',
        borderRadius: '4px',
        fontSize: variant === 'detail' ? '0.72rem' : '0.6rem',
        fontWeight: variant === 'detail' ? 500 : 600,
        lineHeight: 1.5,
        bgcolor: c.bg, color: c.fg, whiteSpace: 'nowrap' as const,
    };
};

// --- Interfaces ---
// EK/ZK criteria extracted from the tender documents (see backend
// scripts/criteria_extraction). Display-only for now.
interface EligibilityCriterion {
    id?: string;
    criterion: string;
    evidence?: string;
    source?: string;
}

interface AwardCriterion {
    id?: string;
    criterion: string;
    weight_pct?: number | null;
    sub_criteria?: AwardCriterion[];
}

interface KeyPersonnelRequirement {
    role: string;
    count?: number | null;
    min_experience_years?: number | null;
    certifications?: string[];
    languages?: string[];
}

interface ReferenceRequirement {
    applies_to?: string;
    count?: number | null;
    recency_years?: number | null;
    similarity?: string;
}

interface Tender {
    id: string;
    projectId: string;
    publicationNumber?: string;
    projectNumber?: string;
    name: string;
    lotNumber: number;
    lotTitle: string;
    industry: string;
    deadline: string;
    summary: string;
    companySize: number;
    requirements: string;
    idealProfile: string;
    matchingScore: number | null;
    matchingScoreComputedAt?: string | null;
    matchingScoreStale?: boolean;
    matchingScoreSubScores?: Record<string, number> | null;
    matchingScoreBlockers?: { requirement: string; status: string; note?: string }[];
    matchingScoreReasons?: Record<string, string> | null;
    matchingScoreConfidence?: string | null;
    matchingScoreDegraded?: boolean;
    isActive: boolean;
    hasDocuments: boolean;
    hasQADocuments: boolean;
    procurementOffice?: string;
    projectSubType?: string;
    availabilityOfTenderDocumentationStart?: string;
    roundOfQuestion1SubmitBy?: string;
    roundOfQuestion2SubmitBy?: string;
    roundOfQuestion3SubmitBy?: string;
    availabilityOfTenderDocumentationEnd?: string;
    tenderStart?: string;
    tenderEnd?: string;
    contractPeriodStart?: string;
    contractPeriodEnd?: string;
    executionPeriodStart?: string;
    executionPeriodEnd?: string;
    simapUrl?: string;
    projectType?: string;
    eligibilityCriteria?: EligibilityCriterion[] | null;
    awardCriteria?: AwardCriterion[] | null;
    keyPersonnelRequirements?: KeyPersonnelRequirement[] | null;
    referenceRequirements?: ReferenceRequirement[] | null;
    // The company's own call on this tender — deliberately separate from the
    // matching score (score = bot's opinion, decision = human's).
    decision?: 'pursue' | 'dismiss' | null;
    decisionReason?: string | null;
    // Deterministic classification tags from SIMAP's structured codes
    // (type:/proc:/canton:/cpv:<division>/construction-codes).
    tags?: string[];
    // SIMAP publication date of this tender's publication ("newest" sort).
    publicationDate?: string | null;
    processed?: boolean;             // the pipeline produced a summary (criteria, vector, score follow)
    latestPubType?: string | null;   // 'tender' | 'abandonment' | '…award' — the project's newest publication
    ndaGated?: boolean;              // only a non-disclosure agreement was published (tag docs:nda)
    ndaRequired?: boolean;           // the documents came in after an NDA was signed (tag docs:nda-required)
    // Who won, from SIMAP's award notice: the vendors in rank order, the
    // award date and the number of offers received.
    awardVendors?: AwardVendor[] | null;
    awardDate?: string | null;
    awardSubmissions?: number | null;
}

// Structured simap Q&A item (simap_core.qna_latest_v via GET /tender-qna).
interface QnaItem {
    roundNumber: number;
    roundState: string;
    roundEndDate?: string | null;
    questionNumber: number;
    question: string;
    answer?: string | null;
    answerState?: string | null;
    askedAt?: string | null;
    answeredAt?: string | null;
    updatedAt?: string | null;
}

interface ReferenceInfo {
    source: 'tender' | 'qa' | 'company';
    project_id: string;
    tender_id: string;
    document_path: string;
    page?: number;
}

interface BotResponse {
    text: string;
    references: Record<string, ReferenceInfo | null>;
    modelTier?: string;   // tier that wrote the answer (older backends: absent)
    model?: string;       // its Gemini model id
    costUsd?: number;     // what the question cost; sent to admins only
}

export interface Timeline {
    docs_available_from?: string;
    question_round_1?: string;
    question_round_2?: string;
    question_round_3?: string;
    docs_available_to?: string;
    tender_start?: string;
    tender_end?: string;
    contract_period_start?: string;
    contract_period_end?: string;
    execution_period_start?: string;
    execution_period_end?: string;
    // SIMAP published an abandonment / award for the project: the countdown
    // to the submission deadline is meaningless then.
    concluded?: 'abandoned' | 'awarded' | null;
}

interface TenderDocument {
    S3LocationPk: string;
    DocumentPath: string;
    Summary: string;
    DocumentPartPk: string;
    DocumentCategory: string;
    DocumentLanguage: string;
    Tags?: string[]; // For company documents
    ProcessingStatus?: string;  // 'complete' | 'protected' | 'corrupt' (see tenderDocuments.ts)
    SkipReason?: string;        // why the pipeline could not read it
}
interface TenderDocumentQA {
    S3LocationPk: string;
    DocumentPath: string;
}

interface ChatMessage {
    id: number;
    user: string;
    bot: BotResponse;
    timestamp: Date;
}

interface Skill {
    name: string;
    rating: string;
}

interface Language {
    name: string;
    rating: string; // e.g., 'Native', 'C1', 'B2', etc.
}

interface WorkforceMember {
    id: string;
    name: string;
    role: string;
    tenderReadyBio: string;
    industryExpertise: string[];
    skills: Skill[];
    languages: Language[];
    education: string[];
    certifications: string[];
}


interface CompanySection {
    sectionName: string;
    sectionOrder: number;
    description: string;
}

interface UserProfile {
    email?: string;
    username?: string;
    preferred_username?: string;
    "cognito:username"?: string;
    [key: string]: any;
}

interface ApiSkill {
    Name: string;
    Rating: string;
}

interface ApiLanguage {
    Name: string;
    Rating: string;
}

interface ApiWorkforceMember {
    MemberId: string;
    Name: string;
    Role: string;
    TenderReadyBio: string;
    IndustryExpertise: string[];
    Skills: ApiSkill[];
    Languages: ApiLanguage[];
    Education: string[];
    Certifications: string[];
    userEmail?: string;
    cognitoUsername?: string;
    cognitoGroups?: string[];
}


interface ApiTender {
    TenderId: string;
    ProjectId: string;
    PublicationNumber?: string;
    ProjectNumber?: string;
    Name: string;
    LotNumber: number;
    LotTitle: string;
    Industry: string;
    Deadline: string;
    IsActive: string;
    HasDocuments: string;
    HasQADocuments: string;
    CompanySize: number;
    Summary?: string;
    Requirements: string;
    IdealProfile: string;
    MatchingScore: number | null;
    MatchingScoreComputedAt?: string | null;
    MatchingScoreStale?: boolean;
    MatchingScoreSubScores?: Record<string, number> | null;
    MatchingScoreBlockers?: { requirement: string; status: string; note?: string }[];
    MatchingScoreReasons?: Record<string, string> | null;
    MatchingScoreConfidence?: string | null;
    MatchingScoreDegraded?: boolean;
    ProcurementOffice?: string;
    ProjectSubType?: string;
    AvailabilityOfTenderDocumentationStart?: string;
    RoundOfQuestion1SubmitBy?: string;
    RoundOfQuestion2SubmitBy?: string;
    RoundOfQuestion3SubmitBy?: string;
    AvailabilityOfTenderDocumentationEnd?: string;
    TenderStart?: string;
    TenderEnd?: string;
    ContractPeriodStart?: string;
    ContractPeriodEnd?: string;
    ExecutionPeriodStart?: string;
    ExecutionPeriodEnd?: string;
    SimapUrl?: string;
    ProjectType?: string;
    EligibilityCriteria?: EligibilityCriterion[] | null;
    AwardCriteria?: AwardCriterion[] | null;
    KeyPersonnelRequirements?: KeyPersonnelRequirement[] | null;
    ReferenceRequirements?: ReferenceRequirement[] | null;
    Decision?: string | null;
    DecisionReason?: string | null;
    Tags?: string[];
    PublicationDate?: string | null;
    Processed?: boolean;
    LatestPubType?: string | null;
    AwardVendors?: AwardVendor[] | null;
    AwardDate?: string | null;
    AwardSubmissions?: number | null;
}

interface PastProject {
    id: string;
    projectName: string;
    customerName: string;
    description: string;
    industry?: string;
    startYear: number;
    endYear?: number;
    userEmail?: string;
    cognitoUsername?: string;
    cognitoGroups?: string[];
}

interface CompanyProfile {
    description: string;
    sections: CompanySection[];
    pastProjects: PastProject[];
    // CPV divisions the company works in; defaults the list's industry
    // filter and drives the scorer's classification pre-gate.
    relevantIndustries: string[];
    // Topic slugs the company sells into; defaults the list's Topic filter.
    relevantTopics: string[];
}

interface ApiPastProject {
    ProjectId: string;
    ProjectName: string;
    CustomerName: string;
    Description: string;
    Industry?: string;
    StartYear: number;
    EndYear?: number;
    userEmail?: string;
    cognitoUsername?: string;
    cognitoGroups?: string[];
}

interface ApiCompanyProfile {
    Description?: string;
    Sections?: ApiCompanySection[];
    RelevantIndustries?: string[];
    RelevantTopics?: string[];
}

interface ApiCompanySection {
    SectionName: string;
    SectionOrder: number;
    Description: string;
}

interface AppConfig {
    api: {
        tender: {
            url: string;
        };
    };
}

interface FileForUpload {
    file: File;
    tags: string[];
    status: 'pending' | 'uploading' | 'processing' | 'success' | 'error';
    errorMessage?: string;
    uploadUrl?: string; // Store the presigned URL once fetched
    s3Key?: string;     // Store the final S3 key
}

interface CompanyDocument {
    S3LocationPk: string;
    DocumentPath: string;
    Tags: string[];
}

// Cast the imported config
const appConfig = config as AppConfig;

// --- Helper Components ---
const PanelPaper: React.FC<{ children: React.ReactNode, sx?: object }> = ({ children, sx }) => (
    <Paper elevation={1} sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', boxSizing: 'border-box', ...sx }}>
        {children}
    </Paper>
);

const DetailItem: React.FC<{ label: string, value?: string | number, children?: React.ReactNode }> = ({ label, value, children }) => (
    <Box sx={{ mb: 1 }}>
        <Typography component="span" variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary', fontSize: 'inherit' }}>{label}: </Typography>
        {value && <Typography component="span" variant="body2" sx={{ fontSize: 'inherit' }}>{value}</Typography>}
        {children}
    </Box>
);

// Chat input isolated in its own component: typing re-renders only this small
// tree, not the whole page (which recomputes and re-renders the full tender
// list on every render — noticeably slow with thousands of tenders).
const ChatInputBox: React.FC<{
    placeholder: string;
    disabled: boolean;
    sending: boolean;
    onSend: (text: string) => void;
}> = ({ placeholder, disabled, sending, onSend }) => {
    const [text, setText] = useState('');
    const trySend = () => {
        const toSend = text.trim();
        if (!toSend || disabled || sending) return;
        setText('');
        onSend(toSend);
    };
    return (
        <>
            <InputBase
                sx={{ ml: 1, flex: 1, fontSize: '0.875rem' }}
                placeholder={placeholder}
                multiline
                maxRows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        trySend();
                    }
                }}
                disabled={disabled || sending}
            />
            <IconButton
                size="small"
                disabled={disabled || sending || !text.trim()}
                onClick={trySend}
                sx={{
                    p: 1,
                    bgcolor: text.trim() && !disabled ? 'primary.main' : 'action.disabledBackground',
                    color: 'white',
                    '&:hover': { bgcolor: text.trim() && !disabled ? 'primary.dark' : 'action.disabledBackground' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.6)', bgcolor: 'action.disabledBackground' },
                }}
            >
                <SendIcon fontSize="small" />
            </IconButton>
        </>
    );
};

const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
    return (
        <ReactMarkdown
            components={{
                p: ({ node, children }) => <Typography variant="body2" gutterBottom>{children}</Typography>,
                ul: ({ node, children }) => <List dense sx={{ paddingLeft: 2, pt: 0, pb: 1 }}>{children}</List>,
                li: ({ node, children }) => (
                    <ListItem sx={{ display: 'list-item', listStyleType: 'disc', p: 0 }}>
                        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{children}</ListItemText>
                    </ListItem>
                ),
                strong: ({ node, children }) => <Typography component="span" sx={{ fontWeight: 'bold' }}>{children}</Typography>,
            }}
        >
            {content}
        </ReactMarkdown>
    );
};

const BotMessageRenderer: React.FC<{
    botResponse: BotResponse;
    onFileClick: (fileInfo: ReferenceInfo) => void;
    isLoading: boolean;
}> = ({ botResponse, onFileClick, isLoading }) => {
    const { text, references } = botResponse;

    // If there are no references, just render the text as plain markdown
    if (!references || Object.keys(references).length === 0) {
        return <MarkdownRenderer content={text} />;
    }

    // This component will be passed to ReactMarkdown
    const components = {
        // We find [citation] text *inside* other elements (like p, li)
        // by first wrapping them in `code` tags, then targeting the `code` tag here.
        code: ({ node, ...props }: any) => {
            const children = props.children;
            // Check if it's one of our citation code blocks
            if (typeof children === 'string' && children.startsWith('[') && children.endsWith(']')) {
                 const key = children.slice(1, -1); // Get the text inside, e.g., "file.pdf, page 5"
                 const refInfo = references[key];
                 
                 if (refInfo) {
                     // It's a FILE reference, make it clickable
                     return (
                        <Chip
                            label={key}
                            icon={<LaunchIcon fontSize="inherit" />}
                            size="small"
                            clickable
                            onClick={() => onFileClick(refInfo)}
                            disabled={isLoading}
                            sx={{ 
                                backgroundColor: 'rgba(0,0,255,0.08)', // Light blue tint
                                height: 'auto', 
                                minHeight: '22px', // Ensure it's not tiny
                                '& .MuiChip-label': { 
                                    whiteSpace: 'normal', // Allow text to wrap
                                    textOverflow: 'unset',
                                    display: 'block',
                                    paddingTop: '2px',
                                    paddingBottom: '2px',
                                },
                                ml: 0.5, 
                                mr: 0.5,
                                verticalAlign: 'middle', // Align with text
                            }}
                        />
                     );
                 } else if (references.hasOwnProperty(key)) {
                     // It's a STATIC reference like [Company Profile]
                     return (
                        <Chip 
                            label={key} 
                            size="small"
                            sx={{ 
                                backgroundColor: 'rgba(0,0,0,0.08)', // Light grey tint
                                height: 'auto',
                                minHeight: '22px',
                                '& .MuiChip-label': { 
                                    whiteSpace: 'normal',
                                    textOverflow: 'unset',
                                    display: 'block',
                                    paddingTop: '2px',
                                    paddingBottom: '2px',
                                },
                                ml: 0.5, 
                                mr: 0.5,
                                verticalAlign: 'middle',
                            }}
                        />
                     );
                 }
            }
            // It's not a citation, render as normal text (stripping the code tags)
            return <>{children}</>;
        },
        // Pass through all other default renderers
        p: ({ node, children }: any) => <Typography variant="body2" gutterBottom>{children}</Typography>,
        ul: ({ node, children }: any) => <List dense sx={{ paddingLeft: 2, pt: 0, pb: 1 }}>{children}</List>,
        li: ({ node, children }: any) => (
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', p: 0 }}>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{children}</ListItemText>
            </ListItem>
        ),
        strong: ({ node, children }: any) => <Typography component="span" sx={{ fontWeight: 'bold' }}>{children}</Typography>,
    };

    // This is the key: we find all [citations] in the text
    // and wrap them in `code` tags.
    // ReactMarkdown will then use our custom `code` component to render them.
    const parsedText = text.replace(/(\[.*?\])/g, '`$1`');
    
    return <ReactMarkdown components={components}>{parsedText}</ReactMarkdown>;
};

// --- API Service ---
const getApiAuthHeaders = (auth: any): Record<string, string> => {
    const userApiKey = auth.user?.profile?.['custom:api_key'] as string;
    const id_token = auth.user?.id_token;
    if (!auth.isAuthenticated || !id_token || !userApiKey) {
        throw new Error("Authentication is required.");
    }
    return { 'Content-Type': 'application/json', 'X-Api-Key': userApiKey, 'Authorization': id_token };
};

const mapApiTendersToLocal = (apiTenders: ApiTender[]): Tender[] => {
    return apiTenders.map(t => ({
        id: t.TenderId,
        projectId: t.ProjectId,
        publicationNumber: t.PublicationNumber,
        projectNumber: t.ProjectNumber,
        name: t.Name || "Unnamed Tender",
        lotNumber: t.LotNumber,
        lotTitle: t.LotTitle || 'N/A',
        industry: t.Industry || 'N/A',
        deadline: t.Deadline,
        summary: t.Summary || 'Not specified',
        companySize: t.CompanySize || 0,
        requirements: t.Requirements || 'N/A',
        idealProfile: t.IdealProfile || 'N/A',
        matchingScore: t.MatchingScore ?? null,
        matchingScoreComputedAt: t.MatchingScoreComputedAt ?? null,
        matchingScoreStale: t.MatchingScoreStale ?? false,
        matchingScoreSubScores: t.MatchingScoreSubScores ?? null,
        matchingScoreBlockers: t.MatchingScoreBlockers ?? [],
        matchingScoreReasons: t.MatchingScoreReasons ?? null,
        matchingScoreConfidence: t.MatchingScoreConfidence ?? null,
        matchingScoreDegraded: t.MatchingScoreDegraded ?? false,
        isActive: t.IsActive === "True",
        hasDocuments: t.HasDocuments === "True",
        hasQADocuments: t.HasQADocuments === "True",
        procurementOffice: t.ProcurementOffice || 'N/A',
        projectSubType: t.ProjectSubType || 'N/A',
        availabilityOfTenderDocumentationStart: t.AvailabilityOfTenderDocumentationStart,
        roundOfQuestion1SubmitBy: t.RoundOfQuestion1SubmitBy,
        roundOfQuestion2SubmitBy: t.RoundOfQuestion2SubmitBy,
        roundOfQuestion3SubmitBy: t.RoundOfQuestion3SubmitBy,
        availabilityOfTenderDocumentationEnd: t.AvailabilityOfTenderDocumentationEnd,
        tenderStart: t.TenderStart,
        tenderEnd: t.TenderEnd,
        contractPeriodStart: t.ContractPeriodStart,
        contractPeriodEnd: t.ContractPeriodEnd,
        executionPeriodStart: t.ExecutionPeriodStart,
        executionPeriodEnd: t.ExecutionPeriodEnd,
        simapUrl: t.SimapUrl,
        projectType: t.ProjectType,
        eligibilityCriteria: t.EligibilityCriteria ?? null,
        awardCriteria: t.AwardCriteria ?? null,
        keyPersonnelRequirements: t.KeyPersonnelRequirements ?? null,
        referenceRequirements: t.ReferenceRequirements ?? null,
        decision: (t.Decision === 'pursue' || t.Decision === 'dismiss') ? t.Decision : null,
        decisionReason: t.DecisionReason ?? null,
        tags: t.Tags ?? [],
        publicationDate: t.PublicationDate ?? null,
        processed: t.Processed,
        latestPubType: t.LatestPubType ?? null,
        awardVendors: t.AwardVendors ?? null,
        awardDate: t.AwardDate ?? null,
        awardSubmissions: t.AwardSubmissions ?? null,
        ndaGated: (t.Tags ?? []).includes(NDA_TAG),
        ndaRequired: (t.Tags ?? []).includes(NDA_REQUIRED_TAG),
    }));
};


const mapApiWorkforceToLocal = (apiMembers: ApiWorkforceMember[]): WorkforceMember[] => {
    return apiMembers.map(m => ({
        id: m.MemberId,
        name: m.Name,
        role: m.Role,
        tenderReadyBio: m.TenderReadyBio || '',
        industryExpertise: m.IndustryExpertise || [],
        skills: (m.Skills || []).map(s => ({ name: s.Name, rating: s.Rating })),
        languages: (m.Languages || []).map(l => ({ name: l.Name, rating: l.Rating })),
        education: m.Education || [],
        certifications: m.Certifications || [],
    }));
};


const mapApiPastProjectsToLocal = (apiProjects: ApiPastProject[]): PastProject[] => {
    return apiProjects.map(p => ({
        id: p.ProjectId,
        projectName: p.ProjectName,
        customerName: p.CustomerName,
        description: p.Description,
        industry: p.Industry,
        startYear: p.StartYear,
        endYear: p.EndYear,
    }));
};


const apiService = {
    fetchTenders: async (auth: any, group?: string) => {
        // Explicit annotation: TS otherwise flags a false circularity (7022)
        // for consts referenced inside a do/while whose condition is computed
        // in the loop body.
        const headers: Record<string, string> = getApiAuthHeaders(auth);
        // The endpoint is paginated (one DynamoDB page per response, well under
        // the Lambda 6MB cap) — follow `cursor` until exhausted. An array
        // response (pre-pagination lambda) is accepted too for deploy skew.
        const all: ApiTender[] = [];
        let cursor: string | null = null;
        do {
            const params = new URLSearchParams();
            if (group) params.set('company', group);
            if (cursor) params.set('cursor', cursor);
            const qs = params.toString();
            const url = `${appConfig.api.tender.url}/tenders${qs ? `?${qs}` : ''}`;
            const res = await fetch(url, { headers });
            if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch tenders');
            const data: ApiTender[] | { items: ApiTender[]; cursor: string | null } = await res.json();
            if (Array.isArray(data)) {
                all.push(...data);
                cursor = null;
            } else {
                all.push(...(data.items ?? []));
                cursor = data.cursor ?? null;
            }
        } while (cursor);
        return mapApiTendersToLocal(all).sort((a, b) => a.name.localeCompare(b.name));
    },
    fetchTenderDetails: async (auth: any, tenderId: string, group?: string): Promise<Tender> => {
        // The list serves slim rows; this returns the full record (summary,
        // requirements, ideal profile, EK/ZK criteria) for one tender.
        const headers: Record<string, string> = getApiAuthHeaders(auth);
        const params = new URLSearchParams();
        if (group) params.set('company', group);
        const qs = params.toString();
        const url = `${appConfig.api.tender.url}/tenders/${encodeURIComponent(tenderId)}${qs ? `?${qs}` : ''}`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch tender details');
        const data: ApiTender = await res.json();
        return mapApiTendersToLocal([data])[0];
    },
    deleteTenderDocumentQA: async (auth: any, projectId: string, documentPath: string, group?: string) => {
        const headers: Record<string, string> = getApiAuthHeaders(auth);
        const res = await fetch(`${appConfig.api.tender.url}/tender-documents-qa`, {
            method: 'DELETE', headers,
            body: JSON.stringify({ project_id: projectId, document_path: documentPath, cognitoGroups: group ? [group] : [] }),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to delete document');
        return res.json();
    },
    fetchTenderQna: async (projectId: string, auth: any): Promise<QnaItem[]> => {
        const headers: Record<string, string> = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/tender-qna/${encodeURIComponent(projectId)}`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch Q&A');
        return res.json();
    },
    // Semantic search: ranked tender ids + cosine similarity for the typed
    // term. Ids only — the page joins them to the list it already holds.
    searchTenders: async (auth: any, term: string): Promise<SemanticHit[]> => {
        const headers: Record<string, string> = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/tenders/search?q=${encodeURIComponent(term)}`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Semantic search failed');
        const data = await res.json();
        return ((data.items ?? []) as { TenderId: string; Similarity: number }[])
            .map(item => ({ tenderId: item.TenderId, similarity: item.Similarity }));
    },
    // The active topic taxonomy: names for the topic:/subtopic: tags and the
    // BKP/eBKP sub-level labels. Small, slow-changing, cached by react-query.
    getTopics: async (auth: any): Promise<TopicCatalog> => {
        const res = await fetch(`${appConfig.api.tender.url}/topics`, { headers: getApiAuthHeaders(auth) });
        if (!res.ok) throw new Error('Topics unavailable');
        const data = await res.json();
        return { version: data.version ?? null, topics: data.topics ?? [], codes: data.codes ?? {} };
    },
    // Company tags and what they match right now (computed by the backend
    // on every call, never stored).
    getCompanyTags: async (auth: any) => {
        const res = await fetch(`${appConfig.api.tender.url}/company-tags`, { headers: getApiAuthHeaders(auth) });
        if (!res.ok) throw new Error('Company tags unavailable');
        return parseCompanyTags(await res.json());
    },
    previewCompanyTag: async (auth: any, draft: TagDraft): Promise<TagPreview> => {
        const res = await fetch(`${appConfig.api.tender.url}/company-tags/preview`, {
            method: 'POST', headers: getApiAuthHeaders(auth), body: JSON.stringify(draftPayload(draft)) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Preview unavailable');
        return { candidates: data.candidates ?? [], keywordHits: data.keywordHits ?? [],
                 keywordMatchesUnavailable: Boolean(data.keywordMatchesUnavailable),
                 unavailableKeywords: data.unavailableKeywords ?? [] };
    },
    saveCompanyTag: async (auth: any, draft: TagDraft, tagId: string | null) => {
        const url = `${appConfig.api.tender.url}/company-tags${tagId ? `/${tagId}` : ''}`;
        const res = await fetch(url, { method: tagId ? 'PUT' : 'POST', headers: getApiAuthHeaders(auth),
                                       body: JSON.stringify(draftPayload(draft)) });
        if (!res.ok) throw new Error((await res.json()).message || 'Could not save the tag');
    },
    deleteCompanyTag: async (auth: any, tagId: string) => {
        const res = await fetch(`${appConfig.api.tender.url}/company-tags/${tagId}`,
                                { method: 'DELETE', headers: getApiAuthHeaders(auth) });
        if (!res.ok) throw new Error((await res.json()).message || 'Could not delete the tag');
    },
    // New SIMAP tenders per day for the last n days, zero-filled by the
    // backend (the market chart in the export dialog).
    getDailyTenderStats: async (auth: any, days: number): Promise<DailyTenderStats> => {
        const res = await fetch(`${appConfig.api.tender.url}/tenders/stats/daily?days=${days}`, { headers: getApiAuthHeaders(auth) });
        if (!res.ok) throw new Error('Tender statistics unavailable');
        const data = await res.json();
        return { from: data.from ?? '', to: data.to ?? '', days: data.days ?? [] };
    },
    recomputeMatches: async (auth: any, group?: string) => {
        const headers = getApiAuthHeaders(auth);
        let url = `${appConfig.api.tender.url}/matching/recompute`;
        if (group) url += `?company=${encodeURIComponent(group)}`;
        const res = await fetch(url, { method: 'POST', headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to trigger recompute');
        return res.json();
    },
    setTenderDecision: async (auth: any, tenderId: string, decision: 'pursue' | 'dismiss' | null,
        reason?: string, scoreAtDecision?: number | null, scoreComputedAt?: string | null, group?: string) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/tender-decisions`;
        const res = await fetch(url, {
            method: 'POST', headers,
            body: JSON.stringify({
                tenderId, decision,
                reason: reason || undefined,
                // Snapshot what the user saw, so a later re-score can't obscure
                // why the decision disagreed with the score at the time.
                scoreAtDecision: scoreAtDecision ?? undefined,
                scoreComputedAt: scoreComputedAt ?? undefined,
                cognitoGroups: group ? [group] : [],
            }),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to save decision');
        return res.json();
    },
    fetchTenderDocuments: async (tenderId: string, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/tender-documents/${tenderId}`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch documents');
        return res.json() as Promise<TenderDocument[]>;
    },
    fetchTenderDocumentsQA: async (projectId: string, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/tender-documents-qa/${projectId}`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch QA documents');
        return res.json() as Promise<TenderDocumentQA[]>;
    },
    fetchFileUrl: async (
        projectId: string, 
        tenderId: string,
        documentPath: string, 
        source: 'tender' | 'qa' | 'company',
        auth: any
    ) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/files/url?project_id=${encodeURIComponent(projectId)}&tender_id=${encodeURIComponent(tenderId)}&document_path=${encodeURIComponent(documentPath)}&source=${encodeURIComponent(source)}`;
        //console.log(url);
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to get file URL');
        return res.json() as Promise<{ url: string }>;
    },
    fetchWorkforce: async (auth: any, group?: string) => {
        const headers = getApiAuthHeaders(auth);
        let url = `${appConfig.api.tender.url}/workforce`;
        if (group) url += `?company=${encodeURIComponent(group)}`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch workforce');
        const data: ApiWorkforceMember[] = await res.json();
        return mapApiWorkforceToLocal(data).sort((a, b) => a.name.localeCompare(b.name));
    },

    fetchCompanyProfileAndProjects: async (auth: any, group?: string): Promise<CompanyProfile> => {
        const headers = getApiAuthHeaders(auth);
        let companyUrl = `${appConfig.api.tender.url}/company`;
        let projectsUrl = `${appConfig.api.tender.url}/projects`;
        if (group) {
            companyUrl += `?company=${encodeURIComponent(group)}`;
            projectsUrl += `?company=${encodeURIComponent(group)}`;
        }
        const [companyRes, projectsRes] = await Promise.all([fetch(companyUrl, { headers }), fetch(projectsUrl, { headers })]);
        if (!companyRes.ok) throw new Error((await companyRes.json()).message || 'Failed to fetch company profile');
        if (!projectsRes.ok) throw new Error((await projectsRes.json()).message || 'Failed to fetch projects');
        const companyData: ApiCompanyProfile = await companyRes.json();
        const projectsData = await projectsRes.json();
        const sections: CompanySection[] = (companyData.Sections || []).map(s => ({
            sectionName: s.SectionName,
            sectionOrder: s.SectionOrder,
            description: s.Description || ''
        })).sort((a, b) => a.sectionOrder - b.sectionOrder);
        return {
            description: '',
            sections,
            relevantIndustries: companyData.RelevantIndustries ?? [],
            relevantTopics: companyData.RelevantTopics ?? [],
            pastProjects: mapApiPastProjectsToLocal(projectsData).sort((a, b) => a.projectName.localeCompare(b.projectName))
        };
    },

    // Same route: a body with RelevantTopics updates the company's topic
    // setting ("My topics", the default of the Topic filter).
    saveRelevantTopics: async ({ topics, auth }: { topics: string[], auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/company`;
        const payload = {
            RelevantTopics: topics,
            CompanyId: auth.user?.profile?.["cognito:groups"]?.[0],
            cognitoGroups: auth.user?.profile?.["cognito:groups"],
        };
        const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to save relevant topics');
        return res.json();
    },

    // Rides on PUT /company: a body with RelevantIndustries updates the
    // company's industry setting (stored next to the profile version).
    saveRelevantIndustries: async ({ industries, auth }: { industries: string[], auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/company`;
        const payload = {
            RelevantIndustries: industries,
            CompanyId: auth.user?.profile?.["cognito:groups"]?.[0],
            cognitoGroups: auth.user?.profile?.["cognito:groups"],
        };
        const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to save relevant industries');
        return res.json();
    },

    generateCompanyDocUploadUrls: async ({ filesData, auth }: { filesData: { filename: string, tags: string[] }[], auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/company-documents`;
        const payload = {
            company_id: auth.user?.profile?.["cognito:groups"]?.[0],
            files: filesData,
            cognitoGroups: auth.user?.profile?.["cognito:groups"],
        };
        const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to generate upload URLs');
        return res.json() as Promise<{ upload_urls: { filename: string, safe_filename: string, upload_url: string, s3_key: string, applied_tags: { [key: string]: string } }[] }>;
    },

    // Presigned PUT URLs for Q&A uploads. The S3 trigger on the
    // tender-documents-qa/ prefix runs extraction, so uploading is all
    // that's needed — new docs appear in the QA list once processed.
    generateQaDocUploadUrls: async ({ projectId, filenames, auth }: { projectId: string, filenames: string[], auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/tender-documents-qa`;
        const payload = {
            project_id: projectId,
            files: filenames.map(filename => ({ filename })),
            cognitoGroups: auth.user?.profile?.["cognito:groups"],
        };
        const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to generate QA upload URLs');
        return res.json() as Promise<{ upload_urls: { filename: string, safe_filename: string, upload_url: string, s3_key: string }[] }>;
    },

    fetchCompanyDocuments: async (auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const companyId = auth.user?.profile?.["cognito:groups"]?.[0];

        if (!companyId) {
            throw new Error("User company group not found.");
        }
        
        const url = `${appConfig.api.tender.url}/company-documents?company_id=${encodeURIComponent(companyId)}`;
        
        const res = await fetch(url, { 
            method: 'GET', 
            headers, 
        });
        
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to fetch company documents');
        return res.json() as Promise<CompanyDocument[]>;
    },

    updateCompanyDocumentTags: async ({ s3LocationPk, tags, auth }: { s3LocationPk: string, tags: string[], auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/company-documents`;
        const companyId = auth.user?.profile?.["cognito:groups"]?.[0];

        const payload = {
            company_id: companyId,
            s3_location_pk: s3LocationPk,
            tags: tags
        };

        const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to update document tags');
        return res.json();
    },

    deleteCompanyDocument: async ({ s3LocationPk, auth }: { s3LocationPk: string, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/company-documents`;
        const companyId = auth.user?.profile?.["cognito:groups"]?.[0];

        const payload = {
            company_id: companyId,
            s3_location_pk: s3LocationPk
        };
        
        // The backend DELETE expects a body
        const res = await fetch(url, { method: 'DELETE', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to delete document');
        return res.json();
    },

    // --- MUTATION FUNCTIONS ---
    saveWorkforceMember: async ({ memberData, auth }: { memberData: Omit<WorkforceMember, 'id'> & { id?: string }, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const isEditing = !!memberData.id;
        const memberId = isEditing ? memberData.id : uuidv4();
        const url = isEditing ? `${appConfig.api.tender.url}/workforce/${memberId}` : `${appConfig.api.tender.url}/workforce`;
        const method = isEditing ? 'PUT' : 'POST';

        const apiPayload: Partial<ApiWorkforceMember> = {
            MemberId: memberId!,
            Name: memberData.name,
            Role: memberData.role,
            TenderReadyBio: memberData.tenderReadyBio,
            IndustryExpertise: memberData.industryExpertise,
            Skills: (memberData.skills || []).map(s => ({ Name: s.name, Rating: s.rating })),
            Languages: (memberData.languages || []).map(l => ({ Name: l.name, Rating: l.rating })),
            Education: memberData.education,
            Certifications: memberData.certifications,
            userEmail: auth.user?.profile?.email,
            cognitoUsername: auth.user?.profile?.["cognito:username"],
            cognitoGroups: auth.user?.profile?.["cognito:groups"],
        };
        const res = await fetch(url, { method, headers, body: JSON.stringify(apiPayload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to save workforce member');
        return res.json();
    },


    deleteWorkforceMember: async ({ memberId, auth }: { memberId: string, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/workforce/${memberId}`;
        const res = await fetch(url, { method: 'DELETE', headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to delete member');
        return res.json();
    },

    createCompanySection: async ({ sectionName, auth }: { sectionName: string, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/company`;
        
        // We'll calculate the order on the backend to avoid race conditions
        const payload = {
            CompanyId: auth.user?.profile?.["cognito:groups"]?.[0] || 'quantum',
            SectionName: sectionName,
            Description: '', // Empty description for new sections
            userEmail: auth.user?.profile?.email,
            cognitoUsername: auth.user?.profile?.["cognito:username"],
            cognitoGroups: auth.user?.profile?.["cognito:groups"],
        };
        const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to create section');
        return res.json();
    },

    saveCompanySection: async ({ sectionData, auth }: { sectionData: { sectionName: string, sectionOrder: number, description: string }, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/company`;
        const payload = {
            CompanyId: auth.user?.profile?.["cognito:groups"]?.[0] || 'quantum',
            SectionName: sectionData.sectionName,
            SectionOrder: sectionData.sectionOrder,
            Description: sectionData.description,
            userEmail: auth.user?.profile?.email,
            cognitoUsername: auth.user?.profile?.["cognito:username"],
            cognitoGroups: auth.user?.profile?.["cognito:groups"],
        };
        const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to save section');
        return res.json();
    },

    deleteCompanySection: async ({ sectionOrder, auth }: { sectionOrder: number, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/company`;
        const payload = {
            CompanyId: auth.user?.profile?.["cognito:groups"]?.[0] || 'quantum',
            SectionOrder: sectionOrder,
        };
        const res = await fetch(url, { method: 'DELETE', headers, body: JSON.stringify(payload) });
        if (!res.ok) {
            const errorData = await res.json();
            console.error('Delete failed:', errorData);
            throw new Error(errorData.message || 'Failed to delete section');
        }
        const result = await res.json();
        return result;
    },

    savePastProject: async ({ projectData, auth }: { projectData: Omit<PastProject, 'id'> & { id?: string }, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const isEditing = !!projectData.id;
        const projectId = isEditing ? projectData.id : uuidv4();
        const url = isEditing ? `${appConfig.api.tender.url}/projects/${projectId}` : `${appConfig.api.tender.url}/projects`;
        const method = isEditing ? 'PUT' : 'POST';

        const apiPayload: ApiPastProject = {
            ProjectId: projectId!,
            ProjectName: projectData.projectName, 
            CustomerName: projectData.customerName, 
            Description: projectData.description,
            Industry: projectData.industry, 
            StartYear: projectData.startYear, 
            EndYear: projectData.endYear,
            userEmail: auth.user?.profile?.email,
            cognitoUsername: auth.user?.profile?.["cognito:username"],
            cognitoGroups: auth.user?.profile?.["cognito:groups"],
        };
        const res = await fetch(url, { method, headers, body: JSON.stringify(apiPayload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to save project');
        return res.json();
    },

    deletePastProject: async ({ projectId, auth }: { projectId: string, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/projects/${projectId}`;
        const res = await fetch(url, { method: 'DELETE', headers });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to delete project');
        return res.json();
    },

    createProposal: async ({ tenderId, auth }: { tenderId: string, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/proposals`;
        
        // We send the tenderId. The backend handles CompanyId via the Auth Token.
        const payload = {
            tenderId: tenderId,
            cognitoGroups: auth.user?.profile?.["cognito:groups"] 
        };

        const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to start proposal drafting');
        return res.json(); 
    },

    createReview: async ({ tenderId, docPaths, auth }: { tenderId: string, docPaths: string[], auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        const url = `${appConfig.api.tender.url}/reviews`;
        const payload = {
            tenderId: tenderId,
            docPaths: docPaths, // Send the list of selected S3 keys
            cognitoGroups: auth.user?.profile?.["cognito:groups"] 
        };
        const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || 'Failed to start review');
        return res.json(); 
    },

    sendChatMessage: async ({ payload, auth }: { payload: any, auth: any }) => {
        const headers = getApiAuthHeaders(auth);
        console.log(payload);
        const url = `${appConfig.api.tender.url}/tenders/${payload.tenderId}/question`;
        const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error((await res.json()).message || `HTTP error! Status: ${res.status}`);
        return res.json();
    }
};

// Search input with its own draft state: keystrokes re-render only this tiny
// component, and the page (with its few hundred list rows) only re-filters
// after a 250ms typing pause — typing stays smooth however long the list is.
const SearchTendersField: React.FC<{
    isSmallScreen: boolean;
    onDebouncedChange: (value: string) => void;
    // A term set by the page (deep link to a tender): shown in the field and
    // applied like typed input.
    preset?: string;
    // Shown under the field while a term is typed: hit counts and whether
    // the filter box applies.
    helper?: React.ReactNode;
}> = ({ isSmallScreen, onDebouncedChange, preset, helper }) => {
    const [draft, setDraft] = useState('');
    useEffect(() => {
        if (preset !== undefined) setDraft(preset);
    }, [preset]);
    useEffect(() => {
        const handle = setTimeout(() => onDebouncedChange(draft), 250);
        return () => clearTimeout(handle);
    }, [draft, onDebouncedChange]);
    return (
        <TextField fullWidth variant="outlined" label="Search Tenders..." value={draft}
            onChange={(e) => setDraft(e.target.value)} size="small"
            helperText={draft.trim() !== '' ? helper : undefined}
            FormHelperTextProps={{ sx: { fontSize: '0.62rem', mt: 0.25, mx: 0.5 } }}
            sx={{ mb: 2, bgcolor: 'background.paper', '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize } }} />
    );
};

// Second, longer debounce for the semantic request: one Bedrock round trip
// per typing pause (~600ms including the field's own 250ms), never per
// keystroke.
function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const handle = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(handle);
    }, [value, delayMs]);
    return debounced;
}

// --- Main Component ---
const TenderQPage: React.FC = () => {
    // --- Local UI State ---
    const [searchTerm, setSearchTerm] = useState('');
    // …/tenderq?tender=<id> (e.g. from an alert mail): the tender to open once
    // the list has loaded, and the search preset that keeps it in the list.
    const deepLinkTenderId = useRef<string | null>(new URLSearchParams(window.location.search).get('tender'));
    const [searchPreset, setSearchPreset] = useState<string | undefined>(undefined);
    const [activeFilters, setActiveFilters] = useState<ActiveFilters>({ status: 'open', hasDocuments: true, showDismissed: false, hideLowFit: true, nda: 'all', applyToSearch: false });
    // The filter box starts folded to one summary line so the tender list
    // gets the height; unfolded, it takes its space from the list (which
    // keeps a floor) and scrolls inside the left panel, never the page.
    const [filtersCollapsed, setFiltersCollapsed] = useState(true);
    const toggleFiltersCollapsed = () => setFiltersCollapsed(collapsed => !collapsed);
    const [projectTypeFilter, setProjectTypeFilter] = useState<'rfp' | 'simap'>('simap');
    // Tag facet chips (Type / Industry / Canton): within a facet OR, across facets AND.
    const [tagSelection, setTagSelection] = useState<TagSelection>(emptyTagSelection);
    // Topics whose sub-topics are unfolded in the Topic menu (plus, always,
    // the parents of selected sub-topics).
    const [expandedTopics, setExpandedTopics] = useState<string[]>([]);
    // "My industries": defaults the industry facet to the company's
    // RelevantIndustries setting until industry chips are picked explicitly.
    const [myIndustriesOnly, setMyIndustriesOnly] = useState(true);
    // "My topics": same for the company's RelevantTopics setting and the Topic facet.
    const [myTopicsOnly, setMyTopicsOnly] = useState(true);
    const [sortBy, setSortBy] = useState<TenderSort>('fit');
    const [selectedTender, setSelectedTender] = useState<Tender | null>(null);
    const [displayedTimelineData, setDisplayedTimelineData] = useState<Timeline | null>(null);
    // Keyed by tender id so each tender keeps its own thread when the user
    // switches between them in the same session. Cleared on page reload.
    const [chatMessagesByTender, setChatMessagesByTender] = useState<Record<string, ChatMessage[]>>({});
    const chatMessages = selectedTender ? (chatMessagesByTender[selectedTender.id] ?? []) : [];
    // Model for chat answers, remembered in the browser for every tender.
    const [chatModelTier, setChatModelTier] = useState<ChatModelTier>(loadChatModelTier);
    const handleChatModelChange = (tier: ChatModelTier) => {
        setChatModelTier(tier);
        saveChatModelTier(tier);
    };
    const [leftPanelOpen, setLeftPanelOpen] = useState(false);
    const [rightPanelOpen, setRightPanelOpen] = useState(false);
    const [manageWorkforceModalOpen, setManageWorkforceModalOpen] = useState(false);
    const [addEditMemberModalOpen, setAddEditMemberModalOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<WorkforceMember | null>(null);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [memberToDeleteId, setMemberToDeleteId] = useState<string | null>(null);
    const [manageCompanyModalOpen, setManageCompanyModalOpen] = useState(false);
    const [addEditProjectModalOpen, setAddEditProjectModalOpen] = useState(false);
    const [editingProject, setEditingProject] = useState<PastProject | null>(null);
    const [confirmDeleteProjectDialogOpen, setConfirmDeleteProjectDialogOpen] = useState(false);
    const [projectToDeleteId, setProjectToDeleteId] = useState<string | null>(null);
    const [confirmDeleteSectionDialogOpen, setConfirmDeleteSectionDialogOpen] = useState(false);
    const [sectionToDeleteOrder, setSectionToDeleteOrder] = useState<number | null>(null);
    const [openArchitectureDialog, setOpenArchitectureDialog] = useState(false);
    const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
    const [selectedCompanyDocuments, setSelectedCompanyDocuments] = useState<Set<string>>(new Set());
    const [documentSelectionDialogOpen, setDocumentSelectionDialogOpen] = useState(false);
    const [chatError, setChatError] = useState<string | null>(null);
    const [qaDocumentsDialogOpen, setQaDocumentsDialogOpen] = useState(false);
    const [qnaDialogOpen, setQnaDialogOpen] = useState(false);
    // Confirmation before deleting an uploaded QA document (quantum only).
    const [qaDocToDelete, setQaDocToDelete] = useState<TenderDocumentQA | null>(null);
    const [qaDocDeleting, setQaDocDeleting] = useState(false);
    const [qaUploadDialogOpen, setQaUploadDialogOpen] = useState(false);
    const [qaFilesForUpload, setQaFilesForUpload] = useState<{ file: File; status: 'pending' | 'uploading' | 'done' | 'error'; errorMessage?: string }[]>([]);
    const [isQaUploading, setIsQaUploading] = useState(false);
    // In-app viewer for PDFs and images. Office docs (xlsx/docx/pptx) still
    // open externally — the browser can't render them natively.
    const [inlineViewer, setInlineViewer] = useState<{ url: string; fileName: string; kind: 'pdf' | 'image'; page?: number } | null>(null);
    const [manageCompanyDocsModalOpen, setManageCompanyDocsModalOpen] = useState(false);
    const [selectedFilesForUpload, setSelectedFilesForUpload] = useState<FileForUpload[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [draftFlowDialogOpen, setDraftFlowDialogOpen] = useState(false);
    const [reviewFlowDialogOpen, setReviewFlowDialogOpen] = useState(false);
    const [onboardingFlowDialogOpen, setOnboardingFlowDialogOpen] = useState(false);
    const [chatFlowDialogOpen, setChatFlowDialogOpen] = useState(false);
    // Optional comment for every dismissal, and for pursuing a low-scoring tender.
    const [decisionDialog, setDecisionDialog] = useState<{ tender: Tender; decision: 'pursue' | 'dismiss' } | null>(null);
    const [decisionReasonText, setDecisionReasonText] = useState('');
    // CSV export of the list as shown.
    const [exportOpen, setExportOpen] = useState(false);

    // --- Hooks ---
    const navigate = useNavigate();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    // Per-tender session id used ONLY for logging. Never affects sending/processing.
    const conversationIdByTenderRef = useRef<Record<string, string>>({});
    const userPrimaryGroup = (auth.user?.profile?.["cognito:groups"] as string[] | undefined)?.[0];
    const [recomputing, setRecomputing] = useState(false);
    const [recomputeMsg, setRecomputeMsg] = useState<string | null>(null);
    const [showScoreDetails, setShowScoreDetails] = useState(false);
    // Which score dimension / blocker row is expanded to show its LLM reason.
    const [expandedScoreDim, setExpandedScoreDim] = useState<string | null>(null);
    const [expandedBlocker, setExpandedBlocker] = useState<number | null>(null);
    const isAuthReady = auth.isAuthenticated && !!auth.user?.profile?.['custom:api_key'];

    // --- Queries ---
    // Instant paint: the last fetched tender list is kept in localStorage and
    // used as initialData — the page renders immediately (sorted, top tender
    // auto-selected) while a fresh fetch runs in the background and quietly
    // replaces it. First-ever visit falls back to the normal loading screen.
    const tendersCacheKey = userPrimaryGroup ? `qabot.tendersCache.${userPrimaryGroup}` : null;
    const tendersQuery = useQuery({
        queryKey: ['tenders', userPrimaryGroup],
        queryFn: () => apiService.fetchTenders(auth, userPrimaryGroup),
        enabled: isAuthReady,
        initialData: () => {
            if (!tendersCacheKey) return undefined;
            try {
                const raw = localStorage.getItem(tendersCacheKey);
                return raw ? (JSON.parse(raw) as Tender[]) : undefined;
            } catch { return undefined; }
        },
        initialDataUpdatedAt: 0, // cached data is always "stale" -> background refetch fires on load
    });
    const tenders = tendersQuery.data ?? [];
    useEffect(() => {
        if (!tendersCacheKey || !tendersQuery.data || tendersQuery.data.length === 0) return;
        try {
            localStorage.setItem(tendersCacheKey, JSON.stringify(tendersQuery.data));
        } catch { /* storage full or blocked — instant paint just won't happen next time */ }
    }, [tendersCacheKey, tendersQuery.data]);

    // Full record for the selected tender, fetched on demand and cached per
    // tender. The list rows are slim (no summaries/criteria). If this fetch
    // fails (e.g. pre-slim lambda still deployed), the UI falls back to
    // whatever the list row carries.
    const tenderDetailQuery = useQuery({
        queryKey: ['tenderDetail', selectedTender?.id, userPrimaryGroup],
        queryFn: () => apiService.fetchTenderDetails(auth, selectedTender!.id, userPrimaryGroup),
        enabled: isAuthReady && !!selectedTender,
        staleTime: 5 * 60 * 1000,
        retry: 1,
    });

    // Semantic search for the typed term. Cached per term; on error the
    // "Similar tenders" section is simply absent and search behaves as before.
    const semanticTerm = useDebouncedValue(searchTerm.trim(), 350);
    const semanticSearchQuery = useQuery({
        queryKey: ['tenderSearch', semanticTerm, userPrimaryGroup],
        queryFn: () => apiService.searchTenders(auth, semanticTerm),
        enabled: isAuthReady && isSemanticTerm(semanticTerm),
        staleTime: 5 * 60 * 1000,
        retry: 0,
        placeholderData: keepPreviousData,
    });

    const topicsQuery = useQuery({
        queryKey: ['topics'],
        queryFn: () => apiService.getTopics(auth),
        enabled: isAuthReady,
        staleTime: 10 * 60 * 1000,
        retry: 0,
    });
    // Handed to the tag module on every render (idempotent), so topic tags
    // label themselves as soon as the catalog arrives; until then, slugs.
    setTopicCatalog(topicsQuery.data);

    // Company tags and their matches; refetched after every tag change. On
    // error the list simply shows no tag badges.
    const companyTagsQuery = useQuery({
        queryKey: ['companyTags', userPrimaryGroup],
        queryFn: () => apiService.getCompanyTags(auth),
        enabled: isAuthReady,
        staleTime: 5 * 60 * 1000,
        retry: 0,
    });
    const companyTags = companyTagsQuery.data ?? EMPTY_COMPANY_TAGS;
    const tagHits = useMemo(() => hitsByTender(companyTags), [companyTags]);
    const [companyTagSelection, setCompanyTagSelection] = useState<string[]>([]);
    const [companyTagsDialogOpen, setCompanyTagsDialogOpen] = useState(false);
    const effectiveCompanyTags = useMemo(() => activeTagSelection(companyTagSelection, companyTags.tags),
        [companyTagSelection, companyTags.tags]);

    const workforceQuery = useQuery({
        queryKey: ['workforce', userPrimaryGroup],
        queryFn: () => apiService.fetchWorkforce(auth, userPrimaryGroup),
        enabled: isAuthReady,
    });
    const workforce = workforceQuery.data ?? [];

    const companyProfileQuery = useQuery({
        queryKey: ['companyProfile', userPrimaryGroup],
        queryFn: () => apiService.fetchCompanyProfileAndProjects(auth, userPrimaryGroup),
        enabled: isAuthReady,
        placeholderData: { description: '', sections: [], pastProjects: [], relevantIndustries: [], relevantTopics: [] }
    });
    const companyProfile = companyProfileQuery.data ?? { description: '', sections: [], pastProjects: [], relevantIndustries: [], relevantTopics: [] };

    const tenderDocumentsQuery = useQuery({
        queryKey: ['tenderDocuments', selectedTender?.id],
        queryFn: () => apiService.fetchTenderDocuments(selectedTender!.id, auth),
        enabled: !!selectedTender?.id && isAuthReady,
    });
    // Documents the pipeline could not read (password-protected, unreadable)
    // are listed but never selectable: they have no content.
    const { readable: tenderDocuments, skipped: skippedDocuments } = useMemo(
        () => splitDocuments(tenderDocumentsQuery.data ?? []), [tenderDocumentsQuery.data]);

    const tenderDocumentsQAQuery = useQuery<TenderDocumentQA[], Error>({
        queryKey: ['tenderDocumentsQA', selectedTender?.projectId],
        queryFn: () => apiService.fetchTenderDocumentsQA(selectedTender!.projectId, auth),
        enabled: !!selectedTender?.projectId && isAuthReady,
    });
    const tenderDocumentsQA = tenderDocumentsQAQuery.data ?? [];

    // Structured simap Q&A for the selected project (small, fetched on select
    // so the detail row can show count + last-updated without opening the dialog).
    const tenderQnaQuery = useQuery<QnaItem[], Error>({
        queryKey: ['tenderQna', selectedTender?.projectId],
        queryFn: () => apiService.fetchTenderQna(selectedTender!.projectId, auth),
        enabled: !!selectedTender?.projectId && isAuthReady,
        staleTime: 5 * 60 * 1000,
    });
    const tenderQna = tenderQnaQuery.data ?? [];
    const qnaIsUnanswered = (q: QnaItem) => !q.answer || q.answerState === 'unanswered' || q.answerState === 'draft';
    const qnaUnanswered = tenderQna.filter(qnaIsUnanswered).length;
    const qnaLastUpdated = tenderQna.reduce<string | null>(
        (max, q) => (q.updatedAt && (!max || q.updatedAt > max) ? q.updatedAt : max), null);
    const qnaRecentlyUpdated = !!qnaLastUpdated
        && (Date.now() - new Date(qnaLastUpdated).getTime()) < 3 * 24 * 3600 * 1000;
    const qnaRounds = useMemo(() => {
        const byRound = new Map<number, QnaItem[]>();
        for (const q of tenderQna) {
            const list = byRound.get(q.roundNumber) ?? [];
            list.push(q);
            byRound.set(q.roundNumber, list);
        }
        return Array.from(byRound.entries()).sort((a, b) => a[0] - b[0]);
    }, [tenderQna]);

    const companyDocumentsQuery = useQuery<CompanyDocument[], Error>({
        queryKey: ['companyDocuments', userPrimaryGroup],
        queryFn: () => apiService.fetchCompanyDocuments(auth),
        enabled: isAuthReady && !!userPrimaryGroup,
    });
    const companyDocuments = companyDocumentsQuery.data ?? [];

    useEffect(() => {
        if (tenderDocumentsQAQuery.isSuccess) {
            // console.log('QA Documents Query Success:', tenderDocumentsQAQuery.data);
        }
    }, [tenderDocumentsQAQuery.isSuccess, tenderDocumentsQA]);
    // --- Side Effects ---
    useEffect(() => {
        if (tenderDocumentsQuery.isSuccess && tenderDocumentsQuery.data) {
            const allDocPaths = new Set(splitDocuments(tenderDocumentsQuery.data).readable.map(doc => doc.DocumentPath));
            setSelectedDocuments(allDocPaths);
        }
    }, [tenderDocumentsQuery.isSuccess, tenderDocumentsQuery.data]);


    // --- Mutations ---
    // Named as a custom hook (use-prefix) so the rules-of-hooks lint accepts
    // the useMutation call inside; it is always called unconditionally below.
    const useInvalidatingMutation = (mutationFn: (vars: any) => Promise<any>, queryKeyToInvalidate: string) => {
        return useMutation({
            mutationFn,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [queryKeyToInvalidate, userPrimaryGroup] });
            },
        });
    };

    const saveWorkforceMemberMutation = useInvalidatingMutation(apiService.saveWorkforceMember, 'workforce');
    const deleteWorkforceMemberMutation = useInvalidatingMutation(apiService.deleteWorkforceMember, 'workforce');
    const createCompanySectionMutation = useMutation({
        mutationFn: ({ sectionName, auth }: { sectionName: string, auth: any }) => 
            apiService.createCompanySection({ sectionName, auth }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['companyProfile'] });
        }
    });

    const saveCompanySectionMutation = useMutation({
        mutationFn: ({ sectionData, auth }: { sectionData: any, auth: any }) => apiService.saveCompanySection({ sectionData, auth }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['companyProfile'] });
        }
    });
    
    const deleteCompanySectionMutation = useMutation({
        mutationFn: ({ sectionOrder, auth }: { sectionOrder: number, auth: any }) => {
            return apiService.deleteCompanySection({ sectionOrder, auth });
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['companyProfile'] });
        },
        onError: (error) => {
            console.error('Delete mutation failed:', error);
        }
    });
    const savePastProjectMutation = useInvalidatingMutation(apiService.savePastProject, 'companyProfile');
    const deletePastProjectMutation = useInvalidatingMutation(apiService.deletePastProject, 'companyProfile');
    const saveRelevantIndustriesMutation = useInvalidatingMutation(apiService.saveRelevantIndustries, 'companyProfile');
    const saveRelevantTopicsMutation = useInvalidatingMutation(apiService.saveRelevantTopics, 'companyProfile');

    const sendChatMessageMutation = useMutation({
        mutationFn: (vars: { payload: any, auth: any }) => apiService.sendChatMessage(vars),
        onSuccess: (result, variables) => {
            const botResponse: BotResponse = {
                text: result.answer || "Sorry, I couldn't get a response.",
                references: result.references || {},
                modelTier: result.modelTier,
                model: result.model,
                costUsd: result.costUsd,
            };
            const tenderId: string = variables.payload.tenderId;
            setChatMessagesByTender(prev => ({
                ...prev,
                [tenderId]: (prev[tenderId] ?? []).map(msg =>
                    msg.id === variables.payload.clientMessageId ? { ...msg, bot: botResponse } : msg
                ),
            }));
        },
        onError: (error, variables) => {
            const errorMessage = error instanceof Error ? error.message : 'Failed to send message.';
            setChatError(errorMessage);
            const errorResponse: BotResponse = {
                text: `Error: ${errorMessage}`,
                references: {}
            };
            const tenderId: string = variables.payload.tenderId;
            setChatMessagesByTender(prev => ({
                ...prev,
                [tenderId]: (prev[tenderId] ?? []).map(msg =>
                    msg.id === variables.payload.clientMessageId ? { ...msg, bot: errorResponse } : msg
                ),
            }));
        }
    });

    const classifyForInlineViewer = (path: string): 'pdf' | 'image' | null => {
        const ext = path.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return 'pdf';
        if (ext && ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
        return null;
    };

    const getFileUrlMutation = useMutation({
        mutationFn: ({ projectId, tenderId, documentPath, source, page }: {
            projectId: string;
            tenderId: string;
            documentPath: string;
            source: 'tender' | 'qa' | 'company';
            page?: number;
        }) =>
            apiService.fetchFileUrl(projectId, tenderId, documentPath, source, auth),
        onSuccess: (data, variables) => {
            if (!data.url) return;
            const kind = classifyForInlineViewer(variables.documentPath);
            if (kind) {
                setInlineViewer({
                    url: data.url,
                    fileName: variables.documentPath.split('/').pop() || variables.documentPath,
                    kind,
                    page: variables.page,
                });
            } else {
                // Office docs and other unsupported types: let the browser
                // handle it (typically a download).
                window.open(data.url, '_blank', 'noopener,noreferrer');
            }
        },
        onError: (error) => {
            console.error('Failed to get file URL:', error);
            // You could show a toast notification here
        }
    });


    // Upload Q&A files straight to S3 via presigned URLs. Extraction runs
    // asynchronously off the S3 trigger, so the list is refetched a few times
    // afterwards to pick up the newly processed documents.
    const handleQaUpload = async () => {
        if (!selectedTender || isQaUploading) return;
        const pendingIdx = qaFilesForUpload
            .map((f, i) => (f.status === 'pending' || f.status === 'error') ? i : -1)
            .filter(i => i !== -1);
        if (pendingIdx.length === 0) return;

        const projectId = selectedTender.projectId;
        const setStatus = (idx: number, status: 'uploading' | 'done' | 'error', errorMessage?: string) =>
            setQaFilesForUpload(prev => prev.map((p, i) => i === idx ? { ...p, status, errorMessage } : p));

        setIsQaUploading(true);
        try {
            const { upload_urls } = await apiService.generateQaDocUploadUrls({
                projectId,
                filenames: pendingIdx.map(i => qaFilesForUpload[i].file.name),
                auth,
            });
            await Promise.all(pendingIdx.map(async (idx) => {
                const item = qaFilesForUpload[idx];
                const urlInfo = upload_urls.find(u => u.filename === item.file.name);
                if (!urlInfo) { setStatus(idx, 'error', 'Could not get upload URL.'); return; }
                setStatus(idx, 'uploading');
                try {
                    const res = await fetch(urlInfo.upload_url, { method: 'PUT', body: item.file });
                    if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
                    setStatus(idx, 'done');
                } catch (e) {
                    setStatus(idx, 'error', e instanceof Error ? e.message : 'Upload failed');
                }
            }));
            [10000, 30000, 60000].forEach(ms => setTimeout(() =>
                queryClient.invalidateQueries({ queryKey: ['tenderDocumentsQA', projectId] }), ms));
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Upload failed';
            pendingIdx.forEach(idx => setStatus(idx, 'error', message));
        } finally {
            setIsQaUploading(false);
        }
    };

    const handleOpenQADocument = (documentPath: string) => {
        if (!selectedTender) return;
        getFileUrlMutation.mutate({
            projectId: selectedTender.projectId,
            tenderId: selectedTender.id,
            documentPath: documentPath,
            source: 'qa' 
        });
    };

    const handleOpenDocumentFromDialog = (
        documentPath: string, 
        source: 'tender' | 'qa' | 'company'
    ) => {
        if (!selectedTender) return;
        
        getFileUrlMutation.mutate({
            projectId: selectedTender.projectId,
            tenderId: selectedTender.id,
            documentPath: documentPath,
            source: source 
        });
    };

    const handleFileClick = (fileInfo: ReferenceInfo) => {
        getFileUrlMutation.mutate({
            projectId: fileInfo.project_id,
            tenderId: fileInfo.tender_id,
            documentPath: fileInfo.document_path,
            source: fileInfo.source,
            page: fileInfo.page // Pass the page number
        });
    };

    // --- Event Handlers & Logic ---
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatMessages]);

    const handleSelectTender = (tender: Tender) => {
        if (selectedTender?.id === tender.id) return;
        setSelectedTender(tender);
        setExpandedScoreDim(null);
        setExpandedBlocker(null);
        setDisplayedTimelineData({
            docs_available_from: tender.availabilityOfTenderDocumentationStart, question_round_1: tender.roundOfQuestion1SubmitBy,
            question_round_2: tender.roundOfQuestion2SubmitBy, question_round_3: tender.roundOfQuestion3SubmitBy,
            docs_available_to: tender.availabilityOfTenderDocumentationEnd, tender_start: tender.tenderStart,
            tender_end: tender.tenderEnd,
            concluded: !isConcluded(tender.latestPubType) ? null
                : tender.latestPubType === 'abandonment' ? 'abandoned' : 'awarded',
        });
    };

    const handleSendChatMessage = async (questionToSend: string) => {
        if (!questionToSend.trim() || !selectedTender || !isAuthReady) return;

        const clientMessageId = Date.now();

        // Only send the last 10 messages (backend caps at this too)
        const historyForPayload = chatMessages.slice(-10).map(m => ({ user: m.user, bot: typeof m.bot === 'object' && m.bot !== null ? m.bot.text : m.bot }));
        const userMessage: ChatMessage = {
            id: clientMessageId,
            user: questionToSend,
            bot: { text: '...', references: {} },
            timestamp: new Date()
        };
        const tenderIdForAppend = selectedTender.id;
        setChatMessagesByTender(prev => ({
            ...prev,
            [tenderIdForAppend]: [...(prev[tenderIdForAppend] ?? []), userMessage],
        }));

        // Send only [s3_path, part_key] — backend fetches summaries from DynamoDB
        const documentKeys = Array.from(selectedDocuments).map(path => {
            const doc = tenderDocuments.find(d => d.DocumentPath === path);
            return doc ? [doc.S3LocationPk, doc.DocumentPartPk] : null;
        }).filter(Boolean);

        const selectedCompanyDocIds = Array.from(selectedCompanyDocuments).map(path => {
            const doc = companyDocuments.find(d => d.DocumentPath === path);
            return doc ? doc.S3LocationPk : null;
        }).filter(Boolean);

        // Best-effort per-tender session id, for logging ONLY. Fully wrapped so it can
        // NEVER affect sending — if anything here fails, conversationId stays undefined
        // and the request goes out exactly as before. UUID generated without crypto.
        let conversationId: string | undefined;
        try {
            let cid = conversationIdByTenderRef.current[tenderIdForAppend];
            if (!cid) {
                cid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
                    const r = (Math.random() * 16) | 0;
                    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
                });
                conversationIdByTenderRef.current[tenderIdForAppend] = cid;
            }
            conversationId = cid;
        } catch { /* logging only — ignore */ }

        // Lean payload: user identity comes from Cognito authorizer claims,
        // company data (workforce, profile, projects) is fetched server-side from DynamoDB
        const payload = {
            clientMessageId,
            conversationId,
            prompt: questionToSend,
            tenderId: selectedTender.id, projectId: selectedTender.projectId, lotNumber: selectedTender.lotNumber, lotTitle: selectedTender.lotTitle,
            includeAllDocuments: selectedDocuments.size === tenderDocuments.length && tenderDocuments.length > 0 ? 1 : 0,
            selectedDocuments: documentKeys,
            selectedCompanyDocuments: selectedCompanyDocIds,
            history: historyForPayload,
            modelTier: chatModelTier,
        };
        sendChatMessageMutation.mutate({ payload, auth });
    };

    // Each row in `tenders` is one publication of a (project, lot). Keep only the
    // latest publication per (projectId, lotNumber) — older versions stay in
    // `tenders` for future use but aren't shown. Memoized: the list is large,
    // and without memoization every unrelated re-render recomputes it.
    const latestTendersByLot = useMemo(() => {
        const parsePublicationNumber = (value?: string): [number, number] => {
            const match = /^(\d+)-(\d+)$/.exec((value ?? '').trim());
            return match ? [parseInt(match[1], 10), parseInt(match[2], 10)] : [-1, -1];
        };
        return Array.from(
            tenders.reduce<Map<string, Tender>>((acc, tender) => {
                const key = `${tender.projectId}::${tender.lotNumber}`;
                const current = acc.get(key);
                if (!current) { acc.set(key, tender); return acc; }
                const [candA, candB] = parsePublicationNumber(tender.publicationNumber);
                const [currA, currB] = parsePublicationNumber(current.publicationNumber);
                if (candA > currA || (candA === currA && candB > currB)) acc.set(key, tender);
                return acc;
            }, new Map()).values(),
        );
    }, [tenders]);

    // The checkbox filters alone (no search term, no tag facets). Shared by
    // the list and, when the filters apply to search, by the "Similar
    // tenders" candidates.
    const passesBoxFilters = useMemo(() => {
        // "Open for Submission" keeps tenders whose submission deadline
        // (TenderStart = the simap offer deadline) is today or later — i.e. a
        // proposal can still be handed in. Tenders with a missing or
        // unparseable deadline (e.g. uploaded RFPs) are kept.
        const today = startOfToday();
        return (tender: Tender): boolean => {
            const documentMatch = !activeFilters.hasDocuments || tender.hasDocuments;
            // "Status": open for submission by default; closed, awarded,
            // abandoned or all on request (exports, analysis).
            const activityMatch = passesStatusFilter(activeFilters.status, tender, today);
            const decisionMatch = activeFilters.showDismissed || tender.decision !== 'dismiss';
            // Uploaded RFPs are curated by hand — the fit floor (and the tag
            // facets, see filteredTenders) are SIMAP noise filters and must
            // not hide them.
            const fitMatch = projectTypeFilter === 'rfp'
                || passesFitFloor(tender.matchingScore, tender.decision, activeFilters.hideLowFit);
            const ndaMatch = passesNdaFilter(activeFilters.nda, tender);
            return documentMatch && activityMatch && decisionMatch && fitMatch && ndaMatch;
        };
    }, [activeFilters, projectTypeFilter]);

    // A typed search reaches across ALL tenders of the selected project type:
    // the checkbox filters (and the tag facets, see filteredTenders) are
    // bypassed so a searched-for tender can never hide behind a filter —
    // unless "Apply filters to search" is ticked.
    const filtersOff = filtersBypassed(searchTerm, activeFilters.applyToSearch);

    // Everything except the tag chips: search, checkboxes, project type.
    // Split out so the facet chip counts reflect this list — a chip's count
    // must not change because you clicked it.
    const baseFilteredTenders = useMemo(() => {
        return latestTendersByLot.filter(tender => {
            // Matches the tender name, or the simap project number with or
            // without the leading "#" (e.g. "#39911" or "39911").
            const numberTerm = searchTerm.trim().replace(/^#/, '');
            const searchTermMatch = tender.name.toLowerCase().includes(searchTerm.toLowerCase())
                || (numberTerm !== '' && (tender.projectNumber ?? '').includes(numberTerm));
            const projectTypeMatch = projectTypeFilter === 'rfp'
                ? tender.projectType === 'rfp'
                : tender.projectType === 'tender';
            return searchTermMatch && projectTypeMatch && (filtersOff || passesBoxFilters(tender));
        });
    }, [latestTendersByLot, searchTerm, projectTypeFilter, filtersOff, passesBoxFilters]);

    // Tags of the list before tag filtering; each facet dropdown scopes them
    // by the OTHER dropdowns for its counts (scopedTags), so a count is what
    // clicking the item will show.
    const baseTags = useMemo(() => baseFilteredTenders.map(t => t.tags), [baseFilteredTenders]);
    const effectiveTagSelection = useMemo(
        () => effectiveSelection(tagSelection, companyProfile.relevantIndustries, myIndustriesOnly,
            companyProfile.relevantTopics, myTopicsOnly),
        [tagSelection, companyProfile.relevantIndustries, myIndustriesOnly, companyProfile.relevantTopics, myTopicsOnly]);

    // The facet menus' items, computed once per list/selection change rather
    // than on every render (hover, chat, search typing): each facet's counts
    // scoped by the other facets; Topic as a tree.
    const facetMenus = useMemo(() => {
        // Labels come from the topic catalog, which arrives after the list:
        // rebuild when it does, or the menu keeps showing slugs.
        setTopicCatalog(topicsQuery.data);
        const plain = (facet: 'type' | 'industry' | 'canton'): FacetOption[] => {
            const tagOf = (value: string) => facet === 'industry' ? `cpv:${value}` : `${facet}:${value}`;
            return Object.entries(facetCounts(scopedTags(baseTags, effectiveTagSelection, facet))[facet])
                .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                .map(([value, count]) => ({ value, count, label: parseTag(tagOf(value)).label }));
        };
        return {
            type: plain('type'), industry: plain('industry'), canton: plain('canton'),
            topic: topicTree(scopedTags(baseTags, effectiveTagSelection, 'topic')),
        };
    }, [baseTags, effectiveTagSelection, topicsQuery.data]);

    const filteredTenders = useMemo(() => {
        return baseFilteredTenders
            // Uploaded RFPs carry no SIMAP tags — tag filtering (incl. the
            // "My industries" default) would silently hide all of them.
            .filter(tender => filtersOff || projectTypeFilter === 'rfp'
                || matchesTagSelection(tender.tags, effectiveTagSelection))
            // "Your tags": any selected company tag.
            .filter(tender => filtersOff
                || passesCompanyTagFilter(tender.id, effectiveCompanyTags, tagHits, tender.projectType))
            .sort(tenderComparator(sortBy));
    }, [baseFilteredTenders, filtersOff, projectTypeFilter, effectiveTagSelection, effectiveCompanyTags,
        tagHits, sortBy]);

    // Semantic hits joined to the tenders the page holds; exact matches are
    // never repeated. Same reach as the search itself: current project type
    // only, checkbox/tag filters ignored unless they apply to search.
    const searching = searchTerm.trim() !== '';
    const searchingSemantic = searching && isSemanticTerm(searchTerm);
    const similarResults = useMemo(() => {
        if (!searchingSemantic || !semanticSearchQuery.data) return [];
        const candidates = latestTendersByLot.filter(t =>
            (projectTypeFilter === 'rfp' ? t.projectType === 'rfp' : t.projectType === 'tender')
            && (filtersOff || (passesBoxFilters(t)
                && (projectTypeFilter === 'rfp' || matchesTagSelection(t.tags, effectiveTagSelection))
                && passesCompanyTagFilter(t.id, effectiveCompanyTags, tagHits, t.projectType))));
        return similarTenders(semanticSearchQuery.data, candidates, new Set(filteredTenders.map(t => t.id)));
    }, [searchingSemantic, semanticSearchQuery.data, latestTendersByLot, projectTypeFilter, filteredTenders,
        filtersOff, passesBoxFilters, effectiveTagSelection, effectiveCompanyTags, tagHits]);
    const showSimilarSection = searchingSemantic && (similarResults.length > 0 || semanticSearchQuery.isFetching);

    const activeTagCount = tagSelection.type.length + tagSelection.industry.length + tagSelection.canton.length
        + tagSelection.topic.length + tagSelection.subtopic.length + effectiveCompanyTags.length;
    const clearTagFilters = () => {
        setTagSelection(emptyTagSelection);
        setCompanyTagSelection([]);
    };
    // "Your tags" counts over the list before tag filtering, like the other facets.
    const companyTagFacetCounts = useMemo(
        () => companyTagCounts(baseFilteredTenders.map(t => t.id), tagHits), [baseFilteredTenders, tagHits]);
    // For the manager: title and open/closed of every tender the page holds.
    const tenderInfoById = useMemo(() => {
        const today = startOfToday();
        return new Map(latestTendersByLot.map(t => [t.id, { title: t.name, open: passesStatusFilter('open', t, today) }]));
    }, [latestTendersByLot]);
    const companyTagListCounts = useMemo(
        () => tagMatchCounts(tagHits, id => tenderInfoById.get(id)), [tagHits, tenderInfoById]);

    useEffect(() => {
        // Guard on data presence, NOT isLoading: with cached initialData the
        // query reports isLoading during its background refetch, which used to
        // suppress the auto-select on the instant-paint path.
        if (tenders.length === 0) return;
        if (deepLinkTenderId.current) {
            // Deep link: select the tender once and put its number in the
            // search, so it stays in the list whatever the filters say.
            const wanted = deepLinkTenderId.current;
            deepLinkTenderId.current = null;
            const hit = tenders.find(t => t.id === wanted);
            if (hit) {
                handleSelectTender(hit);
                if (hit.projectNumber) setSearchPreset(`#${hit.projectNumber}`);
                return;
            }
        }
        const isSelectedTenderInFilteredList = selectedTender && filteredTenders.some(t => t.id === selectedTender.id);
        if (filteredTenders.length > 0 && (!selectedTender || !isSelectedTenderInFilteredList)) {
            handleSelectTender(filteredTenders[0]);
        } else if (filteredTenders.length === 0) {
            // No exact match: fall through to the best semantic hit once it arrives.
            if (similarResults.length > 0) handleSelectTender(similarResults[0].tender);
            else setSelectedTender(null);
        }
    }, [tendersQuery.data, searchTerm, activeFilters, projectTypeFilter, tagSelection, myIndustriesOnly, myTopicsOnly, similarResults]);

    // One row for both the exact-match list and the "Similar tenders" section;
    // the section passes the similarity for its muted chip.
    const renderTenderRow = (tender: Tender, similarity?: number) => {
        const rowPct = tender.matchingScore == null ? null
            : Math.round(tender.matchingScore <= 1 ? tender.matchingScore * 100 : tender.matchingScore);
        const badges = rowBadges(tender.tags);
        // Deadline (due / closed / none) and missing documents, always shown:
        // outlined chips with an icon — a shape the filled tag badges never use.
        const statusMarkers = rowStatus(tender, startOfToday());
        const statusIcon = (kind: RowStatusKind) => {
            switch (kind) {
                case 'no-documents': return <FolderOffOutlinedIcon />;
                case 'nda': return <LockOutlinedIcon />;
                case 'nda-required': return <LockOpenOutlinedIcon />;
                case 'closed': return <EventBusyOutlinedIcon />;
                case 'abandoned': return <BlockOutlinedIcon />;
                case 'awarded': return <GavelOutlinedIcon />;
                case 'unprocessed': case 'unscored': return <HourglassEmptyOutlinedIcon />;
                default: return <EventOutlinedIcon />;
            }
        };
        const statusColor = (kind: RowStatusKind): 'warning' | 'error' | 'default' =>
            kind === 'closed' || kind === 'nda' ? 'warning' : kind === 'abandoned' ? 'error' : 'default';
        return (
        <ListItemButton key={similarity == null ? tender.id : `similar-${tender.id}`} selected={selectedTender?.id === tender.id}
            onClick={() => { handleSelectTender(tender); if (isSmallScreen) setLeftPanelOpen(false); }}
            sx={{
                borderRadius: 1, mb: 0.25, py: 0.5,
                display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                background: scoreRowBar(tender.matchingScore),
                '&:hover': { background: scoreRowBar(tender.matchingScore, 0.3) },
                '&.Mui-selected': {
                    background: tender.matchingScore == null ? undefined : scoreRowBar(tender.matchingScore, 0.36),
                    '&:hover': { background: tender.matchingScore == null ? undefined : scoreRowBar(tender.matchingScore, 0.44) },
                },
            }}>
            {/* Status line above the title: deadline / documents chips on the
                left, then similarity, fit and the decision buttons on the
                right. The title below spans the full row width. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 22 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, flexGrow: 1, minWidth: 0 }}>
                    {statusMarkers.map(marker => (
                        <Tooltip key={marker.kind} title={marker.tooltip} arrow>
                            <Chip size="small" variant="outlined"
                                color={statusColor(marker.kind)}
                                icon={statusIcon(marker.kind)}
                                label={marker.label}
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600,
                                    color: statusColor(marker.kind) === 'default' ? 'text.secondary' : undefined,
                                    '& .MuiChip-icon': { fontSize: '0.8rem', ml: 0.5 }, '& .MuiChip-label': { px: 0.6 } }} />
                        </Tooltip>
                    ))}
                </Box>
                {similarity != null && (
                    <Typography sx={{ flexShrink: 0, fontSize: '0.6rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {similarityLabel(similarity)}
                    </Typography>
                )}
                {rowPct != null && (
                    <Typography sx={{ flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, color: fitColor(rowPct) }}>
                        {rowPct}%
                    </Typography>
                )}
                <Box sx={{ display: 'flex', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={tender.decision === 'pursue' ? 'Pursuing — click to undo' : 'Pursue this tender'} arrow>
                        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleDecisionClick(tender, 'pursue')}>
                            {tender.decision === 'pursue'
                                ? <CheckCircleIcon sx={{ fontSize: '0.95rem', color: 'success.main' }} />
                                : <CheckCircleOutlineIcon sx={{ fontSize: '0.95rem', color: 'text.disabled' }} />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={tender.decision === 'dismiss' ? `Dismissed${tender.decisionReason ? `: ${tender.decisionReason}` : ''} — click to undo` : 'Dismiss this tender'} arrow>
                        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleDecisionClick(tender, 'dismiss')}>
                            {tender.decision === 'dismiss'
                                ? <CancelIcon sx={{ fontSize: '0.95rem', color: 'error.main' }} />
                                : <CancelOutlinedIcon sx={{ fontSize: '0.95rem', color: 'text.disabled' }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <ListItemText
                sx={{ my: 0 }}
                primary={tender.name}
                primaryTypographyProps={{ sx: { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize } }}
                secondary={badges.length > 0 || tagHits.has(tender.id) ? (
                    <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.25 }}>
                        <CompanyTagBadges hits={tagHits.get(tender.id)} />
                        {badges.map(b => (
                            <Box key={`${b.kind}:${b.value}`} component="span" sx={tagBadgeSx(b)}>{b.label}</Box>
                        ))}
                    </Box>
                ) : null}
                secondaryTypographyProps={{ component: 'span', sx: { fontSize: isSmallScreen ? '0.65rem' : '0.75rem' } }} />
        </ListItemButton>
        );
    };


    // --- CRUD Handlers ---
    const handleSaveMember = (memberDataFromDialog: Omit<WorkforceMember, 'id'> & { id?: string }) => {
        saveWorkforceMemberMutation.mutate({ memberData: memberDataFromDialog, auth }, {
            onSuccess: () => handleCloseAddEditModal(),
        });
    };

    const handleConfirmRemoveMember = () => {
        if (!memberToDeleteId) return;
        deleteWorkforceMemberMutation.mutate({ memberId: memberToDeleteId, auth }, {
            onSuccess: () => handleCloseConfirmDeleteDialog(),
        });
    };

    const handleRequestRemoveSection = (sectionOrder: number) => {
        setSectionToDeleteOrder(sectionOrder);
        setConfirmDeleteSectionDialogOpen(true);
    };

    const handleConfirmRemoveSection = () => {
        if (sectionToDeleteOrder === null) return;
        deleteCompanySectionMutation.mutate({ sectionOrder: sectionToDeleteOrder, auth }, {
            onSuccess: () => handleCloseConfirmDeleteSectionDialog(),
        });
    };

    const handleCloseConfirmDeleteSectionDialog = () => {
        setConfirmDeleteSectionDialogOpen(false);
        setSectionToDeleteOrder(null);
        deleteCompanySectionMutation.reset();
    };

    const handleSaveProject = (projectDataFromDialog: Omit<PastProject, 'id'> & { id?: string }) => {
        savePastProjectMutation.mutate({ projectData: projectDataFromDialog, auth }, {
            onSuccess: () => handleCloseAddEditProjectModal(),
        });
    };

    const handleConfirmRemoveProject = () => {
        if (!projectToDeleteId) return;
        deletePastProjectMutation.mutate({ projectId: projectToDeleteId, auth }, {
            onSuccess: () => handleCloseConfirmDeleteProjectDialog(),
        });
    };

    // --- UI State Handlers ---
    const handleLeftDrawerToggle = () => setLeftPanelOpen(!leftPanelOpen);
    const handleRightDrawerToggle = () => setRightPanelOpen(!rightPanelOpen);
    const handleOpenManageWorkforceModal = () => setManageWorkforceModalOpen(true);
    const handleCloseManageWorkforceModal = () => setManageWorkforceModalOpen(false);
    const handleOpenAddEditModal = (member: WorkforceMember | null) => { setEditingMember(member); setAddEditMemberModalOpen(true); };
    const handleCloseAddEditModal = () => { setAddEditMemberModalOpen(false); setEditingMember(null); saveWorkforceMemberMutation.reset(); };
    const handleRequestRemoveMember = (memberId: string) => { setMemberToDeleteId(memberId); setConfirmDeleteDialogOpen(true); };
    const handleCloseConfirmDeleteDialog = () => { setConfirmDeleteDialogOpen(false); setMemberToDeleteId(null); deleteWorkforceMemberMutation.reset(); };
    const handleOpenManageCompanyModal = () => setManageCompanyModalOpen(true);
    const handleCloseManageCompanyModal = () => setManageCompanyModalOpen(false);
    const handleOpenAddEditProjectModal = (project: PastProject | null) => { setEditingProject(project); setAddEditProjectModalOpen(true); };
    const handleCloseAddEditProjectModal = () => { setAddEditProjectModalOpen(false); setEditingProject(null); savePastProjectMutation.reset(); };
    const handleRequestRemoveProject = (projectId: string) => { setProjectToDeleteId(projectId); setConfirmDeleteProjectDialogOpen(true); };
    const handleCloseConfirmDeleteProjectDialog = () => { setConfirmDeleteProjectDialogOpen(false); setProjectToDeleteId(null); deletePastProjectMutation.reset(); };
    const handleOpenArchitectureDialog = () => setOpenArchitectureDialog(true);
    const handleCloseArchitectureDialog = () => setOpenArchitectureDialog(false);
    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => { setActiveFilters({ ...activeFilters, [event.target.name]: event.target.checked }); };

    // --- Tender decision (Pursue/Dismiss) handlers ---
    const tenderScorePct = (tender: Tender): number | null =>
        tender.matchingScore == null ? null
            : Math.round(tender.matchingScore <= 1 ? tender.matchingScore * 100 : tender.matchingScore);

    const applyDecision = async (tender: Tender, decision: 'pursue' | 'dismiss' | null, reason?: string) => {
        // Optimistic: update the cached tender list immediately, resync on error.
        queryClient.setQueryData(['tenders', userPrimaryGroup], (old: Tender[] | undefined) =>
            (old ?? []).map(t => t.id === tender.id ? { ...t, decision, decisionReason: reason || null } : t));
        try {
            await apiService.setTenderDecision(auth, tender.id, decision, reason,
                tenderScorePct(tender), tender.matchingScoreComputedAt ?? null, userPrimaryGroup);
        } catch (e) {
            console.error('Failed to save tender decision', e);
            queryClient.invalidateQueries({ queryKey: ['tenders', userPrimaryGroup] });
        }
    };

    const handleDecisionClick = (tender: Tender, decision: 'pursue' | 'dismiss') => {
        if (tender.decision === decision) { applyDecision(tender, null); return; } // click again to undo
        const pct = tenderScorePct(tender);
        // When the decision contradicts the score, ask (optionally) what the
        // score missed — that disagreement is the scorer's audit data.
        const contradicts = pct != null
            && ((decision === 'dismiss' && pct >= 70) || (decision === 'pursue' && pct <= 40));
        if (decision === 'dismiss' || contradicts) {
            setDecisionReasonText('');
            setDecisionDialog({ tender, decision });
        } else {
            applyDecision(tender, decision);
        }
    };

    // --- Render ---
    const drawerWidth = isSmallScreen ? '80vw' : '280px';

    // Full-screen loading ONLY when there is truly nothing to show: with
    // cached initialData the query still reports isLoading during its
    // background refetch, which used to blank the instant-painted page.
    if (!tendersQuery.data && tendersQuery.isFetching) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Typography>Loading application...</Typography></Box>
    }
    if (tendersQuery.error) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Alert severity="error">Error loading application data: {tendersQuery.error.message}</Alert></Box>
    }

    // Under the search field: "12 matches · 20 similar" and whether the
    // filter box applies, with a one-click way to make it apply.
    const searchHelper = searching ? (
        <>
            {searchCaption(filteredTenders.length, similarResults.length)}
            {semanticSearchQuery.isFetching && ' …'}
            {' · '}
            {activeFilters.applyToSearch ? 'filters applied' : (
                <>
                    {'filters ignored — '}
                    <Box component="span" onClick={() => setActiveFilters({ ...activeFilters, applyToSearch: true })}
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}>apply</Box>
                </>
            )}
        </>
    ) : undefined;

    const leftPanelContent = (
        <PanelPaper sx={{ minHeight: 0, height: '100%', p: 1.5, bgcolor: 'grey.200' }}>
            <ToggleButtonGroup
                value={projectTypeFilter}
                exclusive
                onChange={(_e: React.MouseEvent<HTMLElement>, newValue: 'rfp' | 'simap' | null) => { if (newValue !== null) setProjectTypeFilter(newValue); }}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
            >
                <ToggleButton value="rfp" sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize, textTransform: 'none' }}>Uploaded</ToggleButton>
                <ToggleButton value="simap" sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize, textTransform: 'none' }}>Simap</ToggleButton>
            </ToggleButtonGroup>
            {/* The list re-filter and the facet menus are a transition: React
                keeps the keystrokes flowing and renders the new list when it
                gets to it, instead of freezing the input mid-word. */}
            <SearchTendersField isSmallScreen={isSmallScreen} preset={searchPreset}
                onDebouncedChange={(value) => startTransition(() => setSearchTerm(value))}
                helper={searchHelper} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium', fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Recent Tenders</Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.62rem' }}>· by</Typography>
                <Select variant="standard" disableUnderline value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as TenderSort)}
                    sx={{ fontSize: '0.62rem', color: 'text.secondary',
                          '& .MuiSelect-select': { py: 0, pr: '16px !important' } }}>
                    <MenuItem value="fit" dense sx={{ fontSize: '0.7rem' }}>fit</MenuItem>
                    <MenuItem value="deadline" dense sx={{ fontSize: '0.7rem' }}>deadline</MenuItem>
                    <MenuItem value="newest" dense sx={{ fontSize: '0.7rem' }}>newest</MenuItem>
                </Select>
                <Tooltip title="Fit: best company match first. Deadline: soonest submission deadline first (tenders without one last). Newest: latest publication first. Pursued tenders always stay on top, dismissed at the bottom. Each row is shaded and filled to its score: green = strong fit, red = weak." arrow placement="top" enterTouchDelay={0}>
                    <InfoOutlinedIcon sx={{ fontSize: '0.8rem', color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {!searching && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.62rem', whiteSpace: 'nowrap' }}>
                            {listCountLabel(filteredTenders.length)}
                        </Typography>
                    )}
                    <Tooltip title="Export the list as shown to CSV (opens in Excel)" arrow>
                        <IconButton size="small" onClick={() => setExportOpen(true)} sx={{ p: 0.25 }}>
                            <FileDownloadOutlinedIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Box sx={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, overflowY: 'auto', minHeight: 160, minWidth: 0, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', p: 0.5 }}>
                <List dense disablePadding>
                    {filteredTenders.map((tender) => renderTenderRow(tender))}
                    {showSimilarSection && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, pt: 1, pb: 0.5 }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                    Similar tenders
                                </Typography>
                                {semanticSearchQuery.isFetching && <CircularProgress size={10} thickness={5} />}
                                <Divider sx={{ flexGrow: 1 }} />
                            </Box>
                            {similarResults.map(({ tender, similarity }) => renderTenderRow(tender, similarity))}
                        </>
                    )}
                    {filteredTenders.length === 0 && similarResults.length === 0 && !tendersQuery.isLoading && !semanticSearchQuery.isFetching
                        && <ListItemText primary="No tenders found." sx={{ p: 1.5, textAlign: 'center' }} />}
                </List>
            </Box>
            <Divider sx={{ my: 1 }} />
            {/* The heading unfolds the box; it starts folded so the list gets
                the height. */}
            <Box onClick={toggleFiltersCollapsed}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', mb: filtersCollapsed ? 0 : 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium', fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Filters</Typography>
                {filtersCollapsed && (
                    <Typography variant="caption" noWrap sx={{ color: 'text.secondary', fontSize: '0.62rem', minWidth: 0 }}>
                        · {filterSummary(activeFilters)}{activeTagCount > 0 ? ` · ${activeTagCount} tag${activeTagCount === 1 ? '' : 's'}` : ''}
                    </Typography>
                )}
                <ExpandMoreIcon sx={{ ml: 'auto', fontSize: '1rem', color: 'text.disabled',
                    transform: filtersCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 150ms' }} />
            </Box>
            {!filtersCollapsed && (
            <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                       minHeight: 0, flexShrink: 1, overflowY: 'auto' }}>
                {/* Status: 'open' is the old "Open for Submission" checkbox; the
                    other choices exist for exports and analysis. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Tooltip title="Open for submission: deadline today or later and no award or abandonment notice (the default). Closed: the deadline passed without a notice. Awarded / Abandoned: SIMAP published the notice. All: no status filter, for exports and analysis." arrow placement="top" enterTouchDelay={0}>
                        <Typography sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize, cursor: 'help' }}>Status</Typography>
                    </Tooltip>
                    <TextField select size="small" value={activeFilters.status}
                        onChange={(e) => setActiveFilters({ ...activeFilters, status: e.target.value as StatusFilter })}
                        sx={{ minWidth: 160, '& .MuiInputBase-input': { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize, py: 0.4 } }}>
                        {(Object.keys(STATUS_FILTER_LABELS) as StatusFilter[]).map(value => (
                            <MenuItem key={value} value={value} sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>{STATUS_FILTER_LABELS[value]}</MenuItem>
                        ))}
                    </TextField>
                </Box>
                <FormControlLabel control={<Checkbox checked={activeFilters.hasDocuments} onChange={handleFilterChange} name="hasDocuments" size="small" />} label="Has Documents" sx={{ display: 'block', '& .MuiTypography-root': { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize } }} />
                <FormControlLabel control={<Checkbox checked={activeFilters.showDismissed} onChange={handleFilterChange} name="showDismissed" size="small" />} label="Show Dismissed" sx={{ display: 'block', '& .MuiTypography-root': { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize } }} />
                <FormControlLabel control={<Checkbox checked={activeFilters.hideLowFit} onChange={handleFilterChange} name="hideLowFit" size="small" />} label="Hide low fit (< 40%)" sx={{ display: 'block', '& .MuiTypography-root': { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize } }} />
                {/* Tenders that need a non-disclosure agreement: still behind
                    it (marker "Documents behind NDA", capped score) or released
                    after signing ("NDA required"). */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                    <Tooltip title="Tenders that need a non-disclosure agreement. Behind NDA: only the NDA is published, the score is estimated from the SIMAP notice and capped until somebody signs and adds the documents. NDA required: the documents are in, but every bidder has to sign an NDA to get them." arrow>
                        <Typography sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize, cursor: 'help' }}>NDA</Typography>
                    </Tooltip>
                    <TextField select size="small" value={activeFilters.nda}
                        onChange={(e) => setActiveFilters({ ...activeFilters, nda: e.target.value as NdaFilter })}
                        sx={{ minWidth: 160, '& .MuiInputBase-input': { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize, py: 0.4 } }}>
                        <MenuItem value="all" sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Show all</MenuItem>
                        <MenuItem value="only" sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Only behind NDA</MenuItem>
                        <MenuItem value="required" sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Only NDA required</MenuItem>
                        <MenuItem value="hide" sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Hide behind NDA</MenuItem>
                    </TextField>
                </Box>
                {/* Tag facets from SIMAP's structured codes and the topic
                    taxonomy as compact multi-select dropdowns. Each dropdown
                    counts the tenders that pass the checkboxes above AND the
                    other dropdowns (standard facet counts: a number is what
                    clicking the item shows). Every dropdown has a pinned
                    "Select all / Clear selection" item; "My industries" is a
                    pinned toggle inside Industry. Topic is hierarchical: items
                    grouped by market group, a chevron unfolds a topic's
                    sub-topics (or BKP/eBKP codes) underneath; ticking the topic
                    takes it whole, ticking sub-topics takes only those. */}
                {([['type', 'Type'], ['industry', 'Industry'], ['topic', 'Topic'], ['canton', 'Canton']] as ['type' | 'industry' | 'topic' | 'canton', string][]).map(([facet, title]) => {
                    const SUB = 'sub:'; // sub-topics ride in the Topic Select as 'sub:<value>' items
                    const tagOf = (value: string) => facet === 'industry' ? `cpv:${value}` : `${facet}:${value}`;
                    const tree = facet === 'topic' ? facetMenus.topic : [];
                    const options: FacetOption[] = facetMenus[facet];
                    if (options.length === 0) return null;
                    const parentOf: Record<string, string> = {};
                    const childLabel: Record<string, string> = {};
                    tree.forEach(t => t.children.forEach(c => { parentOf[c.value] = t.value; childLabel[c.value] = c.label; }));
                    const labelOf = (value: string): string => {
                        if (value.startsWith(SUB)) {
                            const child = value.slice(SUB.length);
                            const parent = options.find(o => o.value === parentOf[child])?.label;
                            return `${parent ?? ''} › ${childLabel[child] ?? child}`;
                        }
                        return options.find(o => o.value === value)?.label
                            ?? (value === NO_TOPIC ? 'Other / no topic' : parseTag(tagOf(value)).label);
                    };
                    const selected = tagSelection[facet];
                    // Company defaults: "My industries" (RelevantIndustries) on the
                    // Industry facet, "My topics" (RelevantTopics) on the Topic facet.
                    const mine = facet === 'industry'
                        ? { defaults: companyProfile.relevantIndustries, on: myIndustriesOnly, set: setMyIndustriesOnly, label: 'My industries' }
                        : facet === 'topic'
                            ? { defaults: companyProfile.relevantTopics, on: myTopicsOnly, set: setMyTopicsOnly, label: 'My topics' }
                            : null;
                    const hasMine = mine !== null && mine.defaults.length > 0;
                    const explicit = facet === 'topic'
                        ? tagSelection.topic.length > 0 || tagSelection.subtopic.length > 0
                        : selected.length > 0;
                    const mineActive = hasMine && mine!.on && !explicit;
                    // What the menu shows as ticked: the explicit selection, or the
                    // company defaults while "My industries" / "My topics" is active.
                    // Clicking an item then toggles FROM that baseline (untick 72 =
                    // "my industries minus 72"), because the effective set is the
                    // Select's value. For Topic: whole topics plus 'sub:' items.
                    const effective = facet === 'topic'
                        ? (mineActive ? mine!.defaults : [...tagSelection.topic, ...tagSelection.subtopic.map(v => SUB + v)])
                        : (selected.length > 0 ? selected : (mineActive ? mine!.defaults : []));
                    const allValues = options.map(o => o.value);
                    const allSelected = allValues.every(v => effective.includes(v));
                    const setFacet = (values: string[]) => {
                        if (facet !== 'topic') {
                            setTagSelection(prev => ({ ...prev, [facet]: values }));
                            return;
                        }
                        // A topic and its own sub-topics never coexist: the last click wins.
                        let topics = values.filter(v => !v.startsWith(SUB));
                        let subs = values.filter(v => v.startsWith(SUB)).map(v => v.slice(SUB.length));
                        const addedTopics = topics.filter(v => !tagSelection.topic.includes(v));
                        const addedSubs = subs.filter(v => !tagSelection.subtopic.includes(v));
                        subs = subs.filter(v => !addedTopics.includes(parentOf[v]));
                        topics = topics.filter(v => !addedSubs.some(s => parentOf[s] === v));
                        setTagSelection(prev => ({ ...prev, topic: topics, subtopic: subs }));
                    };
                    const expanded = new Set<string>([...expandedTopics, ...tagSelection.subtopic.map(v => parentOf[v])]);
                    const fontSx = { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize };
                    const pinnedSx = { borderBottom: '1px solid', borderColor: 'divider' };
                    return (
                        <FormControl key={facet} fullWidth size="small" sx={{ mt: 1.25, bgcolor: 'background.paper' }}>
                            <InputLabel shrink={mineActive || undefined} sx={fontSx}>{title}</InputLabel>
                            <Select
                                multiple
                                displayEmpty={mineActive}
                                value={effective}
                                label={title}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const values = typeof raw === 'string' ? raw.split(',') : raw;
                                    if (values.includes('__all__')) {
                                        // Pinned item: everything listed, or nothing. Either way
                                        // an explicit choice, so the company default steps aside.
                                        if (mine) mine.set(false);
                                        setFacet(allSelected ? [] : allValues);
                                        return;
                                    }
                                    if (values.includes('__mine__')) {
                                        // The pinned item toggles the default; it is never stored.
                                        if (mine) mine.set(v => !v);
                                        return;
                                    }
                                    if (mine && values.length === 0 && mine.on) {
                                        // Unticking everything means "show all" — the star
                                        // item brings the default back.
                                        mine.set(false);
                                    }
                                    setFacet(values);
                                }}
                                renderValue={(values) => mineActive
                                    ? `${mine!.label} (${(facet === 'industry' ? mine!.defaults : mine!.defaults.map(labelOf)).join(', ')})`
                                    : values.map(labelOf).join(', ')}
                                sx={fontSx}
                                MenuProps={{ PaperProps: { style: { maxHeight: 380 } } }}
                            >
                                <MenuItem value="__all__" dense sx={pinnedSx}>
                                    <ListItemText
                                        primary={allSelected ? 'Clear selection' : 'Select all'}
                                        primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }} />
                                </MenuItem>
                                {hasMine && (
                                    <MenuItem value="__mine__" dense sx={pinnedSx}>
                                        {mine!.on
                                            ? <StarIcon sx={{ fontSize: '1rem', color: 'warning.main', mr: 1 }} />
                                            : <StarBorderIcon sx={{ fontSize: '1rem', color: 'text.disabled', mr: 1 }} />}
                                        <ListItemText
                                            primary={mine!.label}
                                            secondary={mine!.defaults.map(labelOf).join(', ')}
                                            primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}
                                            secondaryTypographyProps={{ fontSize: '0.65rem' }} />
                                    </MenuItem>
                                )}
                                {options.flatMap((o, i) => {
                                    const items: React.ReactNode[] = [];
                                    if (facet === 'topic' && o.group && o.group !== options[i - 1]?.group) {
                                        items.push(
                                            <ListSubheader key={`group-${o.group}`} disableSticky
                                                sx={{ lineHeight: '26px', fontSize: '0.66rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                                                {o.group}
                                            </ListSubheader>);
                                    }
                                    const children = facet === 'topic' ? tree[i].children : [];
                                    const partial = !effective.includes(o.value)
                                        && children.some(c => tagSelection.subtopic.includes(c.value));
                                    const isOpen = expanded.has(o.value);
                                    items.push(
                                        <MenuItem key={o.value} value={o.value} dense>
                                            <Checkbox size="small" checked={effective.includes(o.value)} indeterminate={partial} sx={{ p: 0.25, mr: 0.5 }} />
                                            <ListItemText
                                                primary={`${o.label} (${o.count})`}
                                                primaryTypographyProps={{ fontSize: '0.78rem' }} />
                                            {children.length > 0 && (
                                                <IconButton size="small" edge="end" sx={{ p: 0.25, ml: 0.5 }}
                                                    aria-label={isOpen ? 'hide sub-topics' : 'show sub-topics'}
                                                    onClick={(e) => {
                                                        // Unfold without toggling the topic itself.
                                                        e.stopPropagation();
                                                        setExpandedTopics(prev => prev.includes(o.value)
                                                            ? prev.filter(v => v !== o.value) : [...prev, o.value]);
                                                    }}>
                                                    {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                                </IconButton>
                                            )}
                                        </MenuItem>);
                                    if (isOpen) {
                                        children.forEach(c => items.push(
                                            <MenuItem key={SUB + c.value} value={SUB + c.value} dense sx={{ pl: 4.5 }}>
                                                <Checkbox size="small" checked={tagSelection.subtopic.includes(c.value)} sx={{ p: 0.25, mr: 0.5 }} />
                                                <ListItemText
                                                    primary={`${c.label} (${c.count})`}
                                                    primaryTypographyProps={{ fontSize: '0.74rem' }} />
                                            </MenuItem>));
                                    }
                                    return items;
                                })}
                            </Select>
                        </FormControl>
                    );
                })}
                <CompanyTagFilter tags={companyTags.tags} counts={companyTagFacetCounts}
                    selected={effectiveCompanyTags} onChange={setCompanyTagSelection}
                    onManage={() => setCompanyTagsDialogOpen(true)}
                    keywordNote={keywordProblem(companyTags.unavailableKeywords, companyTags.keywordMatchesUnavailable)}
                    fontSize={isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize} />
                {activeTagCount > 0 && (
                    <Chip size="small" clickable label={`Clear tag filters (${activeTagCount})`}
                        onClick={clearTagFilters}
                        onDelete={clearTagFilters}
                        sx={{ mt: 1, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.62rem' } }} />
                )}
                <Divider sx={{ my: 0.75 }} />
                {/* Off (default): a search reaches across every tender of the
                    selected type, so a searched-for tender never hides behind
                    a filter. On: the box and the dropdowns above also narrow
                    the search results. */}
                <Tooltip title="Off: a search shows every matching tender, whatever the filters say. On: the filters and dropdowns above also apply to search results." arrow placement="top" enterTouchDelay={0}>
                    <FormControlLabel control={<Checkbox checked={activeFilters.applyToSearch} onChange={handleFilterChange} name="applyToSearch" size="small" />} label="Apply filters to search" sx={{ display: 'block', '& .MuiTypography-root': { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize } }} />
                </Tooltip>
            </Box>
            )}
        </PanelPaper>
    );

    const agentCardSx = {
        p: 1.25, borderRadius: 1.5, cursor: 'pointer',
        border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
        transition: 'all 0.15s ease-out',
        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover', boxShadow: '0 2px 8px rgba(25,118,210,0.10)' },
    };

    // Normalize score: API returns 0–1 (e.g. 0.9), convert to 0–100 for display.
    // A null score means this tender has not been scored for the current company yet.
    // Selected tender with the heavy fields merged in once the detail fetch
    // lands; until then (or if the fetch fails) it is just the slim list row.
    const detailedTender: Tender | null = selectedTender
        ? { ...selectedTender, ...(tenderDetailQuery.data && tenderDetailQuery.data.id === selectedTender.id ? tenderDetailQuery.data : {}) }
        : null;
    const detailLoading = !!selectedTender && tenderDetailQuery.isFetching && tenderDetailQuery.data?.id !== selectedTender.id;

    const hasScore = !!selectedTender && selectedTender.matchingScore != null;
    const scorePercent: number | null = hasScore
        ? (selectedTender!.matchingScore! <= 1
            ? Math.round(selectedTender!.matchingScore! * 100)
            : Math.round(selectedTender!.matchingScore!))
        : null;
    const scoreColor = scorePercent == null
        ? 'text.disabled'
        : (scorePercent >= 70 ? 'success.main' : scorePercent >= 40 ? 'warning.main' : 'error.main');
    const scoreLabel = scorePercent == null
        ? 'Not yet scored'
        : (scorePercent >= 70 ? 'Good Fit' : scorePercent >= 40 ? 'Moderate Fit' : 'Low Fit');
    const scoreComputedAt = selectedTender?.matchingScoreComputedAt
        ? formatDate(selectedTender.matchingScoreComputedAt)
        : null;
    const scoreStale = !!selectedTender?.matchingScoreStale;
    // Sub-scores, blockers, reasons and confidence ride on the detail fetch —
    // the slim list rows no longer carry them (they tripled the list payload).
    const subScores = detailedTender?.matchingScoreSubScores ?? null;
    const blockers = detailedTender?.matchingScoreBlockers ?? [];
    const scoreReasons = detailedTender?.matchingScoreReasons ?? null;
    const scoreConfidence = detailedTender?.matchingScoreConfidence ?? null;
    // Shared with the CSV export (one Fit / Why column per dimension).
    const DIMENSION_LABELS = SCORE_DIMENSION_LABELS;
    const DIMENSION_INFO: Record<string, string> = {
        capability: 'How well your services match what the tender needs — the biggest factor (30% of the score).',
        experience: 'How relevant your past projects and references are to this tender (25% of the score).',
        team: 'Whether your people have the required roles, skills and certifications (20% of the score).',
        size: 'Whether your company size fits the scale of the contract (10% of the score).',
        geography: "How well your location matches the tender's region (10% of the score).",
    };

    const rightPanelContent = (
        <PanelPaper sx={{ overflowY: 'auto', minHeight: 0, minWidth: 0, height: '100%', p: 1.5, bgcolor: 'grey.100' }}>
            {selectedTender ? (
                <Box sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>

                    {/* --- TENDER DETAILS + TIMELINE --- */}
                    <Paper elevation={0} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isSmallScreen ? '0.8rem' : '0.85rem' }}>
                                        Tender Details
                                    </Typography>
                                    {/* Status of the project, not the procedure type (that is the
                                        "Open"/"Selective" badge in the tag row below). */}
                                    {isConcluded(selectedTender.latestPubType) && (
                                        <Chip size="small" variant="outlined"
                                            color={selectedTender.latestPubType === 'abandonment' ? 'error' : 'default'}
                                            icon={selectedTender.latestPubType === 'abandonment' ? <BlockOutlinedIcon /> : <GavelOutlinedIcon />}
                                            label={selectedTender.latestPubType === 'abandonment' ? 'Abandoned' : 'Awarded'}
                                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600,
                                                '& .MuiChip-icon': { fontSize: '0.85rem', ml: 0.5 }, '& .MuiChip-label': { px: 0.75 } }} />
                                    )}
                                </Box>
                                {selectedTender.simapUrl && (
                                    <Tooltip title="View on SIMAP" arrow>
                                        <IconButton component="a" href={selectedTender.simapUrl} target="_blank" rel="noopener noreferrer" size="small" sx={{ color: 'primary.main', p: 0.25 }}>
                                            <LaunchIcon sx={{ fontSize: '0.95rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                            {/* Who won, from SIMAP's award notice. */}
                            {(selectedTender.awardVendors?.length ?? 0) > 0 && (
                                <Box sx={{ mb: 0.75, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                                        Awarded to
                                    </Typography>
                                    {selectedTender.awardVendors!.map((vendor, i) => (
                                        <Typography key={i} sx={{ fontSize: isSmallScreen ? '0.72rem' : '0.76rem' }}>{vendorLine(vendor)}</Typography>
                                    ))}
                                    {awardMeta(selectedTender.awardSubmissions, selectedTender.awardDate) !== '' && (
                                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                            {awardMeta(selectedTender.awardSubmissions, selectedTender.awardDate)}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {/* All classification tags, incl. procedure and the
                                construction-codes marker (rows show a subset). */}
                            {(detailedTender?.tags ?? []).length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                                    {detailBadges(detailedTender?.tags).map(b => (
                                        <Box key={`${b.kind}:${b.value}`} component="span" sx={tagBadgeSx(b, 'detail')}>{b.label}</Box>
                                    ))}
                                </Box>
                            )}
                            <CompanyTagDetail hits={tagHits.get(selectedTender.id)} />
                            {/* Documents the pipeline could not read: the summary and
                                the score are missing them. Details in the document dialog. */}
                            {skippedDocuments.length > 0 && (
                                <DetailItem label="Documents">
                                    <Button variant="text" size="small" onClick={() => setDocumentSelectionDialogOpen(true)}
                                        sx={{ textTransform: 'none', fontSize: '0.7rem', p: 0, minWidth: 0, ml: 0.5,
                                              color: 'warning.main', fontWeight: 600 }}>
                                        {skippedNote(tenderDocuments.length, skippedDocuments)}
                                    </Button>
                                </DetailItem>
                            )}
                            {selectedTender.industry && selectedTender.industry !== 'N/A' && <DetailItem label="Industry" value={selectedTender.industry} />}
                            <DetailItem label="Project" value={selectedTender.name} />
                            {selectedTender.projectNumber && <DetailItem label="Simap No." value={`#${selectedTender.projectNumber}`} />}
                            {selectedTender.procurementOffice && selectedTender.procurementOffice !== 'N/A' && <DetailItem label="Office" value={selectedTender.procurementOffice} />}
                            {tenderQna.length > 0 && (
                                <DetailItem label="Q&A">
                                    <Button variant="text" size="small" onClick={() => setQnaDialogOpen(true)}
                                        sx={{ textTransform: 'none', fontSize: '0.7rem', p: 0, minWidth: 0, ml: 0.5,
                                              color: qnaRecentlyUpdated ? 'primary.main' : 'text.secondary',
                                              fontWeight: qnaRecentlyUpdated ? 600 : 400 }}>
                                        {tenderQna.length} question{tenderQna.length === 1 ? '' : 's'}
                                        {qnaLastUpdated ? ` · updated ${formatDate(qnaLastUpdated)}` : ''}
                                    </Button>
                                    {qnaUnanswered > 0 && (
                                        <Chip size="small" variant="outlined" color="warning" label={`${qnaUnanswered} unanswered`}
                                            sx={{ ml: 0.5, height: 16, fontSize: '0.58rem' }} />
                                    )}
                                </DetailItem>
                            )}
                            {tenderDocumentsQAQuery.isLoading ? (
                                <DetailItem label="Q&A Docs">
                                    <Typography component="span" variant="body2" sx={{ fontSize: 'inherit' }}>Loading...</Typography>
                                </DetailItem>
                            ) : (tenderDocumentsQA.length > 0 || userPrimaryGroup === 'quantum') ? (
                                <DetailItem label="Q&A Docs">
                                    {tenderDocumentsQA.length > 0 ? (
                                        <Button variant="text" size="small" onClick={() => setQaDocumentsDialogOpen(true)}
                                            sx={{ textTransform: 'none', fontSize: '0.7rem', p: 0, minWidth: 0, ml: 0.5 }}>
                                            {tenderDocumentsQA.length} available
                                        </Button>
                                    ) : (
                                        <Typography component="span" variant="body2" sx={{ fontSize: 'inherit', color: 'text.disabled' }}>none</Typography>
                                    )}
                                    {userPrimaryGroup === 'quantum' && (
                                        <Tooltip title="Upload Q&A documents" arrow>
                                            <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }}
                                                onClick={() => { setQaFilesForUpload([]); setQaUploadDialogOpen(true); }}>
                                                <CloudUploadIcon sx={{ fontSize: '0.95rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </DetailItem>
                            ) : null}
                        </Box>

                        {/* Timeline - always visible */}
                        {displayedTimelineData && (
                            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 1.5, pb: 1, pt: 1 }}>
                                <TimelineComponent timeline={displayedTimelineData} />
                            </Box>
                        )}

                        {[
                            { title: 'Summary', content: detailedTender!.summary },
                            { title: 'Requirements', content: detailedTender!.requirements },
                            { title: 'Ideal Profile', content: detailedTender!.idealProfile },
                        ].map((section) => (
                            <Accordion key={section.title} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderTop: '1px solid', borderColor: 'divider' }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
                                    sx={{ minHeight: 0, px: 1.5, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 'inherit' }}>{section.title}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: 'inherit', whiteSpace: 'pre-wrap' }}>
                                        {detailLoading && (!section.content || section.content === 'Not specified' || section.content === 'N/A')
                                            ? 'Loading…'
                                            : (section.content || 'Not specified')}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {/* EK/ZK criteria extracted from the tender documents (display-only, phase 1) */}
                        {((detailedTender!.eligibilityCriteria?.length ?? 0) > 0 || (detailedTender!.awardCriteria?.length ?? 0) > 0
                            || (detailedTender!.keyPersonnelRequirements?.length ?? 0) > 0 || (detailedTender!.referenceRequirements?.length ?? 0) > 0) && (
                            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderTop: '1px solid', borderColor: 'divider' }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
                                    sx={{ minHeight: 0, px: 1.5, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 'inherit' }}>
                                        Criteria (EK/ZK)
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
                                    {(detailedTender!.eligibilityCriteria?.length ?? 0) > 0 && (
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit', mb: 0.5 }}>
                                                Eligibility (Eignungskriterien)
                                            </Typography>
                                            <CriteriaEntries items={detailedTender!.eligibilityCriteria!} type="EK" />
                                        </Box>
                                    )}
                                    {/* Inspection views for the structured scoring facts — may be removed later */}
                                    {(detailedTender!.keyPersonnelRequirements?.length ?? 0) > 0 && (
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit', mb: 0.5 }}>
                                                Key Personnel Required
                                            </Typography>
                                            <List dense sx={{ p: 0 }}>
                                                {detailedTender!.keyPersonnelRequirements!.map((kp, i) => (
                                                    <ListItem key={i} sx={{ px: 0, py: 0.25, alignItems: 'flex-start' }}>
                                                        <ListItemText
                                                            primary={`${kp.count != null ? `${kp.count}× ` : ''}${kp.role}${kp.min_experience_years != null ? ` — min. ${kp.min_experience_years} yrs experience` : ''}`}
                                                            secondary={[
                                                                (kp.certifications?.length ?? 0) > 0 ? `Certifications: ${kp.certifications!.join(', ')}` : '',
                                                                (kp.languages?.length ?? 0) > 0 ? `Languages: ${kp.languages!.join(', ')}` : '',
                                                            ].filter(Boolean).join(' · ') || undefined}
                                                            primaryTypographyProps={{ sx: { fontSize: 'inherit' } }}
                                                            secondaryTypographyProps={{ sx: { fontSize: '0.68rem' } }} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                    {(detailedTender!.referenceRequirements?.length ?? 0) > 0 && (
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit', mb: 0.5 }}>
                                                References Required
                                            </Typography>
                                            <List dense sx={{ p: 0 }}>
                                                {detailedTender!.referenceRequirements!.map((rr, i) => (
                                                    <ListItem key={i} sx={{ px: 0, py: 0.25, alignItems: 'flex-start' }}>
                                                        <ListItemText
                                                            primary={`${rr.count != null ? rr.count : '?'} reference${rr.count === 1 ? '' : 's'}${rr.applies_to === 'key_personnel' ? ' (per key person)' : ''}${rr.recency_years != null ? ` — from the last ${rr.recency_years} years` : ''}`}
                                                            secondary={rr.similarity || undefined}
                                                            primaryTypographyProps={{ sx: { fontSize: 'inherit' } }}
                                                            secondaryTypographyProps={{ sx: { fontSize: '0.68rem' } }} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                    {(detailedTender!.awardCriteria?.length ?? 0) > 0 && (
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit', mb: 0.5 }}>
                                                Award (Zuschlagskriterien)
                                            </Typography>
                                            <CriteriaEntries items={detailedTender!.awardCriteria!} type="ZK" />
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Paper>

                    {/* --- COMPANY MATCHING SCORE --- */}
                    <Paper elevation={0} sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isSmallScreen ? '0.8rem' : '0.85rem', mb: 1 }}>
                            Company Match
                        </Typography>

                        {/* Compact summary: dial + label + a one-line meta + Details toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                                <CircularProgress variant="determinate" value={100} size={44} thickness={4}
                                    sx={{ color: 'grey.200', position: 'absolute' }} />
                                <CircularProgress variant="determinate" value={scorePercent ?? 0} size={44} thickness={4}
                                    sx={{ color: scoreColor }} />
                                <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>
                                        {scorePercent == null ? '—' : `${scorePercent}%`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit', lineHeight: 1.3, color: scoreColor }}>
                                    {scoreLabel}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem', display: 'block', lineHeight: 1.3 }}>
                                    {scoreConfidence ? `Confidence: ${scoreConfidence}` : 'Company matching score'}
                                    {scoreComputedAt ? ` · ${scoreComputedAt}` : ''}{scoreStale ? ' · may be outdated' : ''}
                                </Typography>
                            </Box>
                            {subScores && (
                                <Button size="small" variant="text" onClick={() => setShowScoreDetails(v => !v)}
                                    sx={{ flexShrink: 0, alignSelf: 'flex-start', fontSize: '0.6rem', textTransform: 'none', minWidth: 0, px: 0.5, color: 'primary.main' }}>
                                    {showScoreDetails ? 'Hide ▴' : 'Details ▾'}
                                </Button>
                            )}
                        </Box>

                        {/* Details: sub-score bars + blockers + recompute + disclaimer */}
                        <Collapse in={showScoreDetails} unmountOnExit>
                            <Box sx={{ mt: 1.25 }}>
                                {subScores && Object.keys(DIMENSION_LABELS).filter(k => k in subScores).map(k => {
                                    const v = Math.round(subScores[k]);
                                    const reason = scoreReasons?.[k];
                                    const expanded = expandedScoreDim === k;
                                    return (
                                        <Box key={k} sx={{ mb: 0.6 }}>
                                            <Box onClick={() => reason && setExpandedScoreDim(expanded ? null : k)}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 1,
                                                      cursor: reason ? 'pointer' : 'default', borderRadius: 0.5,
                                                      '&:hover': reason ? { bgcolor: 'action.hover' } : {} }}>
                                                <Box sx={{ width: 84, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                    <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>{DIMENSION_LABELS[k]}</Typography>
                                                    <Tooltip title={DIMENSION_INFO[k]} arrow placement="top" enterTouchDelay={0}>
                                                        <InfoOutlinedIcon sx={{ fontSize: '0.72rem', color: 'text.disabled', cursor: 'help' }} />
                                                    </Tooltip>
                                                </Box>
                                                <LinearProgress variant="determinate" value={v}
                                                    sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: fitColor(v) } }} />
                                                <Typography sx={{ width: 22, flexShrink: 0, textAlign: 'right', fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary' }}>{v}</Typography>
                                                <ExpandMoreIcon sx={{ fontSize: '0.8rem', flexShrink: 0,
                                                    color: reason ? 'text.disabled' : 'transparent',
                                                    transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                                            </Box>
                                            {reason && (
                                                <Collapse in={expanded} unmountOnExit>
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.25, ml: 1, px: 1, py: 0.5,
                                                        borderLeft: '2px solid', borderColor: fitColor(v), bgcolor: 'grey.50',
                                                        fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.4 }}>
                                                        {reason}
                                                    </Typography>
                                                </Collapse>
                                            )}
                                        </Box>
                                    );
                                })}

                                {blockers.filter(b => b.status !== 'met').length > 0 && (
                                    <Box sx={{ mt: 0.75 }}>
                                        {blockers.filter(b => b.status !== 'met').map((b, i) => (
                                            <Box key={i}>
                                                <Typography variant="caption"
                                                    onClick={() => b.note && setExpandedBlocker(expandedBlocker === i ? null : i)}
                                                    sx={{ display: 'block', fontSize: '0.6rem',
                                                          color: b.status === 'unmet' ? 'error.main' : 'warning.main',
                                                          cursor: b.note ? 'pointer' : 'default', borderRadius: 0.5,
                                                          '&:hover': b.note ? { bgcolor: 'action.hover' } : {} }}>
                                                    {b.status === 'unmet' ? '⛔' : '⚠'} {b.requirement}{b.note ? (expandedBlocker === i ? ' ▴' : ' ▾') : ''}
                                                </Typography>
                                                {b.note && (
                                                    <Collapse in={expandedBlocker === i} unmountOnExit>
                                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, mb: 0.5, ml: 1, px: 1, py: 0.5,
                                                            borderLeft: '2px solid',
                                                            borderColor: b.status === 'unmet' ? 'error.main' : 'warning.main',
                                                            bgcolor: 'grey.50', fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.4 }}>
                                                            {b.note}
                                                        </Typography>
                                                    </Collapse>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {scoreStale && (
                                    <Button size="small" variant="outlined" disabled={recomputing}
                                        onClick={async () => {
                                            try {
                                                setRecomputing(true);
                                                await apiService.recomputeMatches(auth, userPrimaryGroup);
                                                setRecomputeMsg('Recompute started — check back shortly.');
                                            } catch {
                                                setRecomputeMsg('Could not start recompute.');
                                            } finally {
                                                setRecomputing(false);
                                            }
                                        }}
                                        sx={{ mt: 1, fontSize: '0.6rem', textTransform: 'none', py: 0.25 }}>
                                        {recomputing ? 'Starting…' : 'Recompute matches'}
                                    </Button>
                                )}
                                {recomputeMsg && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.58rem', color: 'text.secondary' }}>
                                        {recomputeMsg}
                                    </Typography>
                                )}

                                <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: '0.56rem', color: 'text.disabled', fontStyle: 'italic', lineHeight: 1.3 }}>
                                    AI-generated estimate — it can make mistakes. Use it as guidance, not a final decision.
                                </Typography>
                            </Box>
                        </Collapse>
                    </Paper>

                    {/* --- AI AGENTS (Draft + Review) --- */}
                    <Paper elevation={0} sx={{
                        p: 1.5, mb: 1.5, borderRadius: 2,
                        border: '1px solid', borderColor: 'primary.light',
                        background: 'linear-gradient(145deg, rgba(25,118,210,0.03) 0%, rgba(25,118,210,0.07) 100%)',
                    }}>
                        <Typography variant="subtitle2" sx={{
                            fontWeight: 700, fontSize: isSmallScreen ? '0.8rem' : '0.85rem',
                            display: 'flex', alignItems: 'center', mb: 1.25, color: 'primary.dark',
                        }}>
                            <AutoAwesomeIcon sx={{ mr: 0.75, fontSize: '1rem' }} /> AI Agents
                        </Typography>

                        <Stack spacing={0.75}>
                            {[
                                { label: 'Draft Proposal', desc: 'Generate an AI-powered proposal', onClick: () => navigate(`/proposals/new/${selectedTender.id}`), onInfo: () => setDraftFlowDialogOpen(true) },
                                { label: 'Review Proposal', desc: 'Get AI feedback on your proposal', onClick: () => navigate(`/reviews/new/${selectedTender.id}`), onInfo: () => setReviewFlowDialogOpen(true) },
                            ].map((agent) => (
                                <Box key={agent.label} onClick={agent.onClick} sx={agentCardSx}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <SmartToyIcon sx={{ color: 'primary.main', fontSize: '1rem', flexShrink: 0 }} />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: 'inherit' }}>{agent.label}</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.2 }}>{agent.desc}</Typography>
                                        </Box>
                                        <Tooltip title="View agent flow" arrow>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); agent.onInfo(); }} sx={{ p: 0.25 }}>
                                                <AccountTreeIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                                            </IconButton>
                                        </Tooltip>
                                        <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: '1rem', flexShrink: 0 }} />
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>

                    {/* --- COMPANY + ONBOARD --- */}
                    <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isSmallScreen ? '0.8rem' : '0.85rem', mb: 1 }}>
                            Company
                        </Typography>

                        {/* Onboard Company agent card */}
                        <Box onClick={() => navigate('/onboarding')} sx={{
                            ...agentCardSx, mb: 1,
                            borderColor: 'primary.light',
                            background: 'linear-gradient(145deg, rgba(25,118,210,0.02) 0%, rgba(25,118,210,0.05) 100%)',
                        }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <SmartToyIcon sx={{ color: 'primary.main', fontSize: '1rem', flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: 'inherit' }}>Onboard Company</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', lineHeight: 1.2 }}>Set up your company profile with AI</Typography>
                                </Box>
                                <Tooltip title="View onboarding flow" arrow>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOnboardingFlowDialogOpen(true); }} sx={{ p: 0.25 }}>
                                        <AccountTreeIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                                    </IconButton>
                                </Tooltip>
                                <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: '1rem', flexShrink: 0 }} />
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={0.75}>
                            {[
                                { icon: <GroupIcon sx={{ fontSize: '0.95rem' }} />, label: 'Workforce', count: workforceQuery.isLoading ? '...' : workforce.length, onClick: handleOpenManageWorkforceModal },
                                { icon: <WorkIcon sx={{ fontSize: '0.95rem' }} />, label: 'Projects', count: companyProfileQuery.isLoading ? '...' : companyProfile.pastProjects.length, onClick: handleOpenManageCompanyModal },
                                { icon: <FolderIcon sx={{ fontSize: '0.95rem' }} />, label: 'Docs', count: companyDocumentsQuery.isLoading ? '...' : companyDocuments.length, onClick: () => setManageCompanyDocsModalOpen(true) },
                            ].map((item) => (
                                <Button key={item.label} variant="outlined" size="small" onClick={item.onClick}
                                    sx={{
                                        flex: 1, textTransform: 'none', fontSize: isSmallScreen ? '0.65rem' : '0.72rem',
                                        p: '4px 6px', justifyContent: 'space-between', borderRadius: 1.5,
                                    }}>
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                        {item.icon}
                                        <Box component="span" sx={{ ml: 0.5 }}>{item.label}</Box>
                                    </Box>
                                    <Chip label={item.count} size="small" sx={{ height: 18, minWidth: 22, '& .MuiChip-label': { px: 0.5, fontSize: '0.62rem' } }} />
                                </Button>
                            ))}
                        </Stack>
                    </Paper>

                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">Select a tender to view details.</Typography>
                </Box>
            )}
        </PanelPaper>
    );

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', p: { xs: 1, sm: 1.5 }, boxSizing: 'border-box', bgcolor: 'background.default', position: 'relative' }}>
            {isSmallScreen && (
                <>
                    <IconButton
                        onClick={handleLeftDrawerToggle}
                        sx={{ position: 'fixed', top: `calc(${(theme.mixins.toolbar as { minHeight?: number }).minHeight ?? 56}px + ${theme.spacing(1)})`, left: theme.spacing(1.5), zIndex: theme.zIndex.drawer + 1, bgcolor: 'rgba(128,128,128,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(128,128,128,0.7)' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <IconButton
                        onClick={handleRightDrawerToggle}
                        sx={{ position: 'fixed', top: `calc(${(theme.mixins.toolbar as { minHeight?: number }).minHeight ?? 56}px + ${theme.spacing(1)})`, right: theme.spacing(1.5), zIndex: theme.zIndex.drawer + 1, bgcolor: 'rgba(128,128,128,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(128,128,128,0.7)' } }}
                    >
                        <TuneIcon />
                    </IconButton>
                </>
            )}

            <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ height: '100%', minWidth: 0 }}>
                {/* Left Panel */}
                {!isSmallScreen ? (
                    <Grid item md={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
                        {leftPanelContent}
                    </Grid>
                ) : (
                    <Drawer variant="temporary" open={leftPanelOpen} onClose={handleLeftDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, height: '100%', pt: `${(theme.mixins.toolbar as { minHeight?: number }).minHeight ?? 56}px` } }}>
                        {leftPanelContent}
                    </Drawer>
                )}

                {/* Central Chat Area */}
                <Grid item xs={12} md={isSmallScreen ? 12 : 5.5} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
                    <PanelPaper sx={{ justifyContent: 'space-between', minHeight: 0 }}>
                        {isSmallScreen && <Box sx={{ height: theme.spacing(10), flexShrink: 0 }} />}

                        {/* Scrollable Messages Area */}
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, minWidth: 0, mb: 1.5, p: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column' }}>
                            {/* Empty States */}
                            {!selectedTender && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Select a tender to start chatting
                                    </Typography>
                                </Box>
                            )}
                            {selectedTender && chatMessages.length === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, mb: 1, textAlign: 'center' }}>
                                        Ask about {selectedTender.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.disabled">
                                        Your AI assistant is ready to help.
                                    </Typography>
                                </Box>
                            )}

                            {/* Message Bubbles */}
                            <Stack spacing={3} sx={{ width: '100%' }}>
                                {chatMessages.map((msg, index) => (
                                    <React.Fragment key={index}>
                                        {/* User Message - right-aligned pill */}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Typography sx={{
                                                p: '10px 18px',
                                                borderRadius: '20px',
                                                bgcolor: 'action.hover',
                                                color: 'text.primary',
                                                maxWidth: '80%',
                                                wordBreak: 'break-word',
                                                fontSize: '0.875rem',
                                            }}>
                                                {msg.user}
                                            </Typography>
                                        </Box>
                                        {/* Bot Message - full width, no background */}
                                        <Box sx={{ width: '100%' }}>
                                            {msg.bot.text === '...' ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
                                                    Generating response...
                                                </Typography>
                                            ) : (
                                                <>
                                                    <BotMessageRenderer
                                                        botResponse={msg.bot}
                                                        onFileClick={handleFileClick}
                                                        isLoading={getFileUrlMutation.isPending}
                                                    />
                                                    <AnswerModelChip tier={msg.bot.modelTier} costUsd={msg.bot.costUsd} model={msg.bot.model} />
                                                </>
                                            )}
                                        </Box>
                                    </React.Fragment>
                                ))}
                            </Stack>
                            <div ref={messagesEndRef} />
                        </Box>

                        {/* Error Alert */}
                        {chatError && (
                            <Alert severity="error" onClose={() => setChatError(null)} sx={{ mb: 1.5, fontSize: '0.8rem', borderRadius: 2 }}>
                                {chatError}
                            </Alert>
                        )}

                        {/* Document filter chips (shown when not all docs selected) */}
                        {selectedTender && tenderDocuments.length > 0 && selectedDocuments.size < tenderDocuments.length && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, px: 1 }}>
                                <Chip
                                    label={`${selectedDocuments.size} of ${tenderDocuments.length} docs selected`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setDocumentSelectionDialogOpen(true)}
                                    sx={{ cursor: 'pointer' }}
                                />
                            </Box>
                        )}

                        <ThoroughWarning tier={chatModelTier} onChange={handleChatModelChange} />

                        {/* Modern Input Bar */}
                        <Paper
                            elevation={0}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: '6px 12px',
                                borderRadius: '24px',
                                bgcolor: 'action.hover',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Tooltip title="Filter Documents">
                                <span>
                                    <IconButton
                                        onClick={() => setDocumentSelectionDialogOpen(true)}
                                        disabled={!selectedTender}
                                        size="small"
                                        sx={{
                                            color: selectedTender && selectedDocuments.size < tenderDocuments.length
                                                ? 'primary.main'
                                                : 'text.secondary',
                                        }}
                                    >
                                        <FilterAltOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="View chat flow">
                                <IconButton
                                    onClick={() => setChatFlowDialogOpen(true)}
                                    size="small"
                                    sx={{ color: 'text.secondary' }}
                                >
                                    <AccountTreeIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <ChatModelPicker value={chatModelTier} onChange={handleChatModelChange} />
                            <ChatInputBox
                                key={selectedTender?.id ?? 'none'}
                                placeholder={selectedTender ? `Ask about ${selectedTender.name}...` : 'Select a tender first'}
                                disabled={!selectedTender}
                                sending={sendChatMessageMutation.isPending}
                                onSend={handleSendChatMessage}
                            />
                        </Paper>
                    </PanelPaper>
                </Grid>

                {/* Right Panel */}
                {!isSmallScreen ? (
                    <Grid item md={3.5} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
                        {rightPanelContent}
                    </Grid>
                ) : (
                    <Drawer variant="temporary" anchor="right" open={rightPanelOpen} onClose={handleRightDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, height: '100%', pt: `${(theme.mixins.toolbar as { minHeight?: number }).minHeight ?? 56}px` } }}>
                        {rightPanelContent}
                    </Drawer>
                )}
            </Grid>

            {/* Manage Workforce Modal to display richer data */}
            <Dialog open={manageWorkforceModalOpen} onClose={handleCloseManageWorkforceModal} maxWidth="md" fullWidth fullScreen={isSmallScreen}>
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.2rem' }}>Manage Workforce ({workforce.length})</DialogTitle>
                <DialogContent dividers sx={{ p: 0, minHeight: '200px', position: 'relative' }}>
                    {workforceQuery.isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', position: 'absolute', top: 0, left: 0, width: '100%', bgcolor: 'rgba(255,255,255,0.8)', zIndex: 10 }}>
                            <Typography>Loading workforce...</Typography>
                        </Box>
                    )}
                    {workforceQuery.error && (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Alert severity="error">Error: {workforceQuery.error.message}</Alert>
                        </Box>
                    )}
                    {!workforceQuery.isLoading && !workforceQuery.error && workforce.length === 0 && <Typography sx={{ p: 2, textAlign: 'center' }}>No workforce members found.</Typography>}
                    <List disablePadding>
                        {workforce.map((member) => (
                            <Accordion key={member.id} elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 'medium', fontSize: isSmallScreen ? '0.9rem' : '1rem' }}>{member.name}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>{member.role}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Tooltip title="Edit Member"><IconButton size="small" sx={{ mr: 0.5 }} onClick={(e) => { e.stopPropagation(); handleOpenAddEditModal(member); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Remove Member"><IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRequestRemoveMember(member.id); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 1, px: 2, bgcolor: 'grey.100' }}>
                                    <Stack spacing={1.5}>
                                        {member.tenderReadyBio && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Bio</Typography>
                                                <Typography variant="body2" color="text.secondary">{member.tenderReadyBio}</Typography>
                                            </Box>
                                        )}
                                        {member.industryExpertise?.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Industry Expertise</Typography>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    {member.industryExpertise.map((industry, i) => <Chip key={i} label={industry} size="small" />)}
                                                </Stack>
                                            </Box>
                                        )}
                                        {member.skills?.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Skills</Typography>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    {member.skills.map((skill, i) => <Chip key={i} label={`${skill.name} (${skill.rating})`} size="small" />)}
                                                </Stack>
                                            </Box>
                                        )}
                                        {member.languages?.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Languages</Typography>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    {member.languages.map((lang, i) => <Chip key={i} label={`${lang.name} (${lang.rating})`} size="small" />)}
                                                </Stack>
                                            </Box>
                                        )}
                                        {member.certifications?.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Certifications</Typography>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    {member.certifications.map((cert, i) => <Chip key={i} label={cert} size="small" />)}
                                                </Stack>
                                            </Box>
                                        )}
                                        {member.education?.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Education</Typography>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    {member.education.map((edu, i) => <Chip key={i} label={edu} size="small" />)}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </List>
                    <Box sx={{ p: 2, textAlign: 'center', borderTop: (workforce.length > 0 || workforceQuery.error) ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Button variant="outlined" onClick={() => handleOpenAddEditModal(null)} sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>+ Add New Member</Button>
                    </Box>
                </DialogContent>
                <DialogActions><Button onClick={handleCloseManageWorkforceModal} sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Close</Button></DialogActions>
            </Dialog>

            <AddEditWorkforceMemberDialog
                open={addEditMemberModalOpen}
                onClose={handleCloseAddEditModal}
                member={editingMember}
                onSave={handleSaveMember}
                isSmallScreen={isSmallScreen}
                isSaving={saveWorkforceMemberMutation.isPending}
                saveError={saveWorkforceMemberMutation.isError ? saveWorkforceMemberMutation.error.message : null}
                userApiKey={auth.user?.profile?.['custom:api_key'] as string}
                idToken={auth.user?.id_token}
            />

            <Dialog open={confirmDeleteDialogOpen} onClose={handleCloseConfirmDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.1rem' }}>Confirm Deletion</DialogTitle>
                <DialogContent>
                    {deleteWorkforceMemberMutation.error && (
                        <Alert severity="error" sx={{ mb: 2, fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>
                            {deleteWorkforceMemberMutation.error.message}
                        </Alert>
                    )}
                    <Typography sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Are you sure you want to remove this workforce member?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDeleteDialog} sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Cancel</Button>
                    <Button
                        onClick={handleConfirmRemoveMember}
                        color="error"
                        variant="contained"
                        disabled={deleteWorkforceMemberMutation.isPending}
                        sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>
                        {deleteWorkforceMemberMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ManageCompanyDialog
                open={manageCompanyModalOpen}
                onClose={handleCloseManageCompanyModal}
                companyProfile={companyProfile}
                onOpenAddEditProjectModal={handleOpenAddEditProjectModal}
                onRemoveProject={handleRequestRemoveProject}
                isSmallScreen={isSmallScreen}
                isLoading={companyProfileQuery.isLoading}
                fetchProfileError={companyProfileQuery.isError ? companyProfileQuery.error.message : null}
                onSaveSection={(sectionData) => {
                    saveCompanySectionMutation.mutate({ sectionData, auth });
                }}
                onRemoveSection={handleRequestRemoveSection}
                onAddSection={(sectionName) => {
                    createCompanySectionMutation.mutate({ sectionName, auth });
                }}
                relevantIndustries={companyProfile.relevantIndustries}
                onSaveRelevantIndustries={(industries) => {
                    saveRelevantIndustriesMutation.mutate({ industries, auth });
                }}
                savingIndustries={saveRelevantIndustriesMutation.isPending}
                relevantTopics={companyProfile.relevantTopics}
                topicCatalog={topicsQuery.data}
                onSaveRelevantTopics={(topics) => {
                    saveRelevantTopicsMutation.mutate({ topics, auth });
                }}
                savingTopics={saveRelevantTopicsMutation.isPending}
                onManageTags={() => setCompanyTagsDialogOpen(true)}
            />
            <CompanyTagsDialog
                open={companyTagsDialogOpen}
                onClose={() => setCompanyTagsDialogOpen(false)}
                tags={companyTags.tags}
                matchCounts={companyTagListCounts}
                tenderInfo={(id) => tenderInfoById.get(id)}
                onPreview={(draft) => apiService.previewCompanyTag(auth, draft)}
                onSave={async (draft, tagId) => {
                    await apiService.saveCompanyTag(auth, draft, tagId);
                    await queryClient.invalidateQueries({ queryKey: ['companyTags', userPrimaryGroup] });
                }}
                onDelete={async (tagId) => {
                    await apiService.deleteCompanyTag(auth, tagId);
                    await queryClient.invalidateQueries({ queryKey: ['companyTags', userPrimaryGroup] });
                }}
                isSmallScreen={isSmallScreen}
            />

            <AddEditProjectDialog
                open={addEditProjectModalOpen}
                onClose={handleCloseAddEditProjectModal}
                project={editingProject}
                onSave={handleSaveProject}
                isSmallScreen={isSmallScreen}
                isSaving={savePastProjectMutation.isPending}
                saveError={savePastProjectMutation.isError ? savePastProjectMutation.error.message : null}
                existingProjects={companyProfile.pastProjects}
            />

            <ManageCompanyDocumentsDialog
                open={manageCompanyDocsModalOpen}
                onClose={() => setManageCompanyDocsModalOpen(false)}
                isSmallScreen={isSmallScreen}
                auth={auth}
                companyDocumentsQuery={companyDocumentsQuery}
            />

            <Dialog open={!!qaDocToDelete} onClose={() => !qaDocDeleting && setQaDocToDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: '1rem' }}>Delete this Q&A document?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all', mb: 1 }}>
                        {qaDocToDelete?.DocumentPath.split('/').pop()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        It will be removed for everyone, and the bot will stop using it in its answers. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQaDocToDelete(null)} disabled={qaDocDeleting}>Cancel</Button>
                    <Button variant="contained" color="error" disabled={qaDocDeleting}
                        onClick={async () => {
                            if (!qaDocToDelete || !selectedTender) return;
                            setQaDocDeleting(true);
                            try {
                                await apiService.deleteTenderDocumentQA(auth, selectedTender.projectId, qaDocToDelete.DocumentPath, userPrimaryGroup);
                                queryClient.invalidateQueries({ queryKey: ['tenderDocumentsQA', selectedTender.projectId] });
                                setQaDocToDelete(null);
                            } catch (e) {
                                console.error('Failed to delete QA document', e);
                            } finally {
                                setQaDocDeleting(false);
                            }
                        }}>
                        {qaDocDeleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={qnaDialogOpen} onClose={() => setQnaDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isSmallScreen}>
                <DialogTitle sx={{ pb: 0.5 }}>
                    Q&A — {selectedTender?.name}
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {tenderQna.length} question{tenderQna.length === 1 ? '' : 's'}
                        {qnaLastUpdated ? ` · last updated ${formatDate(qnaLastUpdated)}` : ''}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {qnaRounds.map(([roundNumber, items]) => {
                        const round = items[0];
                        const roundOpen = round.roundState === 'open';
                        return (
                            <Box key={roundNumber} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                                        Round {roundNumber}
                                    </Typography>
                                    <Chip size="small" variant="outlined" label={round.roundState}
                                        color={roundOpen ? 'success' : 'default'}
                                        sx={{ height: 16, fontSize: '0.58rem', textTransform: 'capitalize' }} />
                                    {round.roundEndDate && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem' }}>
                                            {roundOpen ? 'ends' : 'ended'} {formatDate(round.roundEndDate)}
                                        </Typography>
                                    )}
                                </Box>
                                {items.map((q) => {
                                    const unanswered = qnaIsUnanswered(q);
                                    const changed = q.answerState === 'changed';
                                    return (
                                        <Box key={q.questionNumber} sx={{ mb: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                                Q{q.questionNumber} · {q.question}
                                            </Typography>
                                            {unanswered ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                                                    <Chip size="small" variant="outlined" color="warning" label="not yet answered"
                                                        sx={{ height: 16, fontSize: '0.58rem' }} />
                                                    {q.askedAt && (
                                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
                                                            asked {formatDate(q.askedAt)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: changed ? 'primary.main' : 'divider' }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.72rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                                                        {q.answer}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                                        {changed && (
                                                            <Chip size="small" variant="outlined" color="primary" label="answer updated"
                                                                sx={{ height: 16, fontSize: '0.58rem' }} />
                                                        )}
                                                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
                                                            {changed && q.updatedAt ? `updated ${formatDate(q.updatedAt)}`
                                                                : q.answeredAt ? `answered ${formatDate(q.answeredAt)}`
                                                                : q.updatedAt ? `updated ${formatDate(q.updatedAt)}` : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })}
                    {tenderQna.length === 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            No Q&A published for this tender.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQnaDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* CSV export of the list as shown: the exact matches, then the
                similar tenders while searching. */}
            <TenderExportDialog open={exportOpen} onClose={() => setExportOpen(false)}
                tenders={[...filteredTenders, ...similarResults.map(s => s.tender)]}
                loadDetails={(id) => apiService.fetchTenderDetails(auth, id, userPrimaryGroup)}
                loadMarket={(days) => apiService.getDailyTenderStats(auth, days)} />
            <Dialog open={!!decisionDialog} onClose={() => setDecisionDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: '1rem' }}>
                    {decisionDialog?.decision === 'dismiss' ? 'Dismiss this tender?' : 'Pursue this tender?'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                        {decisionDialog?.decision === 'dismiss'
                            ? `Why are you dismissing "${decisionDialog?.tender.name}"? You can add an optional comment to record your reason.`
                            : `"${decisionDialog?.tender.name}" scored low for your company. What did the score miss? (optional — helps improve the matching)`}
                    </Typography>
                    <TextField autoFocus fullWidth multiline minRows={2} size="small"
                        label="Comment (optional)" inputProps={{ maxLength: 2000 }}
                        placeholder="e.g. we lack the required certification / we did exactly this kind of project before"
                        value={decisionReasonText} onChange={(e) => setDecisionReasonText(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDecisionDialog(null)}>Cancel</Button>
                    <Button variant="contained" onClick={() => {
                        if (decisionDialog) applyDecision(decisionDialog.tender, decisionDialog.decision, decisionReasonText.trim() || undefined);
                        setDecisionDialog(null);
                    }}>
                        {decisionDialog?.decision === 'dismiss' ? 'Dismiss tender' : 'Pursue anyway'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteProjectDialogOpen} onClose={handleCloseConfirmDeleteProjectDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.1rem' }}>Confirm Project Deletion</DialogTitle>
                <DialogContent>
                    {deletePastProjectMutation.error && (
                        <Alert severity="error" sx={{ mb: 2, fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>
                            {deletePastProjectMutation.error.message}
                        </Alert>
                    )}
                    <Typography sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Are you sure you want to remove this project?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDeleteProjectDialog} sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Cancel</Button>
                    <Button
                        onClick={handleConfirmRemoveProject}
                        color="error" variant="contained"
                        disabled={deletePastProjectMutation.isPending}
                        sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>
                        {deletePastProjectMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteSectionDialogOpen} onClose={handleCloseConfirmDeleteSectionDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.1rem' }}>Confirm Section Deletion</DialogTitle>
                <DialogContent>
                    {deleteCompanySectionMutation.error && (
                        <Alert severity="error" sx={{ mb: 2, fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>
                            {deleteCompanySectionMutation.error.message}
                        </Alert>
                    )}
                    <Typography sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>
                        Are you sure you want to remove this company information section?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDeleteSectionDialog} sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}>Cancel</Button>
                    <Button
                        onClick={handleConfirmRemoveSection}
                        color="error"
                        variant="contained"
                        disabled={deleteCompanySectionMutation.isPending}
                        sx={{ fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize }}
                    >
                        {deleteCompanySectionMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <DocumentSelectionDialog
                open={documentSelectionDialogOpen}
                onClose={() => setDocumentSelectionDialogOpen(false)}
                documents={tenderDocuments}
                skippedDocuments={skippedDocuments}
                selectedDocs={selectedDocuments}
                onSelectedDocsChange={setSelectedDocuments}
                companyDocuments={companyDocuments}
                selectedCompanyDocs={selectedCompanyDocuments}
                onSelectedCompanyDocsChange={setSelectedCompanyDocuments}
                qaDocuments={tenderDocumentsQA}
                isSmallScreen={isSmallScreen}
                onOpenDocument={handleOpenDocumentFromDialog}
                isOpeningDocument={getFileUrlMutation.isPending}
                openingDocumentPath={getFileUrlMutation.variables?.documentPath}
            />


            <Dialog open={openArchitectureDialog} onClose={handleCloseArchitectureDialog} maxWidth="lg" fullWidth PaperProps={{ style: { height: '90vh' } }}>
                <DialogTitle>Application Architecture</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto', p: 1, bgcolor: 'grey.100' }}>
                    <img src="/aws_tenderq_architecture.png" alt="Application Architecture" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseArchitectureDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Draft Proposal Flow Dialog */}
            <Dialog open={draftFlowDialogOpen} onClose={() => setDraftFlowDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ style: { height: '90vh' } }}>
                <DialogTitle>Draft Proposal Flow</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto', p: 2, bgcolor: 'grey.100' }}>
                    <MermaidDiagram chart={DRAFT_PROPOSAL_FLOW} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDraftFlowDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Review Proposal Flow Dialog */}
            <Dialog open={reviewFlowDialogOpen} onClose={() => setReviewFlowDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ style: { height: '90vh' } }}>
                <DialogTitle>Review Proposal Flow</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto', p: 2, bgcolor: 'grey.100' }}>
                    <MermaidDiagram chart={REVIEW_PROPOSAL_FLOW} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReviewFlowDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Onboarding Flow Dialog */}
            <Dialog open={onboardingFlowDialogOpen} onClose={() => setOnboardingFlowDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ style: { height: '90vh' } }}>
                <DialogTitle>Company Onboarding Flow</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto', p: 2, bgcolor: 'grey.100' }}>
                    <MermaidDiagram chart={COMPANY_ONBOARDING_FLOW} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOnboardingFlowDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Chat Flow Dialog */}
            <Dialog open={chatFlowDialogOpen} onClose={() => setChatFlowDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ style: { height: '90vh' } }}>
                <DialogTitle>Chat Flow</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto', p: 2, bgcolor: 'grey.100' }}>
                    <MermaidDiagram chart={CHAT_FLOW} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setChatFlowDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* QA Documents Dialog */}
            <Dialog 
                open={qaDocumentsDialogOpen} 
                onClose={() => setQaDocumentsDialogOpen(false)} 
                maxWidth="md" 
                fullWidth 
                fullScreen={isSmallScreen}
            >
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.2rem' }}>
                    QA Documents for {selectedTender?.name}
                </DialogTitle>
                <DialogContent dividers>
                    {tenderDocumentsQAQuery.isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <Typography>Loading QA documents...</Typography>
                        </Box>
                    )}
                    {tenderDocumentsQAQuery.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {tenderDocumentsQAQuery.error.message}
                        </Alert>
                    )}
                    {!tenderDocumentsQAQuery.isLoading && !tenderDocumentsQAQuery.error && tenderDocumentsQA.length === 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <Typography color="text.secondary">No QA documents found for this tender.</Typography>
                        </Box>
                    )}
                    {!tenderDocumentsQAQuery.isLoading && !tenderDocumentsQAQuery.error && tenderDocumentsQA.length > 0 && (
                        <List>
                            {tenderDocumentsQA.map((doc, index) => (
                                <ListItem
                                    key={index}
                                    divider
                                    secondaryAction={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Click to open document
                                            </Typography>
                                            <IconButton
                                                edge="end"
                                                aria-label="open document"
                                                onClick={() => handleOpenQADocument(doc.DocumentPath)}
                                                size="small"
                                                disabled={getFileUrlMutation.isPending} // Disable while loading
                                            >
                                                <LaunchIcon fontSize="small" color="action" />
                                            </IconButton>
                                            {userPrimaryGroup === 'quantum' && (
                                                <Tooltip title="Delete this document" arrow>
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete document"
                                                        size="small"
                                                        disabled={qaDocDeleting}
                                                        onClick={(e) => { e.stopPropagation(); setQaDocToDelete(doc); }}
                                                    >
                                                        <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    }
                                    component={ListItemButton}
                                    onClick={() => handleOpenQADocument(doc.DocumentPath)}
                                    sx={{ py: 1 }} 
                                    disabled={getFileUrlMutation.isPending} // Disable while loading
                                >
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                                                <DescriptionIcon fontSize="small" color="primary" />
                                                <Typography variant="body1" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {doc.DocumentPath}
                                                </Typography>
                                                {getFileUrlMutation.isPending && getFileUrlMutation.variables?.documentPath === doc.DocumentPath && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                        Opening...
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        primaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQaDocumentsDialogOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload Q&A documents (quantum only). Files go straight to S3 via
                presigned URLs; extraction runs off the S3 trigger, so new docs
                appear in the QA list after a short processing delay. */}
            <Dialog open={qaUploadDialogOpen} onClose={() => !isQaUploading && setQaUploadDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.2rem' }}>
                    Upload Q&A Documents for {selectedTender?.name}
                </DialogTitle>
                <DialogContent dividers>
                    <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={isQaUploading} sx={{ mb: 1 }}>
                        Select Files
                        <input type="file" hidden multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                            onChange={(e) => {
                                const files = Array.from(e.target.files ?? []);
                                setQaFilesForUpload(prev => [...prev, ...files.map(file => ({ file, status: 'pending' as const }))]);
                                e.target.value = '';
                            }} />
                    </Button>
                    <List dense>
                        {qaFilesForUpload.map((item, idx) => (
                            <ListItem key={`${item.file.name}-${idx}`}
                                secondaryAction={
                                    item.status === 'uploading' ? <CircularProgress size={16} />
                                    : item.status === 'done' ? <CheckCircleIcon color="success" fontSize="small" />
                                    : item.status === 'error' ? <ErrorIcon color="error" fontSize="small" />
                                    : (
                                        <IconButton edge="end" size="small" disabled={isQaUploading}
                                            onClick={() => setQaFilesForUpload(prev => prev.filter((_, i) => i !== idx))}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )
                                }>
                                <ListItemText primary={item.file.name}
                                    secondary={item.status === 'error' ? item.errorMessage : undefined}
                                    secondaryTypographyProps={{ color: 'error' }} />
                            </ListItem>
                        ))}
                    </List>
                    {qaFilesForUpload.some(f => f.status === 'done') && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Uploaded. The documents are being processed and will appear in the QA list within about a minute.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQaUploadDialogOpen(false)} disabled={isQaUploading}>Close</Button>
                    <Button variant="contained" onClick={handleQaUpload}
                        disabled={isQaUploading || !qaFilesForUpload.some(f => f.status === 'pending' || f.status === 'error')}>
                        {isQaUploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Inline document viewer for PDFs and images */}
            <Dialog
                open={inlineViewer !== null}
                onClose={() => setInlineViewer(null)}
                maxWidth="lg"
                fullWidth
                fullScreen={isSmallScreen}
                PaperProps={{ style: { height: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, py: 1 }}>
                    <Box sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: isSmallScreen ? '0.95rem' : '1.1rem', fontWeight: 500 }}>
                        {inlineViewer?.fileName}
                    </Box>
                    {inlineViewer && (
                        <Tooltip title="Open in new tab">
                            <IconButton
                                size="small"
                                component="a"
                                href={inlineViewer.url + (inlineViewer.page ? `#page=${inlineViewer.page}` : '')}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <LaunchIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton size="small" onClick={() => setInlineViewer(null)} aria-label="close">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', bgcolor: 'grey.100' }}>
                    {inlineViewer?.kind === 'pdf' && (
                        <iframe
                            src={inlineViewer.url + (inlineViewer.page ? `#page=${inlineViewer.page}` : '')}
                            title={inlineViewer.fileName}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                        />
                    )}
                    {inlineViewer?.kind === 'image' && (
                        <img
                            src={inlineViewer.url}
                            alt={inlineViewer.fileName}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};


// --- Document Selection Dialog Component ---
interface DocumentSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    documents: TenderDocument[]; // tender docs
    selectedDocs: Set<string>;
    onSelectedDocsChange: React.Dispatch<React.SetStateAction<Set<string>>>;
    companyDocuments: CompanyDocument[];
    selectedCompanyDocs: Set<string>;
    onSelectedCompanyDocsChange: React.Dispatch<React.SetStateAction<Set<string>>>;
    // Q&A documents are listed here for transparency but are always included
    // server-side; the user can't deselect them.
    qaDocuments: TenderDocumentQA[];
    // Documents the pipeline could not read: shown greyed out, never selectable.
    skippedDocuments?: TenderDocument[];
    isSmallScreen: boolean;
    onOpenDocument: (documentPath: string, source: 'tender' | 'qa' | 'company') => void;
    isOpeningDocument: boolean;
    openingDocumentPath?: string;
}

const DocumentSelectionDialog: React.FC<DocumentSelectionDialogProps> = ({
    open,
    onClose,
    documents,
    skippedDocuments = [],
    selectedDocs,
    onSelectedDocsChange,
    companyDocuments,
    selectedCompanyDocs,
    onSelectedCompanyDocsChange,
    qaDocuments,
    isSmallScreen,
    onOpenDocument,
    isOpeningDocument,
    openingDocumentPath
}) => {
    const totalSelectableDocuments = documents.length + companyDocuments.length;
    const selectedCount = selectedDocs.size + selectedCompanyDocs.size;
    // Q&A docs don't carry a language of their own; inherit from the tender
    // docs (a tender's QA clarifications are almost always in the tender's
    // language). Empty string if we can't tell — chip is hidden in that case.
    const qaInheritedLanguage = documents.find(d => d.DocumentLanguage && d.DocumentLanguage.trim() !== '')?.DocumentLanguage ?? '';

    // Compute select-all states for headers
    const tenderAllPaths = React.useMemo(() => documents.map(d => d.DocumentPath), [documents]);
    const tenderAllSelected = tenderAllPaths.length > 0 && tenderAllPaths.every(p => selectedDocs.has(p));
    const tenderIndeterminate = selectedDocs.size > 0 && !tenderAllSelected && tenderAllPaths.some(p => selectedDocs.has(p));
    const toggleTenderAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSelectedDocsChange(e.target.checked ? new Set(tenderAllPaths) : new Set());
    };

    const companyAllPaths = React.useMemo(() => companyDocuments.map(d => d.DocumentPath), [companyDocuments]);
    const companyAllSelected = companyAllPaths.length > 0 && companyAllPaths.every(p => selectedCompanyDocs.has(p));
    const companyIndeterminate = selectedCompanyDocs.size > 0 && !companyAllSelected && companyAllPaths.some(p => selectedCompanyDocs.has(p));
    const toggleCompanyAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSelectedCompanyDocsChange(e.target.checked ? new Set(companyAllPaths) : new Set());
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isSmallScreen}>
            <DialogTitle>
                Select Documents for Context
                <Typography variant="body2" color="text.secondary">
                    Selected: {selectedCount} of {totalSelectableDocuments}
                    {qaDocuments.length > 0 && ` · ${qaDocuments.length} Q&A doc${qaDocuments.length === 1 ? '' : 's'} always included`}
                    {skippedDocuments.length > 0 && ` · ${skippedDocuments.length} could not be read`}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Accordion defaultExpanded={true} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <FormControlLabel
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            label={<Typography sx={{ fontWeight: 'medium' }}>Select All Tender Documents</Typography>}
                            control={<Checkbox size="small" checked={tenderAllSelected} indeterminate={tenderIndeterminate} onChange={toggleTenderAll} />}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        <DocumentExplorer
                            documents={documents}
                            selectedDocs={selectedDocs}
                            onSelectedDocsChange={onSelectedDocsChange}
                            showSelectAll={false}
                            onOpenDocument={(path) => onOpenDocument(path, 'tender')}
                            isOpeningDocument={isOpeningDocument}
                            openingDocumentPath={openingDocumentPath}
                            lockedRowsAfter={[
                            ...skippedDocuments.map((doc, index) => (
                                <Tooltip key={`skipped-${index}`} title={doc.Summary || doc.SkipReason || 'This file could not be read.'}
                                    placement="top-start" arrow>
                                    <ListItemButton disableRipple sx={{ pl: 0, py: 0.2, cursor: 'default', opacity: 0.6 }}>
                                        <Box sx={{ width: 28, mr: 0.5 }} />
                                        <Checkbox size="small" checked={false} disabled sx={{ p: 0.5, mr: 0.5 }} />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexGrow: 1, overflow: 'hidden' }}>
                                            {doc.ProcessingStatus === 'protected' ? <LockOutlinedIcon fontSize="small" /> : <BlockOutlinedIcon fontSize="small" />}
                                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem' }}>
                                                {doc.DocumentPath}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', flexShrink: 0 }}>
                                                <Chip label={skippedLabel(doc.ProcessingStatus)} size="small" variant="outlined" color="warning"
                                                    sx={{ height: 16, fontSize: '0.65rem' }} />
                                            </Stack>
                                        </div>
                                    </ListItemButton>
                                </Tooltip>
                            )),
                            ...qaDocuments.map((doc, index) => {
                                const isThisDocOpening = isOpeningDocument && openingDocumentPath === doc.DocumentPath;
                                return (
                                    <Tooltip
                                        key={`qa-${index}`}
                                        title="Q&A clarifications are always part of the bot's context. They often hold critical answers."
                                        placement="top-start"
                                        arrow
                                    >
                                        <ListItemButton
                                            onClick={() => onOpenDocument(doc.DocumentPath, 'qa')}
                                            disabled={isOpeningDocument}
                                            sx={{ pl: 0, py: 0.2 }}
                                        >
                                            <Box sx={{ width: 28, mr: 0.5 }} />
                                            <Checkbox size="small" checked disabled sx={{ p: 0.5, mr: 0.5 }} />
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexGrow: 1, overflow: 'hidden' }}>
                                                <DescriptionIcon fontSize="small" />
                                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem' }}>
                                                    {doc.DocumentPath}
                                                </Typography>
                                                {isThisDocOpening && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                                                        Opening...
                                                    </Typography>
                                                )}
                                                <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', flexShrink: 0 }}>
                                                    {qaInheritedLanguage && (
                                                        <Chip label={qaInheritedLanguage.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                                                    )}
                                                    <Chip label="Q&A Documents" size="small" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
                                                </Stack>
                                            </div>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); onOpenDocument(doc.DocumentPath, 'qa'); }}
                                                disabled={isOpeningDocument}
                                                sx={{ ml: 1, flexShrink: 0 }}
                                            >
                                                <LaunchIcon fontSize="inherit" />
                                            </IconButton>
                                        </ListItemButton>
                                    </Tooltip>
                                );
                            })]}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded={true} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <FormControlLabel
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            label={<Typography sx={{ fontWeight: 'medium' }}>Select All Company Documents</Typography>}
                            control={<Checkbox size="small" checked={companyAllSelected} indeterminate={companyIndeterminate} onChange={toggleCompanyAll} />}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        <DocumentExplorer
                            // Map company docs to minimal TenderDocument-like shape for tree rendering
                            documents={companyDocuments.map(d => ({
                                S3LocationPk: d.S3LocationPk,
                                DocumentPath: d.DocumentPath,
                                Summary: '',
                                DocumentPartPk: '',
                                DocumentCategory: 'Company',
                                DocumentLanguage: '',
                                Tags: d.Tags || []
                            }))}
                            selectedDocs={selectedCompanyDocs}
                            onSelectedDocsChange={onSelectedCompanyDocsChange}
                            showSelectAll={false}
                            onOpenDocument={(path) => onOpenDocument(path, 'company')}
                            isOpeningDocument={isOpeningDocument}
                            openingDocumentPath={openingDocumentPath}
                        />
                    </AccordionDetails>
                </Accordion>

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Done</Button>
            </DialogActions>
        </Dialog>
    );
};


// --- Add/Edit Workforce Member Dialog Component ---
interface AddEditWorkforceMemberDialogProps {
    open: boolean;
    onClose: () => void;
    member: WorkforceMember | null;
    onSave: (member: Omit<WorkforceMember, 'id'> & { id?: string }) => void;
    isSmallScreen: boolean;
    isSaving: boolean;
    saveError: string | null;
    userApiKey: string;
    idToken: string | undefined;
}

const AddEditWorkforceMemberDialog: React.FC<AddEditWorkforceMemberDialogProps> = ({ open, onClose, member, onSave, isSmallScreen, isSaving, saveError, userApiKey, idToken }) => {
    const initialFormState: Omit<WorkforceMember, 'id'> = {
        name: '',
        role: '',
        tenderReadyBio: '',
        industryExpertise: [],
        skills: [],
        languages: [],
        education: [],
        certifications: [],
    };

    const [formData, setFormData] = useState<Omit<WorkforceMember, 'id'>>(initialFormState);
    const [newItem, setNewItem] = useState({
        industry: '',
        skillName: '',
        skillRating: 'Intermediate',
        languageName: '',
        languageRating: 'B2 (Professional Fluency)',
        education: '',
        certification: '',
    });
    // NEW: State for CV Upload functionality
    const [inputMethod, setInputMethod] = useState<'manual' | 'cv'>('manual');
    const [cvFile, setCvFile] = useState<File | null>(null);
    const [cvFileName, setCvFileName] = useState<string>('');
    const [isProcessingCv, setIsProcessingCv] = useState<boolean>(false);
    const [processCvError, setProcessCvError] = useState<string | null>(null);


    useEffect(() => {
        if (open) {
            if (member) {
                setFormData({
                    name: member.name || '',
                    role: member.role || '',
                    tenderReadyBio: member.tenderReadyBio || '',
                    industryExpertise: [...(member.industryExpertise || [])],
                    skills: [...(member.skills || [])],
                    languages: [...(member.languages || [])],
                    education: [...(member.education || [])],
                    certifications: [...(member.certifications || [])],
                });
                setInputMethod('manual'); // When editing, always start in manual mode
            } else {
                // Reset everything for a new member
                setFormData(initialFormState);
                setCvFile(null);
                setCvFileName('');
                setProcessCvError(null);
                setIsProcessingCv(false);
                setInputMethod('manual');
            }
        }
    }, [open, member]);

    const handleInputMethodChange = (event: React.MouseEvent<HTMLElement>, newInputMethod: 'manual' | 'cv' | null) => {
        if (newInputMethod !== null) {
            setInputMethod(newInputMethod);
            // Reset form when switching modes
            setFormData(initialFormState);
            setCvFile(null);
            setCvFileName('');
            setProcessCvError(null);
            setIsProcessingCv(false);
        }
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleNewItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewItem(prev => ({ ...prev, [name]: value }));
    };
    
    // Generic function to add an item to a simple string array
    const handleAddItemToArray = (field: keyof Omit<WorkforceMember, 'id' | 'name' | 'role' | 'tenderReadyBio' | 'skills' | 'languages'>, value: string) => {
        if (!value.trim()) return;
        const currentItems = formData[field] as string[] || [];
        if(currentItems.includes(value.trim())) return; // Avoid duplicates
        setFormData(prev => ({ ...prev, [field]: [...currentItems, value.trim()] }));
    };

    // Generic function to remove an item from any array in the form
    const handleRemoveItemFromArray = (field: keyof Omit<WorkforceMember, 'id' | 'name' | 'role' | 'tenderReadyBio'>, index: number) => {
        const currentItems = formData[field] as any[];
        setFormData(prev => ({ ...prev, [field]: currentItems.filter((_, i) => i !== index) }));
    };

    const handleAddSkill = () => {
        if (!newItem.skillName.trim()) return;
        if (formData.skills.some(s => s.name.toLowerCase() === newItem.skillName.trim().toLowerCase())) return;
        setFormData(prev => ({
            ...prev,
            skills: [...prev.skills, { name: newItem.skillName.trim(), rating: newItem.skillRating }],
        }));
        setNewItem(prev => ({ ...prev, skillName: '' }));
    };

    const handleAddLanguage = () => {
        if (!newItem.languageName.trim()) return;
        if (formData.languages.some(l => l.name.toLowerCase() === newItem.languageName.trim().toLowerCase())) return;
        setFormData(prev => ({
            ...prev,
            languages: [...prev.languages, { name: newItem.languageName.trim(), rating: newItem.languageRating }],
        }));
        setNewItem(prev => ({ ...prev, languageName: '' }));
    };

    const handleCvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            setCvFile(file);
            setCvFileName(file.name);
        } else {
            setCvFile(null);
            setCvFileName('');
        }
    };

    const handleProcessCv = async () => {
        if (!cvFile || !userApiKey || !idToken) {
            setProcessCvError("CV file and authentication are required.");
            return;
        }

        setIsProcessingCv(true);
        setProcessCvError(null);
        const formDataUpload = new FormData();
        formDataUpload.append('file', cvFile);
        const uploadCvUrl = `${appConfig.api.tender.url}/files/upload`;

        try {
            const response = await fetch(uploadCvUrl, {
                method: 'POST',
                headers: { 'X-Api-Key': userApiKey, 'Authorization': idToken },
                body: formDataUpload,
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || `HTTP error! status: ${response.status}`);

            const cvData = result.data;
            
            // **FIXED**: This now correctly uses the data from the backend.
            setFormData({
                name: cvData.name || '',
                role: cvData.role || '',
                tenderReadyBio: cvData.tenderReadyBio || '',
                skills: cvData.skills || [],
                languages: cvData.languages || [],
                industryExpertise: cvData.industryExpertise || [],
                education: cvData.education || [],
                certifications: cvData.certifications || [],
            });
            setInputMethod('manual');
        } catch (err) {
            setProcessCvError(err instanceof Error ? err.message : 'An unknown error occurred during CV processing.');
        } finally {
            setIsProcessingCv(false);
        }
    };


    const handleInternalSave = () => {
        if (!formData.name.trim() || !formData.role.trim()) {
            alert("Please fill in at least Name and Role.");
            return;
        }
        onSave({ ...formData, id: member?.id });
    };

    const renderArrayManager = (
        title: string,
        field: keyof Omit<WorkforceMember, 'id' | 'name' | 'role' | 'tenderReadyBio' | 'skills' | 'languages'>,
        newItemKey: keyof typeof newItem,
        placeholder: string
    ) => (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mb: 1 }}>{title}</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={placeholder}
                    name={newItemKey}
                    value={newItem[newItemKey]}
                    onChange={handleNewItemChange}
                />
                <Button variant="outlined" onClick={() => {
                    handleAddItemToArray(field, newItem[newItemKey]);
                    setNewItem(prev => ({ ...prev, [newItemKey]: '' }));
                }} sx={{ flexShrink: 0 }}>Add</Button>
            </Stack>
            {formData[field] && (formData[field] as string[]).length > 0 &&
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {(formData[field] as string[]).map((item, index) => (
                        <Chip key={index} label={item} onDelete={() => handleRemoveItemFromArray(field, index)} />
                    ))}
                </Stack>
            }
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isSmallScreen}>
            <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.2rem' }}>{member ? 'Edit Workforce Member' : 'Add New Workforce Member'}</DialogTitle>
            <DialogContent dividers sx={{ '& .MuiTypography-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiButton-root': { fontSize: isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize } }}>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                    
                    {!member && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                            <ToggleButtonGroup value={inputMethod} exclusive onChange={handleInputMethodChange} size="small">
                                <ToggleButton value="manual">Manual</ToggleButton>
                                <ToggleButton value="cv">CV Upload</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    {inputMethod === 'manual' && (
                         <>
                            <TextField label="Name" name="name" value={formData.name} onChange={handleInputChange} fullWidth size="small" required />
                            <TextField label="Role" name="role" value={formData.role} onChange={handleInputChange} fullWidth size="small" required />
                            
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Tender-Ready Bio</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TextField
                                        label="Bio for Proposals" name="tenderReadyBio" value={formData.tenderReadyBio} onChange={handleInputChange}
                                        fullWidth size="small" multiline rows={4} helperText="A short, client-facing summary perfect for proposals."
                                    />
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Industry Expertise</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {renderArrayManager("Industries", "industryExpertise", "industry", "Add Industry (e.g., Banking)")}
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography>Skills</Typography>
                                        <Tooltip
                                            title={
                                                <Typography variant="body2" sx={{ p: 1 }}>
                                                    Add skills relevant for tender applications. Don't focus exclusively on technical skills. Include also methodological and soft skills, like project management, etc.
                                                </Typography>
                                            }
                                            arrow
                                        >
                                            <IconButton size="small" sx={{ color: 'text.secondary' }}>
                                                <InfoOutlinedIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                        <TextField fullWidth size="small" label="Skill Name" name="skillName" value={newItem.skillName} onChange={handleNewItemChange} />
                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                            <InputLabel>Rating</InputLabel>
                                            <Select value={newItem.skillRating} label="Rating" onChange={(e) => setNewItem(prev => ({...prev, skillRating: e.target.value}))}>
                                                <MenuItem value="Expert">Expert</MenuItem>
                                                <MenuItem value="Intermediate">Intermediate</MenuItem>
                                                <MenuItem value="Beginner">Beginner</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Button variant="outlined" onClick={handleAddSkill} sx={{ flexShrink: 0 }}>Add</Button>
                                    </Stack>
                                    {formData.skills.length > 0 &&
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {formData.skills.map((skill, index) => (
                                                <Chip key={index} label={`${skill.name} (${skill.rating})`} onDelete={() => handleRemoveItemFromArray("skills", index)} />
                                            ))}
                                        </Stack>
                                    }
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Languages</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                        <TextField fullWidth size="small" label="Language" name="languageName" value={newItem.languageName} onChange={handleNewItemChange} />
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Proficiency</InputLabel>
                                            <Select value={newItem.languageRating} label="Proficiency" onChange={(e) => setNewItem(prev => ({...prev, languageRating: e.target.value}))}>
                                                <MenuItem value="Native">Native</MenuItem>
                                                <MenuItem value="C2 (Mastery)">C2 (Mastery)</MenuItem>
                                                <MenuItem value="C1 (Advanced)">C1 (Advanced)</MenuItem>
                                                <MenuItem value="B2 (Professional Fluency)">B2 (Professional Fluency)</MenuItem>
                                                <MenuItem value="B1 (Conversational)">B1 (Conversational)</MenuItem>
                                                <MenuItem value="A1/A2 (Basic)">A1/A2 (Basic)</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Button variant="outlined" onClick={handleAddLanguage}>Add</Button>
                                    </Stack>
                                    {formData.languages.length > 0 &&
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {formData.languages.map((lang, index) => (
                                                <Chip key={index} label={`${lang.name} (${lang.rating})`} onDelete={() => handleRemoveItemFromArray("languages", index)} />
                                            ))}
                                        </Stack>
                                    }
                                </AccordionDetails>
                            </Accordion>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Qualifications</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {renderArrayManager("Certifications", "certifications", "certification", "Add Certification (e.g., Tableau)")}
                                        <Divider />
                                        {renderArrayManager("Education", "education", "education", "Add Degree (e.g., MSc CompSci)")}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        </>
                    )}

                    {inputMethod === 'cv' && (
                         <Stack spacing={2} alignItems="center">
                            {processCvError && <Alert severity="error" sx={{ width: '100%' }}>{processCvError}</Alert>}
                            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth>
                                Upload CV
                                <input type="file" hidden accept=".pdf,.doc,.docx" onChange={handleCvFileChange} />
                            </Button>
                            {cvFileName && <Typography variant="body2" color="text.secondary">Uploaded: {cvFileName}</Typography>}
                            <Button variant="contained" onClick={handleProcessCv} disabled={!cvFile || isProcessingCv}>
                                {isProcessingCv ? 'Processing CV...' : 'Process & Review'}
                            </Button>
                            {isProcessingCv && <Typography variant="caption" color="text.secondary">Extracting information, please wait...</Typography>}
                        </Stack>
                    )}

                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleInternalSave} disabled={isSaving || isProcessingCv || inputMethod === 'cv' || !formData.name.trim() || !formData.role.trim()}>
                    {isSaving ? 'Saving...' : (member ? 'Save Changes' : 'Add Member')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// --- Manage Company Dialog Component ---
interface ManageCompanyDialogProps {
    open: boolean;
    onClose: () => void;
    companyProfile: CompanyProfile;
    onOpenAddEditProjectModal: (project: PastProject | null) => void;
    onRemoveProject: (projectId: string) => void;
    isSmallScreen: boolean;
    isLoading: boolean;
    fetchProfileError: string | null;
    onSaveSection: (sectionData: { sectionName: string, sectionOrder: number, description: string }) => void;
    onRemoveSection: (sectionOrder: number) => void;
    onAddSection: (sectionName: string) => void;
    relevantIndustries: string[];
    onSaveRelevantIndustries: (industries: string[]) => void;
    savingIndustries: boolean;
    relevantTopics: string[];
    topicCatalog: TopicCatalog | undefined;
    onSaveRelevantTopics: (topics: string[]) => void;
    savingTopics: boolean;
    onManageTags: () => void;
}

const ManageCompanyDialog: React.FC<ManageCompanyDialogProps> = ({
    open, onClose, companyProfile, onOpenAddEditProjectModal, onRemoveProject, isSmallScreen,
    isLoading, fetchProfileError, onSaveSection, onRemoveSection, onAddSection,
    relevantIndustries, onSaveRelevantIndustries, savingIndustries,
    relevantTopics, topicCatalog, onSaveRelevantTopics, savingTopics, onManageTags
}) => {
    const [editingSection, setEditingSection] = useState<string | null>(null);
    const [editingContent, setEditingContent] = useState('');
    const [editingDescription, setEditingDescription] = useState<string | null>(null);
    const [editingDescriptionContent, setEditingDescriptionContent] = useState('');
    const [addInfoDialogOpen, setAddInfoDialogOpen] = useState(false);
    const [newSectionName, setNewSectionName] = useState('');
    // Draft of the relevant-industries setting (saved explicitly).
    const [industriesDraft, setIndustriesDraft] = useState<string[]>([]);
    useEffect(() => { setIndustriesDraft(relevantIndustries || []); }, [open, relevantIndustries]);
    // Draft of the relevant-topics setting (saved explicitly). Options are the
    // active taxonomy's level-1 topics; a saved slug the catalog no longer
    // knows is kept and shown by its slug.
    const [topicsDraft, setTopicsDraft] = useState<string[]>([]);
    useEffect(() => { setTopicsDraft(relevantTopics || []); }, [open, relevantTopics]);
    const topicOptionsForSetting = useMemo(() => {
        const known = (topicCatalog?.topics ?? []).filter(t => t.level === 1);
        const order = Object.keys(GROUP_LABELS);
        const sorted = [...known].sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group) || a.sort - b.sort);
        const slugs = sorted.map(t => t.slug);
        (relevantTopics || []).forEach(s => { if (!slugs.includes(s)) slugs.push(s); });
        return slugs;
    }, [topicCatalog, relevantTopics]);
    const topicSettingLabel = (slug: string) => topicCatalog?.topics.find(t => t.slug === slug)?.name ?? slug;
    const topicSettingGroup = (slug: string) => {
        const group = topicCatalog?.topics.find(t => t.slug === slug)?.group;
        return group ? (GROUP_LABELS[group] ?? group) : 'Other';
    };
    // Past-projects browsing: freetext filter + which customer groups are open.
    const [projectSearch, setProjectSearch] = useState('');
    const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (open) {
            setEditingSection(null);
            setEditingContent('');
            setEditingDescription(null);
            setEditingDescriptionContent('');
            setProjectSearch('');
            setExpandedCustomers(new Set());
        }
    }, [open]);

    // Projects grouped by customer (case-insensitive), filtered by the search
    // term across name/customer/description/industry. Customers A-Z, projects
    // newest first inside each group.
    const projectSearchActive = projectSearch.trim() !== '';
    const customerGroups = useMemo(() => {
        const term = projectSearch.trim().toLowerCase();
        const filtered = companyProfile.pastProjects.filter(p => !term
            || p.projectName.toLowerCase().includes(term)
            || p.customerName.toLowerCase().includes(term)
            || (p.description || '').toLowerCase().includes(term)
            || (p.industry || '').toLowerCase().includes(term));
        const groups = new Map<string, { displayName: string; projects: PastProject[] }>();
        for (const p of filtered) {
            const key = p.customerName.trim().toLowerCase() || '(no customer)';
            const g = groups.get(key) ?? { displayName: p.customerName.trim() || 'No customer', projects: [] };
            g.projects.push(p);
            groups.set(key, g);
        }
        return Array.from(groups.entries())
            .map(([key, g]) => ({
                key,
                displayName: g.displayName,
                projects: [...g.projects].sort((a, b) => (b.startYear || 0) - (a.startYear || 0)),
            }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [companyProfile.pastProjects, projectSearch]);
    const visibleProjectCount = customerGroups.reduce((n, g) => n + g.projects.length, 0);
    const toggleCustomer = (key: string) => setExpandedCustomers(prev => {
        const next = new Set(prev);
        if (next.has(key)) { next.delete(key); } else { next.add(key); }
        return next;
    });

    const dialogFontSize = isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isSmallScreen}>
            <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.2rem' }}>Manage Company Profile</DialogTitle>
            <DialogContent dividers sx={{ '& .MuiTypography-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiButton-root': { fontSize: dialogFontSize } }}>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {isLoading && <Typography>Loading company profile...</Typography>}
                    {fetchProfileError && <Alert severity="error">Error loading profile: {fetchProfileError}</Alert>}
                    {!isLoading && !fetchProfileError && (
                        <>
                            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>Your Industries</Typography>
                            <Typography variant="caption" color="text.secondary">
                                The industries your company works in. Construction tenders are only
                                considered if construction is one of them.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    multiple size="small" fullWidth
                                    options={Object.keys(CPV_DIVISION_LABELS)}
                                    getOptionLabel={(code) => `${code} — ${CPV_DIVISION_LABELS[code]}`}
                                    value={industriesDraft}
                                    onChange={(_e, value) => setIndustriesDraft(value)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Industries" placeholder="Add industry..." />
                                    )}
                                />
                                <Button variant="outlined" disabled={savingIndustries}
                                    onClick={() => onSaveRelevantIndustries(industriesDraft)}
                                    sx={{ whiteSpace: 'nowrap', mt: 0.5, flexShrink: 0 }}>
                                    {savingIndustries ? 'Saving...' : 'Save'}
                                </Button>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>Your Topics</Typography>
                            <Typography variant="caption" color="text.secondary">
                                The topics your company sells into. The tender list opens on them
                                ("My topics" in the Topic filter); untick it there to see everything.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    multiple size="small" fullWidth
                                    options={topicOptionsForSetting}
                                    groupBy={topicSettingGroup}
                                    getOptionLabel={topicSettingLabel}
                                    value={topicsDraft}
                                    onChange={(_e, value) => setTopicsDraft(value)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Topics" placeholder="Add topic..." />
                                    )}
                                />
                                <Button variant="outlined" disabled={savingTopics}
                                    onClick={() => onSaveRelevantTopics(topicsDraft)}
                                    sx={{ whiteSpace: 'nowrap', mt: 0.5, flexShrink: 0 }}>
                                    {savingTopics ? 'Saving...' : 'Save'}
                                </Button>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>Your Tags</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Your own labels for tenders: a kind of work or a product. They mark and
                                filter the tender list and never change scores.
                            </Typography>
                            <Box>
                                <Button variant="outlined" onClick={onManageTags}>Manage tags</Button>
                            </Box>
                            <Divider />
                            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>Company Information Sections ({companyProfile.sections.length} of 5)</Typography>
                            {/* Render sections list */}
                            <List disablePadding sx={{ maxHeight: isSmallScreen ? 'calc(100vh - 300px)' : '400px', overflowY: 'auto' }}>
                                {companyProfile.sections.sort((a, b) => a.sectionOrder - b.sectionOrder).map((section, idx) => (
                                    <Accordion key={`${section.sectionName}-${idx}`} elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    {editingSection !== section.sectionName ? (
                                                        <>
                                                            <Typography sx={{ fontWeight: 'medium' }}>{section.sectionName}</Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {section.description ? `${section.description.substring(0, 50)}${section.description.length > 50 ? '...' : ''}` : 'No content provided.'}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                            <TextField
                                                                value={editingContent}
                                                                onChange={(e) => setEditingContent(e.target.value)}
                                                                variant="outlined"
                                                                size="small"
                                                                placeholder="Section Name"
                                                                sx={{ 
                                                                    '& .MuiInputBase-root': { 
                                                                        fontWeight: 'medium',
                                                                        fontSize: '1rem'
                                                                    }
                                                                }}
                                                                autoFocus
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        const section = companyProfile.sections.find(s => s.sectionName === editingSection);
                                                                        if (section && editingContent.trim()) {
                                                                            onSaveSection({
                                                                                sectionName: editingContent.trim(),
                                                                                sectionOrder: section.sectionOrder,
                                                                                description: section.description
                                                                            });
                                                                            setEditingSection(null);
                                                                            setEditingContent('');
                                                                        }
                                                                    } else if (e.key === 'Escape') {
                                                                        setEditingSection(null);
                                                                        setEditingContent('');
                                                                    }
                                                                }}
                                                            />
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Button 
                                                                    size="small" 
                                                                    onClick={(e) => { 
                                                                        e.stopPropagation(); 
                                                                        setEditingSection(null); 
                                                                        setEditingContent(''); 
                                                                    }}
                                                                >
                                                                    Discard
                                                                </Button>
                                                                <Button 
                                                                    size="small" 
                                                                    variant="contained" 
                                                                    onClick={(e) => { 
                                                                        e.stopPropagation();
                                                                        const section = companyProfile.sections.find(s => s.sectionName === editingSection);
                                                                        if (section && editingContent.trim()) {
                                                                            onSaveSection({
                                                                                sectionName: editingContent.trim(),
                                                                                sectionOrder: section.sectionOrder,
                                                                                description: section.description
                                                                            });
                                                                            setEditingSection(null);
                                                                            setEditingContent('');
                                                                        }
                                                                    }}
                                                                    disabled={!editingContent.trim()}
                                                                >
                                                                    Save
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                                {editingSection !== section.sectionName && (
                                                    <Box>
                                                        <Tooltip title="Edit Section Name">
                                                            <IconButton size="small" onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                setEditingSection(section.sectionName); 
                                                                setEditingContent(section.sectionName); // Start editing with current name
                                                            }}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Section">
                                                            <IconButton size="small" onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                onRemoveSection(section.sectionOrder); 
                                                            }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ bgcolor: 'grey.100' }}>
                                            {editingDescription !== section.sectionName ? (
                                                <>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                                                        {section.description || 'No content provided.'}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Button 
                                                            size="small" 
                                                            variant="outlined"
                                                            onClick={(e) => { 
                                                                e.stopPropagation();
                                                                // Start editing description
                                                                setEditingDescription(section.sectionName); 
                                                                setEditingDescriptionContent(section.description); 
                                                            }}
                                                        >
                                                            Edit Description
                                                        </Button>
                                                    </Box>
                                                </>
                                            ) : (
                                                <>
                                                    <TextField
                                                        multiline
                                                        rows={4}
                                                        value={editingDescriptionContent}
                                                        onChange={(e) => setEditingDescriptionContent(e.target.value)}
                                                        variant="outlined"
                                                        fullWidth
                                                        size="small"
                                                        placeholder="Section Description"
                                                        sx={{ mb: 1 }}
                                                    />
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                        <Button 
                                                            size="small"
                                                            onClick={(e) => { 
                                                                e.stopPropagation();
                                                                setEditingDescription(null); 
                                                                setEditingDescriptionContent(''); 
                                                            }}
                                                        >
                                                            Discard
                                                        </Button>
                                                        <Button 
                                                            size="small" 
                                                            variant="contained" 
                                                            onClick={(e) => { 
                                                                e.stopPropagation();
                                                                const section = companyProfile.sections.find(s => s.sectionName === editingDescription);
                                                                if (section) {
                                                                    onSaveSection({
                                                                        sectionName: section.sectionName,
                                                                        sectionOrder: section.sectionOrder,
                                                                        description: editingDescriptionContent
                                                                    });
                                                                    setEditingDescription(null);
                                                                    setEditingDescriptionContent('');
                                                                }
                                                            }}
                                                        >
                                                            Save
                                                        </Button>
                                                    </Box>
                                                </>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </List>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
                                <Button 
                                    variant="outlined" 
                                    size="small" 
                                    onClick={() => { setNewSectionName(''); setAddInfoDialogOpen(true); }}
                                    disabled={companyProfile.sections.length >= 5}
                                    sx={{ 
                                        opacity: companyProfile.sections.length >= 5 ? 0.6 : 1,
                                        '&:disabled': { 
                                            color: 'text.disabled',
                                            borderColor: 'text.disabled'
                                        }
                                    }}
                                >
                                    + Add Further Company Information
                                </Button>
                                {companyProfile.sections.length >= 5 && (
                                    <Tooltip
                                        title={
                                            <Box sx={{ p: 0.5 }}>
                                                <Typography variant="body2">
                                                    Maximum of 5 company information sections reached. This limit helps focus the information provided to the AI assistant for better responses.
                                                </Typography>
                                            </Box>
                                        }
                                        placement="right"
                                        arrow
                                        componentsProps={{ tooltip: { sx: { maxWidth: 360, bgcolor: 'warning.light', color: 'warning.contrastText', border: '1px solid', borderColor: 'warning.main' } } }}
                                    >
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            width: 20, 
                                            height: 20, 
                                            borderRadius: '50%', 
                                            bgcolor: 'warning.main', 
                                            color: 'warning.contrastText',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            cursor: 'help'
                                        }}>
                                            !
                                        </Box>
                                    </Tooltip>
                                )}
                                <Tooltip
                                    title={
                                        <Box sx={{ p: 0.5 }}>
                                            <Typography variant="body2">
                                                Add key strategic statements and policies, such as your mission statement, sustainability strategy, quality policy, security policy, or company values.
                                            </Typography>
                                        </Box>
                                    }
                                    placement="right"
                                    arrow
                                    componentsProps={{ tooltip: { sx: { maxWidth: 360, bgcolor: 'background.paper', color: 'text.primary', border: '1px solid', borderColor: 'divider' } } }}
                                >
                                    <IconButton size="small" sx={{ color: 'text.secondary', p: 0.25 }}>
                                        <InfoOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </>
                    )}
                    <Divider />
                    <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        Past Projects ({projectSearchActive
                            ? `${visibleProjectCount} of ${companyProfile.pastProjects.length}`
                            : companyProfile.pastProjects.length})
                    </Typography>
                    <TextField
                        fullWidth size="small"
                        placeholder="Search projects — name, customer, description, industry…"
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                    />
                    <List disablePadding sx={{ maxHeight: isSmallScreen ? 'calc(100vh - 300px)' : '400px', overflowY: 'auto' }}>
                        {customerGroups.map((group) => {
                            const expanded = projectSearchActive || expandedCustomers.has(group.key);
                            return (
                                <Box key={group.key}>
                                    <ListItemButton onClick={() => toggleCustomer(group.key)}
                                        sx={{ py: 0.75, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <ExpandMoreIcon sx={{ fontSize: '1.1rem', mr: 0.75, color: 'text.secondary',
                                            transform: expanded ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                                        <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{group.displayName}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {group.projects.length} project{group.projects.length === 1 ? '' : 's'}
                                        </Typography>
                                    </ListItemButton>
                                    <Collapse in={expanded} unmountOnExit>
                                        {group.projects.map((project) => (
                                            <Accordion key={project.id} elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pl: 3.5 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 'medium' }}>{project.projectName}</Typography>
                                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.25 }}>
                                                                {project.startYear && (
                                                                    <Chip size="small" label={`${project.startYear}–${project.endYear || 'ongoing'}`}
                                                                        sx={{ height: 18, fontSize: '0.65rem' }} />
                                                                )}
                                                                {project.industry && (
                                                                    <Chip size="small" variant="outlined" label={project.industry}
                                                                        sx={{ height: 18, fontSize: '0.65rem' }} />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        <Box>
                                                            <Tooltip title="Edit Project"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenAddEditProjectModal(project); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                            <Tooltip title="Remove Project"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemoveProject(project.id); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                        </Box>
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ bgcolor: 'grey.100' }}>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{project.description}</Typography>
                                                    {project.industry && <DetailItem label="Industry" value={project.industry} />}
                                                    {project.startYear && <DetailItem label="Period" value={`${project.startYear} - ${project.endYear || 'Ongoing'}`} />}
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Collapse>
                                </Box>
                            );
                        })}
                        {customerGroups.length === 0 && (
                            <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                                {projectSearchActive ? `No projects match "${projectSearch.trim()}".` : 'No projects yet.'}
                            </Typography>
                        )}
                    </List>
                    <Box sx={{ p: 1, textAlign: 'center', borderTop: companyProfile.pastProjects.length > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Button variant="outlined" onClick={() => onOpenAddEditProjectModal(null)}>+ Add New Project</Button>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>

            {/* Add Further Company Information Dialog */}
            <Dialog open={addInfoDialogOpen} onClose={() => setAddInfoDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.1rem' }}>Add Company Information Section</DialogTitle>
                <DialogContent>
                    {companyProfile.sections.length >= 5 ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Maximum of 5 company information sections reached. This limit helps focus the information provided to the AI assistant for better responses.
                        </Alert>
                    ) : (
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            placeholder="e.g., Sustainability Strategy"
                            value={newSectionName}
                            onChange={(e) => setNewSectionName(e.target.value)}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddInfoDialogOpen(false)}>
                        {companyProfile.sections.length >= 5 ? 'Close' : 'Discard'}
                    </Button>
                    {companyProfile.sections.length < 5 && (
                        <Button 
                            variant="contained" 
                            onClick={() => { 
                                if (!newSectionName.trim()) return; 
                                onAddSection(newSectionName.trim());
                                setAddInfoDialogOpen(false);
                                setNewSectionName('');
                            }}
                            disabled={!newSectionName.trim()}
                        >
                            Save
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

// --- Add/Edit Project Dialog Component ---
interface AddEditProjectDialogProps {
    open: boolean;
    onClose: () => void;
    project: PastProject | null;
    onSave: (project: Omit<PastProject, 'id'> & { id?: string }) => void;
    isSmallScreen: boolean;
    isSaving: boolean;
    saveError: string | null;
    existingProjects: PastProject[];
}

const AddEditProjectDialog: React.FC<AddEditProjectDialogProps> = ({ open, onClose, project, onSave, isSmallScreen, isSaving, saveError, existingProjects }) => {
    const initialFormState: Omit<PastProject, 'id'> = {
        projectName: '', customerName: '', description: '', industry: '', startYear: new Date().getFullYear(), endYear: undefined,
    };
    const [formData, setFormData] = useState<Omit<PastProject, 'id'>>(initialFormState);

    // Existing customers (case-insensitive unique) with their project counts —
    // shown as autocomplete options so nobody re-enters "UBS" as "UBS AG" or
    // wonders whether a customer is already in.
    const customerOptions = useMemo(() => {
        const byKey = new Map<string, { name: string; count: number }>();
        for (const p of existingProjects) {
            const key = p.customerName.trim().toLowerCase();
            if (!key) continue;
            const entry = byKey.get(key);
            if (entry) { entry.count += 1; } else { byKey.set(key, { name: p.customerName.trim(), count: 1 }); }
        }
        return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [existingProjects]);

    useEffect(() => {
        if (open) {
            setFormData(project ? { ...project } : initialFormState);
        }
    }, [open, project]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: (name === 'startYear' || name === 'endYear') ? (value === '' ? undefined : parseInt(value, 10)) : value }));
    };

    const handleInternalSave = () => {
        if (!formData.projectName.trim() || !formData.customerName.trim() || !formData.description.trim() || !formData.startYear) {
            alert("Project Name, Customer Name, Description, and Start Year are required.");
            return;
        }
        if (formData.endYear && formData.startYear && formData.endYear < formData.startYear) {
            alert("End year cannot be before the start year.");
            return;
        }
        onSave({ ...formData, id: project?.id });
    };

    const dialogFontSize = isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isSmallScreen}>
            <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.2rem' }}>{project ? 'Edit Project' : 'Add New Project'}</DialogTitle>
            <DialogContent dividers sx={{ '& .MuiTypography-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiButton-root': { fontSize: dialogFontSize } }}>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                    <TextField label="Project Name" name="projectName" value={formData.projectName} onChange={handleInputChange} fullWidth size="small" required />
                    <Autocomplete
                        freeSolo
                        options={customerOptions.map(c => c.name)}
                        inputValue={formData.customerName}
                        onInputChange={(_e, value) => setFormData(prev => ({ ...prev, customerName: value }))}
                        renderOption={(props, option) => {
                            const match = customerOptions.find(c => c.name === option);
                            return (
                                <li {...props} key={option}>
                                    {option}
                                    {match && (
                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                            {match.count} existing project{match.count === 1 ? '' : 's'}
                                        </Typography>
                                    )}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Customer Name" size="small" required
                                helperText="Pick an existing customer if there is one — avoids duplicates under different spellings" />
                        )}
                    />
                    <TextField label="Description" name="description" value={formData.description} onChange={handleInputChange} multiline rows={3} fullWidth size="small" required />
                    <Stack direction="row" spacing={2}>
                        <TextField label="Industry (Optional)" name="industry" value={formData.industry || ''} onChange={handleInputChange} fullWidth size="small" />
                        <TextField
                            label="Start Year"
                            name="startYear"
                            value={formData.startYear || ''}
                            onChange={handleInputChange}
                            type="number"
                            inputProps={{ min: 1900, max: new Date().getFullYear() + 10 }}
                            size="small"
                            sx={{ width: '150px' }}
                            required
                            error={!formData.startYear}
                            helperText={!formData.startYear ? "Required" : ""}
                        />
                        <TextField
                            label="End Year"
                            name="endYear"
                            value={formData.endYear || ''}
                            onChange={handleInputChange}
                            type="number"
                            inputProps={{ min: 1900, max: new Date().getFullYear() + 20 }}
                            size="small"
                            sx={{ width: '150px' }}
                            helperText={!!(formData.endYear && formData.startYear && formData.endYear < formData.startYear) ? "Must be after start" : "Ongoing if empty"}
                            error={!!(formData.endYear && formData.startYear && formData.endYear < formData.startYear)}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleInternalSave} disabled={isSaving || !formData.projectName.trim() || !formData.customerName.trim() || !formData.description.trim() || !formData.startYear || !!(formData.endYear && formData.startYear && formData.endYear < formData.startYear)}>
                    {isSaving ? 'Saving...' : (project ? 'Save Changes' : 'Add Project')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface EditDocTagsDialogProps {
    open: boolean;
    onClose: () => void;
    document: CompanyDocument | null;
    onSave: (s3LocationPk: string, newTags: string[]) => void;
    isSaving: boolean;
    isSmallScreen: boolean;
    saveError: string | null;
}

const EditDocTagsDialog: React.FC<EditDocTagsDialogProps> = ({
    open,
    onClose,
    document,
    onSave,
    isSaving,
    isSmallScreen,
    saveError
}) => {
    const [currentTags, setCurrentTags] = useState<string[]>([]);
    const [newTag, setNewTag] = useState('');
    const [tagError, setTagError] = useState<string | null>(null); // State for validation error

    useEffect(() => {
        if (open && document) {
            setCurrentTags(document.Tags || []);
            setNewTag('');
            setTagError(null); // Reset error when dialog opens
        }
    }, [open, document]);

    const handleAddTag = () => {
        const trimmedTag = newTag.trim();

        if (!trimmedTag) {
            setTagError(null); // Don't show an error if just empty
            return;
        }

        const tagRegex = /^[a-zA-Z0-9_-]+$/;
        if (!tagRegex.test(trimmedTag)) {
            setTagError('Invalid: Use only a-z, 0-9, -, _'); // Set error state
            return;
        }

        if (currentTags.includes(trimmedTag)) {
            setTagError('Tag already added.');
            return;
        }

        setTagError(null); // Clear error if validation passes

        if (trimmedTag && !currentTags.includes(trimmedTag)) {
            setCurrentTags(prev => [...prev, trimmedTag]);
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setCurrentTags(prev => prev.filter(tag => tag !== tagToRemove));
    };

    const handleSave = () => {
        if (document) {
            onSave(document.S3LocationPk, currentTags);
        }
    };

    const dialogFontSize = isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isSmallScreen}>
            <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.1rem' }}>Edit Tags</DialogTitle>
            <DialogContent dividers sx={{ '& .MuiTypography-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiButton-root': { fontSize: dialogFontSize } }}>
                <Stack spacing={1} sx={{ pt: 1 }}> {/* Reduced spacing */}
                    {saveError && <Alert severity="error" sx={{ mb: 1 }}>{saveError}</Alert>}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Document: <strong>{document?.DocumentPath.split('/').pop()}</strong>
                    </Typography>
                    
                    {/* Add Tag Input */}
                    <Stack direction="row" spacing={1}>
                        <TextField
                            fullWidth
                            size="small"
                            label="New Tag"
                            value={newTag}
                            onChange={(e) => {
                                setNewTag(e.target.value);
                                if (tagError) setTagError(null); // Clear error while typing
                            }}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                            error={!!tagError} // Highlight field on error
                            helperText={tagError || "Tags cannot contain spaces or colons."}
                        />
                        <Button variant="outlined" onClick={handleAddTag} disabled={!newTag.trim()}>Add</Button>
                    </Stack>

                    {/* Tags List */}
                    <Paper variant="outlined" sx={{ p: 1, minHeight: '50px', mt: 2 }}>
                        {currentTags.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">No tags added.</Typography>
                        ) : (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {currentTags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        size="small"
                                        onDelete={() => handleRemoveTag(tag)}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Paper>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Tags'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface ManageCompanyDocumentsDialogProps {
    open: boolean;
    onClose: () => void;
    isSmallScreen: boolean;
    auth: any; // Pass auth object for API calls
    companyDocumentsQuery: UseQueryResult<CompanyDocument[], Error>;
}

const ManageCompanyDocumentsDialog: React.FC<ManageCompanyDocumentsDialogProps> = ({
    open,
    onClose,
    isSmallScreen,
    auth,
    companyDocumentsQuery
}) => {
    const queryClient = useQueryClient();
    const [selectedFiles, setSelectedFiles] = useState<FileForUpload[]>([]);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const [generalError, setGeneralError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // New state for editing and deleting
    const [editingTagsDoc, setEditingTagsDoc] = useState<CompanyDocument | null>(null);
    const [docToDelete, setDocToDelete] = useState<CompanyDocument | null>(null);
    const [confirmDeleteDocOpen, setConfirmDeleteDocOpen] = useState(false);

    const companyId = auth.user?.profile?.["cognito:groups"]?.[0];
    const isAuthReady = auth.isAuthenticated && !!auth.user?.profile?.['custom:api_key'];
    const dialogFontSize = isSmallScreen ? smallScreenPanelFontSize : desktopPanelFontSize;

    // Polling
    const pollingIntervalId = useRef<NodeJS.Timeout | null>(null);
    const pollingTimeoutId = useRef<NodeJS.Timeout | null>(null);

    // This removes the state race condition.
    const processingFiles = React.useMemo(() => {
        return new Set(
            selectedFiles
                .filter(f => f.status === 'processing' && f.s3Key)
                .map(f => f.s3Key!)
        );
    }, [selectedFiles]); // Re-calculates whenever selectedFiles changes

    // --- QUERIES & MUTATIONS ---

    useEffect(() => {
        // console.log("--- Running data-handling useEffect ---");
        // console.log(`Query Status: isSuccess=${companyDocumentsQuery.isSuccess}, hasData=${!!companyDocumentsQuery.data}, processingFiles.size=${processingFiles.size}`);
        
        // Only run if the query was successful, we have data, AND we are actually polling
        if (companyDocumentsQuery.isSuccess && companyDocumentsQuery.data && processingFiles.size > 0) {
            const data = companyDocumentsQuery.data;
            // console.log("Polling Check - Fetched Documents:", data);
            // console.log("Polling Check - Currently Waiting For Keys:", processingFiles);

            const newlyFoundKeys = new Set<string>();
            const processingKeys = Array.from(processingFiles);

            // Check if any of our processing files are now in the document list
            data.forEach(doc => {
                const foundKey = processingKeys.find(key => {
                    const s3Key = key.trim();
                    const documentPath = doc.DocumentPath.trim();
                    const comparisonResult = s3Key.endsWith(documentPath); 
                    
                    // console.log(`  Comparing (Key): "${s3Key}" with (DocumentPath): "${documentPath}" -> endsWith? ${comparisonResult}`);
                    return comparisonResult;
                });

                if (foundKey) {
                    // console.log(`  MATCH FOUND! Key: "${foundKey}" matches DocumentPath: "${doc.DocumentPath}"`); // Log fixed comparison
                    newlyFoundKeys.add(foundKey);
                }
            });

            if (newlyFoundKeys.size > 0) {
                // console.log("Polling Update - Found Keys:", newlyFoundKeys); 

                // Instead of updating status to 'success', we will REMOVE
                // the successful file from the 'selectedFiles' list.
                setSelectedFiles(prev => {
                    const nextState = prev.filter(file => {
                        // Check if the file's key is in the set of newly found keys
                        if (file.s3Key && newlyFoundKeys.has(file.s3Key)) {
                            // console.log(`  File "${file.file.name}" is successful. Removing from upload list.`);
                            // Return false to filter this item out
                            return false;
                        }
                        // Return true to keep this item (it's either not found or still processing)
                        return true;
                    });
                    
                    // console.log("Polling Update - Next SelectedFiles State:", nextState);
                    return nextState;
                });
            }
        } else if (processingFiles.size > 0) {
            // console.log("Polling Check - Query not ready or no data yet.");
         }
    // removed setProcessingFiles from dependency array
    }, [companyDocumentsQuery.isSuccess, companyDocumentsQuery.data, processingFiles, setSelectedFiles]);

    // Mutation to get presigned URLs
    const generateUrlsMutation = useMutation({
        mutationFn: (filesData: { filename: string, tags: string[] }[]) =>
            apiService.generateCompanyDocUploadUrls({ filesData, auth }),
        onError: (error) => {
            console.error("Error generating URLs:", error);
            setGeneralError(error instanceof Error ? error.message : "Failed to prepare files for upload.");
            setIsUploadingFiles(false); // Stop upload process on URL generation failure
        }
    });

    // Mutation to update tags
    const updateTagsMutation = useMutation({
        mutationFn: (vars: { s3LocationPk: string, tags: string[] }) =>
            apiService.updateCompanyDocumentTags({ ...vars, auth }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['companyDocuments', companyId] });
            setEditingTagsDoc(null); // Close dialog on success
        },
    });

    // Mutation to delete document
    const deleteDocMutation = useMutation({
        mutationFn: (vars: { s3LocationPk: string }) =>
            apiService.deleteCompanyDocument({ ...vars, auth }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['companyDocuments', companyId] });
            setDocToDelete(null);
            setConfirmDeleteDocOpen(false); // Close confirmation dialog
        },
    });


    // --- STATE & EVENT HANDLERS ---

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setSelectedFiles([]);
            setIsUploadingFiles(false);
            setGeneralError(null);
            generateUrlsMutation.reset();
            updateTagsMutation.reset();
            deleteDocMutation.reset();
            setEditingTagsDoc(null);
            setDocToDelete(null);
            setConfirmDeleteDocOpen(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = ''; // Reset file input
            }
        }
    }, [open]);

    const isPolling = processingFiles.size > 0;

    useEffect(() => {
        // --- Start Polling ---
        if (isPolling && !pollingIntervalId.current) {
            // console.log("Starting to poll for:", processingFiles);
            
            pollingIntervalId.current = setInterval(() => {
                // console.log("--- Polling Interval Fired --- Invalidating Query.");
                queryClient.invalidateQueries({ queryKey: ['companyDocuments', companyId] });
            }, 5000); // 5 seconds


            pollingTimeoutId.current = setTimeout(() => {
                // console.error("Polling timed out.");
                
                setSelectedFiles(prev => {
                    // Re-calculate processing files *inside* the timeout
                    const currentProcessingKeys = new Set(
                        prev.filter(f => f.status === 'processing' && f.s3Key).map(f => f.s3Key!)
                    );

                    if (currentProcessingKeys.size === 0) return prev; // Nothing to time out

                    return prev.map(file => {
                        if (file.s3Key && currentProcessingKeys.has(file.s3Key)) {
                            return { ...file, status: 'error', errorMessage: 'Processing timed out. Please try again.' };
                        }
                        return file;
                    });
                });
                
                // Polling will stop automatically on the next render
                // because processingFiles.size will become 0
            }, 120000); // 120 seconds
        }
        
        // --- Stop Polling ---
        else if (!isPolling && pollingIntervalId.current) {
            // console.log("Stopping polling.");
            clearInterval(pollingIntervalId.current);
            if (pollingTimeoutId.current) clearTimeout(pollingTimeoutId.current);
            pollingIntervalId.current = null;
            pollingTimeoutId.current = null;
        }

        // Cleanup function for when component unmounts
        return () => {
            // console.log("!!! Polling useEffect cleanup running (on unmount) !!!");
            if (pollingIntervalId.current) clearInterval(pollingIntervalId.current);
            if (pollingTimeoutId.current) clearTimeout(pollingTimeoutId.current);
        };
    // This effect now *only* runs when `isPolling` (true/false) changes.
    // We add setSelectedFiles because it's used in the timeout.
    }, [isPolling, queryClient, companyId, setSelectedFiles]);

    // Handle file selection from input
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const newFiles = Array.from(event.target.files).map(file => ({
                file,
                tags: [] as string[],
                status: 'pending' as 'pending',
            }));

            // Prevent duplicates based on name and size (simple check)
            const uniqueNewFiles = newFiles.filter(nf =>
                !selectedFiles.some(sf => sf.file.name === nf.file.name && sf.file.size === nf.file.size)
            );

            setSelectedFiles(prev => [...prev, ...uniqueNewFiles]);

            // Clear the input value so the same file can be selected again if removed
             if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Trigger hidden file input click
    const handleAddFilesClick = () => {
        fileInputRef.current?.click();
    };

    // Remove a file from the list
    const handleRemoveFile = (fileToRemove: FileForUpload) => {
        setSelectedFiles(prev => prev.filter(f => f !== fileToRemove));
    };

    // Add or update a tag for a specific file
    const handleUpdateTags = (fileToUpdate: FileForUpload, newTag: string, action: 'add' | 'remove') => {
        setSelectedFiles(prev => prev.map((item) => {
            if (item === fileToUpdate) {
                const currentTags = new Set(item.tags); // Use a Set for easy add/delete/checking duplicates
                const trimmedTag = newTag.trim();

                if (action === 'add' && trimmedTag) {
                    currentTags.add(trimmedTag);
                } else if (action === 'remove') {
                    currentTags.delete(trimmedTag); // Use the tag value directly for removal
                }
                return { ...item, tags: Array.from(currentTags).sort() }; // Convert back to sorted array
            }
            return item;
        }));
    };


    // Helper to upload a single file to S3
    const uploadFileToS3 = async (fileItem: FileForUpload, uploadUrl: string, tags: string[]) => {
        const options: RequestInit = {
            method: 'PUT',
            body: fileItem.file,
            headers: {}
        };

        if (tags && tags.length > 0) {
            // Use tag value as both key and value: "tag1=tag1&tag2=tag2"
            const tagString = tags
                .map(tag => {
                    const encodedTag = encodeURIComponent(tag);
                    return `${encodedTag}=${encodedTag}`; // Key = Value
                 })
                .join('&');
            options.headers = { ...options.headers, 'x-amz-tagging': tagString };
        }

        try {
            const response = await fetch(uploadUrl, options);
            if (!response.ok) {
                throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
            }
            return 'success';
        } catch (error) {
            console.error(`Error uploading ${fileItem.file.name}:`, error);
            return error instanceof Error ? error.message : 'Unknown upload error';
        }
    };

    // Main upload process triggered by Upload button
    const handleUploadFiles = async () => {
        const filesToUpload = selectedFiles.filter(f => f.status !== 'success');
        if (filesToUpload.length === 0 || isUploadingFiles) return;

        setIsUploadingFiles(true);
        setGeneralError(null);
        // Reset status only for files that are not 'success'
        setSelectedFiles(prev => prev.map(f => 
            f.status === 'success' ? f : { ...f, status: 'pending', errorMessage: undefined }
        ));

        const filesToRequestUrls = filesToUpload.map(f => ({
            filename: f.file.name,
            tags: f.tags
        }));

        try {
            const urlData = await generateUrlsMutation.mutateAsync(filesToRequestUrls);

            // Match URLs back to files, but make sure to skip 'success' files
            const filesWithUrls = selectedFiles.map(fileItem => {
                // If it's already successful, leave it alone
                if (fileItem.status === 'success') {
                    return fileItem;
                }

                const urlInfo = urlData.upload_urls.find(u => u.filename === fileItem.file.name);
                if (urlInfo) {
                    return {
                        ...fileItem,
                        uploadUrl: urlInfo.upload_url,
                        s3Key: urlInfo.s3_key,
                        status: 'uploading' as 'uploading'
                    };
                }
                
                // Only mark as error if it's not successful and didn't get a URL
                return { ...fileItem, status: 'error' as 'error', errorMessage: 'Could not get upload URL.' };
            });
            setSelectedFiles(filesWithUrls);

            // Perform S3 uploads
            const uploadPromises = filesWithUrls
                .filter(f => f.status === 'uploading' && f.uploadUrl)
                .map(async (fileItem) => {
                    const originalIndex = selectedFiles.findIndex(sf => sf.file.name === fileItem.file.name && sf.file.size === fileItem.file.size);
                    const result = await uploadFileToS3(fileItem, fileItem.uploadUrl!, fileItem.tags);

                    // Update status for this specific file *after S3 upload*
                     setSelectedFiles(prev => prev.map((item, idx) => {
                        if (idx === originalIndex) {
                            // This is fine: 'uploading' for success, 'error' for failure
                            const newStatus: 'uploading' | 'error' = result === 'success' ? 'uploading' : 'error';
                            return {
                                ...item,
                                status: newStatus,
                                errorMessage: result !== 'success' ? result : undefined
                            };
                        }
                        return item;
                    }));
                });

            await Promise.all(uploadPromises);
            
            // After all S3 uploads are done, find files that finished
            // and move them to 'processing'.
            setSelectedFiles(prevFiles => {
                return prevFiles.map(file => {
                    // Check if file status is 'uploading' (meaning S3 upload succeeded)
                    if (file.status === 'uploading' && file.s3Key) {
                        // console.log(`File "${file.file.name}" finished S3 upload successfully. Moving to 'processing' state.`);
                        
                        const updatedFile: FileForUpload = {
                            ...file,
                            status: 'processing' // Flip to 'processing'
                        };
                        return updatedFile;
                    }
                    // Keep 'error' or other statuses as-is
                    return file;
                });
            });

            // The useMemo/useEffect hooks will automatically
            // pick up the 'processing' files and start polling.

        } catch (error) {
            // Error handled by generateUrlsMutation.onError
            console.error("Upload process error:", error);
        } finally {
            // This spinner is just for the S3 upload part.
            // The `isWorking` flag will take over for the 'processing' phase.
            setIsUploadingFiles(false);
        }
    };

    // Handlers for new dialogs
    const handleOpenEditTags = (doc: CompanyDocument) => {
        updateTagsMutation.reset();
        setEditingTagsDoc(doc);
    };

    const handleSaveTags = (s3LocationPk: string, newTags: string[]) => {
        updateTagsMutation.mutate({ s3LocationPk, tags: newTags });
    };

    const handleRequestDelete = (doc: CompanyDocument) => {
        deleteDocMutation.reset();
        setDocToDelete(doc);
        setConfirmDeleteDocOpen(true);
    };

    const handleConfirmDelete = () => {
        if (docToDelete) {
            deleteDocMutation.mutate({ s3LocationPk: docToDelete.S3LocationPk });
        }
    };


    // --- RENDER ---
    const isWorking = isUploadingFiles || processingFiles.size > 0;
    
    const renderExistingDocuments = () => {
        if (companyDocumentsQuery.isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading existing documents...</Typography>
                </Box>
            );
        }

        if (companyDocumentsQuery.error) {
            return <Alert severity="error">Error loading documents: {companyDocumentsQuery.error.message}</Alert>;
        }

        const documents = companyDocumentsQuery.data || [];
        if (documents.length === 0) {
            return <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>No company documents found.</Typography>;
        }

        return (
            <Paper variant="outlined" sx={{ maxHeight: isSmallScreen ? '200px' : '300px', overflowY: 'auto' }}>
                <List dense disablePadding>
                    {documents.map((doc) => (
                        <ListItem key={doc.S3LocationPk} divider>
                            <ListItemText
                                primary={
                                    <Tooltip title={doc.DocumentPath}>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {doc.DocumentPath.split('/').pop()}
                                        </Typography>
                                    </Tooltip>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                                secondary={
                                    (doc.Tags && doc.Tags.length > 0) ? ( // <-- Check if doc.Tags exists
                                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                                            {doc.Tags.map((tag: string) => <Chip key={tag} label={tag} size="small" />)} {/* <-- Explicitly type 'tag' */}
                                        </Stack>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">No tags</Typography>
                                    )
                                }
                            />
                            <IconButton size="small" sx={{ mr: 0.5 }} onClick={() => handleOpenEditTags(doc)} disabled={isWorking}>
                                <EditIcon fontSize="inherit" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleRequestDelete(doc)} disabled={isWorking}>
                                <DeleteIcon fontSize="inherit" />
                            </IconButton>
                        </ListItem>
                    ))}
                </List>
            </Paper>
        );
    };

    const filesToUploadCount = selectedFiles.filter(f => f.status !== 'success').length;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isSmallScreen}>
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.2rem' }}>Manage Company Documents</DialogTitle>
                <DialogContent dividers sx={{ '& .MuiTypography-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiButton-root': { fontSize: dialogFontSize } }}>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        
                        {/* --- Existing Documents Section --- */}
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'medium' }}>Existing Documents</Typography>
                        {generalError && <Alert severity="error">{generalError}</Alert>}
                        {renderExistingDocuments()}
                        
                        <Divider sx={{ my: 1 }} />

                        {/* --- Add New Documents Section --- */}
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'medium' }}>Add New Documents</Typography>

                        {/* Hidden File Input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            hidden
                            onChange={handleFileSelect}
                            accept="*" // Or specify accepted types like ".pdf,.docx,.xlsx"
                        />

                        {/* Add Files Button */}
                        <Button
                            variant="outlined"
                            onClick={handleAddFilesClick}
                            startIcon={<CloudUploadIcon />}
                            disabled={isWorking}
                        >
                            Add Documents
                        </Button>

                        {/* List of Selected Files */}
                        
                        {selectedFiles.filter(f => f.status !== 'success').length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5, maxHeight: isSmallScreen ? 'calc(100vh - 450px)' : '400px', overflowY: 'auto' }}>
                                <List dense disablePadding>
                                    {selectedFiles.filter(f => f.status !== 'success').map((item, index) => (
                                        <ListItem
                                            key={`${item.file.name}-${item.file.lastModified}`}
                                            divider
                                            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}
                                        >
                                            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                                                {item.status === 'pending' && <DescriptionIcon fontSize="small" color="action" sx={{ mr: 1 }} />}
                                                {item.status === 'uploading' && <CircularProgress size={16} sx={{ mr: 1 }} />}
                                                {item.status === 'processing' && <CircularProgress size={16} color="warning" sx={{ mr: 1 }} />}
                                                {item.status === 'success' && <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />}
                                                {item.status === 'error' && <ErrorIcon fontSize="small" color="error" sx={{ mr: 1 }} />}

                                                <Tooltip title={item.file.name}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium', flexGrow: 1, mr: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {item.file.name}
                                                    </Typography>
                                                </Tooltip>
                                                <Typography variant="caption" color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>
                                                    ({(item.file.size / 1024 / 1024).toFixed(2)} MB)
                                                </Typography>
                                                <IconButton size="small" onClick={() => handleRemoveFile(item)} disabled={isWorking} sx={{p: 0.25}}>
                                                    <DeleteIcon fontSize="inherit" />
                                                </IconButton>
                                            </Box>

                                            {item.status === 'error' && item.errorMessage && (
                                                <Alert severity="error" sx={{ width: '100%', fontSize: '0.75rem', py: 0, px: 1, mb: 1 }}>
                                                    {item.errorMessage}
                                                </Alert>
                                            )}

                                            {/* Tagging UI */}
                                            <Box sx={{ width: '100%', pl: 3 }}>
                                                <Typography variant="caption" color="text.secondary" >Tags:</Typography>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5, mb: 1 }}>
                                                    {item.tags.map((tag) => (
                                                        <Chip
                                                            key={tag}
                                                            label={tag}
                                                            size="small"
                                                            onDelete={() => handleUpdateTags(item, tag, 'remove')}
                                                            disabled={isWorking}
                                                        />
                                                    ))}
                                                    <AddSimpleTagForm
                                                        onTagAdd={(tagValue) => handleUpdateTags(item, tagValue, 'add')}
                                                        existingTags={item.tags}
                                                        disabled={isWorking}
                                                    />
                                                </Stack>
                                            </Box>

                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isWorking}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUploadFiles}
                        disabled={filesToUploadCount === 0 || isWorking}
                        startIcon={(isUploadingFiles || processingFiles.size > 0) ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                        {isUploadingFiles ? 'Uploading...' : 
                            (processingFiles.size > 0 ? `Processing (${processingFiles.size})...` : 
                            `Upload ${selectedFiles.length} File(s)`)
                        }
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- Child Dialogs --- */}
            
            <EditDocTagsDialog
                open={!!editingTagsDoc}
                onClose={() => setEditingTagsDoc(null)}
                document={editingTagsDoc}
                onSave={handleSaveTags}
                isSaving={updateTagsMutation.isPending}
                isSmallScreen={isSmallScreen}
                saveError={updateTagsMutation.isError ? updateTagsMutation.error.message : null}
            />

            <Dialog 
                open={confirmDeleteDocOpen} 
                onClose={() => setConfirmDeleteDocOpen(false)} 
                maxWidth="xs" 
                fullWidth
            >
                <DialogTitle sx={{ fontSize: isSmallScreen ? '1rem' : '1.1rem' }}>Confirm Deletion</DialogTitle>
                <DialogContent>
                    {deleteDocMutation.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>{deleteDocMutation.error.message}</Alert>
                    )}
                    <Typography>
                        Are you sure you want to permanently delete this document?
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
                        {docToDelete?.DocumentPath.split('/').pop()}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDocOpen(false)} disabled={deleteDocMutation.isPending}>Cancel</Button>
                    <Button 
                        onClick={handleConfirmDelete} 
                        color="error" 
                        variant="contained" 
                        disabled={deleteDocMutation.isPending}
                    >
                        {deleteDocMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};


// Helper component for adding tags inline
interface AddSimpleTagFormProps {
    onTagAdd: (tagValue: string) => void; // Simplified callback
    existingTags: string[];
    disabled?: boolean;
}
const AddSimpleTagForm: React.FC<AddSimpleTagFormProps> = ({ onTagAdd, existingTags, disabled }) => {
    const [tagValue, setTagValue] = useState('');
    const [showInput, setShowInput] = useState(false);
    const [tagError, setTagError] = useState<string | null>(null); // New state for error

    const handleSave = () => {
        const trimmedTag = tagValue.trim();

        // Check if empty
        if (!trimmedTag) {
            setTagError(null);
            setShowInput(false); // Hide if input is empty
            setTagValue('');
            return;
        }

        // Validation Rule: letters, numbers, hyphens, underscores only.
        const tagRegex = /^[a-zA-Z0-9_-]+$/;
        if (!tagRegex.test(trimmedTag)) {
            setTagError('Invalid: a-z, 0-9, -, _'); // More specific error
            return;
        }

        if (existingTags.includes(trimmedTag)) {
            setTagError('Duplicate');
            return;
        }

        if (trimmedTag.includes(' ')) {
            setTagError('No spaces'); // Set compact error message
            return;
        }

        setTagError(null); // Clear error
        onTagAdd(trimmedTag);
        setTagValue(''); // Clear input after adding
        // We keep the input visible for adding more tags
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent form submission if wrapped in form
            handleSave();
        } else if (event.key === 'Escape') {
             setTagValue('');
             setTagError(null); // Clear error
             setShowInput(false);
        }
    };


    if (!showInput) {
        return (
            <Chip
                label="+ Add Tag"
                size="small"
                onClick={() => {
                    setShowInput(true);
                    setTagError(null); // Clear any old error on open
                }}
                disabled={disabled}
                variant="outlined"
                clickable
                sx={{ cursor: 'pointer' }}
            />
        );
    }

    return (
        <Paper elevation={0} variant="outlined" sx={{ p: 0.5, display: 'flex', gap: 0.5, alignItems: 'center', backgroundColor: 'action.hover' }}>
             <TextField
                autoFocus
                variant="standard"
                size="small"
                placeholder="New tag"
                value={tagValue}
                onChange={(e) => {
                    setTagValue(e.target.value);
                    if (tagError) setTagError(null); // Clear error on type
                }}
                onKeyDown={handleKeyDown} // Handle Enter/Escape
                onBlur={handleSave} // Attempt to save when losing focus
                InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem' } }}
                sx={{ width: '100px' }} // Adjust width as needed
                disabled={disabled}
                error={!!tagError} // Show error state
                helperText={tagError} // Display error message
                FormHelperTextProps={{ sx: { margin: 0, fontSize: '0.65rem', whiteSpace: 'nowrap' } }} // Compact helper
            />
             {/* Simple close button */}
             <IconButton 
                size="small" 
                onClick={() => { 
                    setShowInput(false); 
                    setTagValue(''); 
                    setTagError(null); // Clear error on close
                }} 
                disabled={disabled} 
                sx={{ p: 0.25 }}
            >
                <CloseIcon fontSize="inherit" color="action"/>
            </IconButton>
        </Paper>
    );
};

// --- Document Explorer Component ---
interface DocumentTreeItem {
    id: string;
    name: string;
    document?: TenderDocument;
    children: DocumentTreeItem[];
    docIds: string[];
}

interface DocumentExplorerProps {
    documents: TenderDocument[];
    selectedDocs: Set<string>;
    onSelectedDocsChange: React.Dispatch<React.SetStateAction<Set<string>>>;
    showSelectAll?: boolean;
    onOpenDocument: (documentPath: string) => void;
    isOpeningDocument: boolean;
    openingDocumentPath?: string;
    // Extra rows rendered inside the same scrollable Paper as the tree, after
    // it. Used for always-included items (e.g. Q&A docs) that must share the
    // tree's left/right alignment.
    lockedRowsAfter?: React.ReactNode;
}

const DocumentExplorer: React.FC<DocumentExplorerProps> = ({
    documents,
    selectedDocs,
    onSelectedDocsChange,
    showSelectAll = true,
    onOpenDocument,
    isOpeningDocument,
    openingDocumentPath,
    lockedRowsAfter
}) => {
    const [tree, setTree] = useState<DocumentTreeItem[]>([]);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    useEffect(() => {
        const root: { children: DocumentTreeItem[] } = { children: [] };
        documents.forEach(doc => {
            const processedPath = doc.DocumentPath.replace(/\.xlsx\//, '.xlsx|');
            const parts = processedPath.split('/').flatMap(p => p.includes('|') ? p.split('|') : p).filter(p => p);
            let currentLevel = root.children;
            let currentPath = '';

            parts.forEach((part, idx) => {
                currentPath = currentPath ? `${currentPath}/${part}` : part;
                let node = currentLevel.find(child => child.id === currentPath);
                if (!node) {
                    node = { id: currentPath, name: part, children: [], docIds: [] };
                    currentLevel.push(node);
                }
                if (idx === parts.length - 1) node.document = doc;
                currentLevel = node.children;
            });
        });

        const collectDocIds = (node: DocumentTreeItem): string[] => {
            let paths = node.document ? [node.document.DocumentPath] : [];
            node.children.forEach(child => { paths = paths.concat(collectDocIds(child)); });
            node.docIds = paths;
            return paths;
        };

        root.children.forEach(collectDocIds);
        root.children.sort((a, b) => a.name.localeCompare(b.name));
        setTree(root.children);
    }, [documents]);

    const handleToggleExpand = (id: string) => {
        setExpanded(exp => {
            const next = new Set(exp);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSelectChange = (node: DocumentTreeItem, checked: boolean) => {
        onSelectedDocsChange(prev => {
            const next = new Set(prev);
            node.docIds.forEach(path => checked ? next.add(path) : next.delete(path));
            return next;
        });
    };

    const renderTree = (nodes: DocumentTreeItem[], depth = 0): React.ReactNode[] => {
        nodes.sort((a, b) => a.name.localeCompare(b.name));
        return nodes.map(node => {
            const isFolder = node.children.length > 0;
            const isChecked = node.docIds.length > 0 && node.docIds.every(path => selectedDocs.has(path));
            const isIndeterminate = node.docIds.some(path => selectedDocs.has(path)) && !isChecked;

            // 1. Show for simple files (PDF, DOCX) that are not Excel sheets
            const showForLeaf = !isFolder && node.document && !node.document.DocumentPath.includes('.xlsx/');
            
            // 2. Show for the main Excel file "folder"
            const showForExcelFolder = isFolder && node.name.toLowerCase().endsWith('.xlsx');

            // Final decision
            const showOpenIcon = showForLeaf || showForExcelFolder;
            
            // Determine what path to open
            let path_to_open: string | null = null;
            if (showForLeaf) {
                path_to_open = node.document!.DocumentPath; // e.g., "folder/file.pdf"
            } else if (showForExcelFolder) {
                path_to_open = node.id; // e.g., "folder/file.xlsx"
            }

            // Check if *this specific* document is being opened
            const isThisDocOpening = !!(
                isOpeningDocument &&
                openingDocumentPath &&
                path_to_open &&
                openingDocumentPath === path_to_open
            );

            return (
                <div key={node.id}>
                    <ListItemButton 
                        onClick={() => isFolder && handleToggleExpand(node.id)} 
                        sx={{ pl: depth * 2, py: 0.2 }}
                    >
                        {isFolder ? (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleExpand(node.id); }} sx={{ mr: 0.5 }}>
                                {expanded.has(node.id) ? <ExpandMoreIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
                            </IconButton>
                        ) : (<Box sx={{ width: 28, mr: 0.5 }} />)}
                        <Checkbox size="small" checked={isChecked} indeterminate={isIndeterminate} onChange={(_, checked) => handleSelectChange(node, checked)} sx={{ p: 0.5, mr: 0.5 }} />
                        <Tooltip title={node.document?.Summary || node.name} placement="top-start" arrow>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexGrow: 1, overflow: 'hidden' }}>
                                {isFolder ? <FolderIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
                                <Typography variant="body2" sx={{ fontWeight: isFolder ? 'medium' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem' }}>
                                    {node.name}
                                </Typography>
                                {isThisDocOpening && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                                        Opening...
                                    </Typography>
                                )}
                                {node.document && (
                                    <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', flexShrink: 0 }}>
                                        {node.document.DocumentLanguage && node.document.DocumentLanguage.trim() !== '' && (
                                            <Chip label={node.document.DocumentLanguage.toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                                        )}
                                        {/* For company documents: show tags if available, otherwise show nothing */}
                                        {/* For tender documents: always show DocumentCategory */}
                                        {node.document.Tags && node.document.Tags.length > 0 ? (
                                            node.document.Tags.map((tag: string) => (
                                                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
                                            ))
                                        ) : (
                                            // Only show DocumentCategory for tender documents (not for company documents)
                                            node.document.DocumentCategory && node.document.DocumentCategory !== 'Company' && (
                                                <Chip label={node.document.DocumentCategory} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
                                            )
                                        )}
                                    </Stack>
                                )}
                            </div>
                        </Tooltip>
                        {showOpenIcon && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation(); // Stop it from toggling the folder
                                    if (path_to_open) {
                                        onOpenDocument(path_to_open);
                                    }
                                }}
                                disabled={isOpeningDocument} // Disable all buttons while one is opening
                                sx={{ ml: 1, flexShrink: 0 }}
                            >
                                <LaunchIcon fontSize="inherit" />
                            </IconButton>
                        )}
                    </ListItemButton>
                    {isFolder && (
                        <Collapse in={expanded.has(node.id)} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>{renderTree(node.children, depth + 1)}</List>
                        </Collapse>
                    )}
                </div>
            );
        });
    };

    const allPaths = documents.map(d => d.DocumentPath);
    const allSelected = allPaths.length > 0 && allPaths.every(path => selectedDocs.has(path));
    const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => onSelectedDocsChange(e.target.checked ? new Set(allPaths) : new Set());

    return (
        <Box>
            {showSelectAll && (
                <FormControlLabel label={"Select All Documents"} control={<Checkbox size="small" checked={allSelected} onChange={toggleAll} indeterminate={selectedDocs.size > 0 && !allSelected} />} />
            )}
            <Paper variant="outlined" sx={{ maxHeight: 400, overflowY: 'auto' }}>
                <List dense disablePadding>
                    {renderTree(tree)}
                    {lockedRowsAfter}
                </List>
            </Paper>
        </Box>
    );
};

export default TenderQPage;
